import { StorageBufferAttribute } from 'three/webgpu';
import type {
  AvbdBodySolveMode,
  ContactPoolMode,
  JointDesc,
  PhysicsConfig,
  RigidBodyDesc,
  PhysicsStats,
  SpringDesc,
} from './types';
import { IntegrationStage } from './gpu/integration';
import {
  ContactGenerationStage,
  type CcdDiagnostics,
} from './gpu/contactGeneration';
import {
  AvbdStateStage,
  resolveAvbdContactDispatchForTesting,
  type AvbdAdaptiveIterationStats,
  type AvbdSolvePathTelemetry,
} from './gpu/avbdState';
import {
  BroadPhaseStage,
  type BroadphaseComputeTail,
  type BroadphaseDebugSnapshot,
  type BroadphaseFatalSampleDiagnostics,
  type BroadphaseLifecycleSnapshot,
  type BroadphasePairDedupTelemetry,
  type BroadphaseSleepingSparseDiagnostics,
  type BroadphaseSleepingSparseScratchDiagnostics,
} from './gpu/broadPhase';
import { PlayerControlStage, type PlayerProbeState } from './gpu/playerControl';
import {
  RestitutionStage,
  type RestitutionStageAllocationTelemetry,
} from './gpu/restitution';
import {
  VelocityConstraintProjectionStage,
  type VelocityConstraintProjectionAllocationTelemetry,
} from './gpu/velocityConstraintProjection';
import {
  SleepingIslandsStage,
  type SleepingComputeGroupDiagnostics,
  type SleepingIslandsTelemetry,
  type SleepingTopologyCacheAllocationTelemetry,
  type SleepingZeroWorkDiagnostics,
} from './gpu/sleepingIslands';
import {
  PhysicsSubmissionLedger,
  type PhysicsSubmissionSnapshot,
} from './gpu/physicsSubmissionLedger';
import {
  createThreeWebGpuComputePreparationCache,
  prewarmThreeWebGpuCompute,
  prepareThreeWebGpuCompute,
  recordPreparedThreeWebGpuCompute,
  type NativeComputePassDescriptorFactory,
  type ThreeWebGpuComputeCommand,
  type ThreeWebGpuComputePreparationCache,
  type ThreeWebGpuComputePrewarmStats,
  type ThreeWebGpuDispatchSize,
} from './gpu/threeWebGpuComputeBridge';
import {
  createGpuOnlyStorageAttribute,
  type GpuOnlyStorageAllocation,
} from './gpu/gpuOnlyStorage';
import {
  CONSTRAINT_SAFETY_ALL_FATAL_BITS,
  ConstraintSafetyTransactionStage,
  normalizeConstraintSafetyReadbackTimeoutMs,
} from './gpu/constraintSafety';
import {
  assertPhysicsEngineCapacityFitsDevice,
  planPhysicsEngineCapacity,
  resolvePhysicsPairEndpointEncoding,
  runCapacityPlannerSelfTests,
  type CapacityPlannerSelfTestResult,
  type CapacityGpuOnlyBufferKey,
  type PhysicsEngineCapacityInput,
  type PhysicsEngineCapacityAllocationOptions,
  type PhysicsEngineCapacityMode,
  type PhysicsEngineCapacityPlan,
} from './gpu/capacityPlanner';
import {
  COMPACT_CONTACT_POOL_GENERATION_MODE,
  type CompactContactPoolLayout,
  planCompactContactPoolLayout,
} from './gpu/compactContactPool';
import { decodeSolverContactListHeader } from './gpu/solverContactList';
import {
  packLegacyBodyPair,
  type PairEndpointEncoding,
} from './gpu/pairAbi';
import {
  CONTACT_INFO_RESERVED_BIT_30,
  CONTACT_INFO_SPECULATIVE_BIT,
  CONTACT_RECORD_ARM_A_OFFSET,
  CONTACT_RECORD_ARM_B_OFFSET,
  CONTACT_RECORD_CACHE_OFFSET,
  CONTACT_RECORD_CONSTRAINT_C0_OFFSET,
  CONTACT_RECORD_DUAL_OFFSET,
  CONTACT_RECORD_FLOATS,
  CONTACT_RECORD_META_OFFSET,
  CONTACT_RECORD_NORMAL_PEN_OFFSET,
  CONTACT_RECORD_PENALTY_OFFSET,
  CONTACT_RECORD_SHADOW_OFFSET,
  CONTACT_RECORD_VEC4S,
  decodeContactLogicalSlot,
  contactRecordVec4FloatIndex,
} from './gpu/contactRecord';
import {
  JOINT_RECORD_ANCHOR_A_OFFSET,
  JOINT_RECORD_ANCHOR_B_OFFSET,
  JOINT_RECORD_C0_ANG_OFFSET,
  JOINT_RECORD_C0_LIN_OFFSET,
  JOINT_RECORD_FLOATS,
  JOINT_RECORD_LAMBDA_ANG_OFFSET,
  JOINT_RECORD_LAMBDA_LIN_OFFSET,
  JOINT_RECORD_META_OFFSET,
  JOINT_RECORD_PENALTY_ANG_OFFSET,
  JOINT_RECORD_PENALTY_LIN_OFFSET,
  JOINT_RECORD_REST_RELATIVE_ROTATION_OFFSET,
  JOINT_RECORD_STIFFNESS_OFFSET,
  JOINT_RECORD_VEC4S,
  jointRecordVec4FloatIndex,
} from './gpu/jointRecord';
import {
  SPRING_RECORD_ANCHOR_A_OFFSET,
  SPRING_RECORD_ANCHOR_B_OFFSET,
  SPRING_RECORD_FLOATS,
  SPRING_RECORD_META_OFFSET,
  SPRING_RECORD_STATE_OFFSET,
  SPRING_RECORD_VEC4S,
  springRecordVec4FloatIndex,
} from './gpu/springRecord';
import {
  DEFAULT_COLLISION_GROUP,
  DEFAULT_COLLISION_MASK,
  SHAPE_TYPE_BOX,
  SHAPE_TYPE_SPHERE,
  clampCollisionFilterWord,
  clampShapeFriction,
  decodeShapeFrictionWord,
  decodeShapeTypeWord,
  packShapeMetaWord,
} from './gpu/shapeEncoding';
import {
  AVBD_COLLISION_MARGIN,
  AVBD_FRICTION_DYNAMIC,
  AVBD_FRICTION_STATIC,
  AVBD_REFERENCE_ANGULAR_DUAL_UPDATE_BETA,
  AVBD_REFERENCE_DUAL_UPDATE_BETA,
  AVBD_REFERENCE_PENALTY_DECAY_GAMMA,
  AVBD_REFERENCE_REGULARIZATION_ALPHA,
} from './avbdParams';

const CANDIDATE_LIST_HEADER_WORDS = 1;
const AVBD_K_START = 1.0;
const AVBD_HARD_CONSTRAINT_PENALTY_MAX = 1.0e10;

/**
 * The public AVBD algorithm performs one color-ordered primal sweep followed
 * by one dual update per nonlinear iteration. More sweeps are a useful local
 * diagnostic extension, but they mutate `sweepOffset` between dispatches and
 * therefore cannot use a deferred Three ComputeNode collector without owning
 * a distinct uniform allocation for every occurrence.
 */
export const AVBD_NATIVE_COMMAND_COLLECTOR_PAIR_SWEEPS = 1;

export type AvbdNativeCommandCollectorPolicy = Readonly<{
  eligible: boolean;
  reason:
    | 'invariant-colored-graph'
    | 'serial-uniform-variation'
    | 'multi-sweep-uniform-variation'
    | 'extended-constraint-uniform-variation';
}>;

export type AvbdNativeCommandCollectorFeatures = Readonly<{
  restitutionActive?: boolean;
  jointCount?: number;
  springCount?: number;
}>;

export function getAvbdNativeCommandCollectorPolicy(
  bodySolveMode: AvbdBodySolveMode,
  pairSweeps: number,
  features: AvbdNativeCommandCollectorFeatures = {},
): AvbdNativeCommandCollectorPolicy {
  if (bodySolveMode === 'serial') {
    return Object.freeze({
      eligible: false,
      reason: 'serial-uniform-variation',
    });
  }
  if (pairSweeps !== AVBD_NATIVE_COMMAND_COLLECTOR_PAIR_SWEEPS) {
    return Object.freeze({
      eligible: false,
      reason: 'multi-sweep-uniform-variation',
    });
  }
  // Restitution-only graphs are invariant within one substep: the epoch and
  // dispatch bounds are established once, while coupled rounds only repeat
  // the same nodes. Joints/springs still mutate per-dispatch color/body
  // uniforms and therefore remain on the immediate path.
  if ((features.jointCount ?? 0) > 0 || (features.springCount ?? 0) > 0) {
    return Object.freeze({
      eligible: false,
      reason: 'extended-constraint-uniform-variation',
    });
  }
  return Object.freeze({
    eligible: true,
    reason: 'invariant-colored-graph',
  });
}

function sameThreeWebGpuDispatchSize(
  left: ThreeWebGpuDispatchSize | null | undefined,
  right: ThreeWebGpuDispatchSize | null | undefined,
): boolean {
  if (left === right) return true;
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  return left[0] === right[0]
    && left[1] === right[1]
    && left[2] === right[2];
}

export type AvbdNativeCommandCollectorPolicySelfTestResult = Readonly<{
  ok: boolean;
  checks: number;
  failures: readonly string[];
}>;

/** GPU-free guard against silently widening native fusion to mutable uniforms. */
export function runAvbdNativeCommandCollectorPolicySelfTest():
AvbdNativeCommandCollectorPolicySelfTestResult {
  const failures: string[] = [];
  let checks = 0;
  const check = (name: string, condition: boolean): void => {
    checks++;
    if (!condition) failures.push(name);
  };
  check(
    'one-sweep colored AVBD is the only native invariant graph',
    getAvbdNativeCommandCollectorPolicy('colored', 1).eligible,
  );
  check(
    'two-sweep colored diagnostics are rejected by native fusion',
    !getAvbdNativeCommandCollectorPolicy('colored', 2).eligible,
  );
  check(
    'four-sweep colored diagnostics are rejected by native fusion',
    !getAvbdNativeCommandCollectorPolicy('colored', 4).eligible,
  );
  check(
    'serial body-index uniforms are rejected by native fusion',
    !getAvbdNativeCommandCollectorPolicy('serial', 1).eligible,
  );
  check(
    'restitution-only graphs preserve per-substep uniforms and can be fused',
    getAvbdNativeCommandCollectorPolicy('colored', 1, { restitutionActive: true }).eligible,
  );
  check(
    'joint graphs are rejected until per-command uniforms are snapshotted',
    !getAvbdNativeCommandCollectorPolicy('colored', 1, { jointCount: 1 }).eligible,
  );
  check(
    'spring graphs are rejected until per-command uniforms are snapshotted',
    !getAvbdNativeCommandCollectorPolicy('colored', 1, { springCount: 1 }).eligible,
  );
  check(
    'restitution does not make a joint graph safe to fuse',
    !getAvbdNativeCommandCollectorPolicy('colored', 1, {
      restitutionActive: true,
      jointCount: 1,
    }).eligible,
  );
  const oneDimensionalBoundary = resolveAvbdContactDispatchForTesting(
    65_535 * 64,
  );
  const twoDimensionalBoundary = resolveAvbdContactDispatchForTesting(
    65_535 * 64 + 1,
  );
  check(
    'contact dispatch keeps the exact 1D boundary',
    oneDimensionalBoundary[0] === 65_535
      && oneDimensionalBoundary[1] === 1
      && oneDimensionalBoundary[2] === 1,
  );
  check(
    'contact dispatch tiles the first row beyond the 1D limit',
    twoDimensionalBoundary[0] === 65_535
      && twoDimensionalBoundary[1] === 2
      && twoDimensionalBoundary[2] === 1,
  );
  return Object.freeze({
    ok: failures.length === 0,
    checks,
    failures: Object.freeze(failures),
  });
}
const INERTIAL_POSE_VEC4S_PER_BODY = 5;
const INERTIAL_POSE_FLOATS_PER_BODY = INERTIAL_POSE_VEC4S_PER_BODY * 4;
const WORLD_BODY_INDEX = 0xffffffff;
const DEFAULT_MAX_JOINTS_PER_BODY_SOLVER = 8;
const DEFAULT_MAX_SPRINGS_PER_BODY_SOLVER = 12;
const IGNORED_PAIR_HASH_ENTRY_WORDS = 2;
const IGNORED_PAIR_HASH_STATE_EMPTY = 0;
const IGNORED_PAIR_HASH_STATE_OCCUPIED = 1;
const IGNORED_PAIR_HASH_STATE_TOMBSTONE = 2;
const LEGACY_GPU_ONLY_CAPACITY_KEYS = Object.freeze([
  'pairContacts',
  'jointRecords',
  'springRecords',
] as const satisfies readonly CapacityGpuOnlyBufferKey[]);
const COMPACT_GPU_ONLY_CAPACITY_KEYS = Object.freeze([
  ...LEGACY_GPU_ONLY_CAPACITY_KEYS,
  'persistentManifoldMap',
] as const satisfies readonly CapacityGpuOnlyBufferKey[]);

function configuredContactPoolMode(config: PhysicsConfig): ContactPoolMode {
  const mode = config.contactPoolMode ?? 'legacy';
  if (mode !== 'legacy' && mode !== 'compact') {
    throw new Error(`Unknown contactPoolMode: ${String(mode)}.`);
  }
  const capacity = config.compactContactPoolCapacity;
  if (mode === 'compact') {
    if (!Number.isSafeInteger(capacity) || (capacity ?? 0) < 1) {
      throw new Error(
        'contactPoolMode="compact" requires a positive integer compactContactPoolCapacity.',
      );
    }
  } else if (capacity !== undefined) {
    throw new Error(
      'compactContactPoolCapacity is only valid when contactPoolMode="compact".',
    );
  }
  return mode;
}

function installedGpuOnlyCapacityKeys(
  mode: ContactPoolMode,
): readonly CapacityGpuOnlyBufferKey[] {
  return mode === 'compact'
    ? COMPACT_GPU_ONLY_CAPACITY_KEYS
    : LEGACY_GPU_ONLY_CAPACITY_KEYS;
}

function installedCapacityAllocationOptions(
  config: PhysicsConfig,
  mode: ContactPoolMode,
  allocatedJoints: number,
  allocatedSprings: number,
  contactOnlyAdjacencyAlias: boolean,
): PhysicsEngineCapacityAllocationOptions {
  return {
    mode: mode === 'compact' ? 'optimized' : 'legacy',
    ...(mode === 'compact'
      ? { compactContactPoolCapacity: config.compactContactPoolCapacity }
      : {}),
    gpuOnlyBufferKeys: installedGpuOnlyCapacityKeys(mode),
    allocatedJoints,
    allocatedSprings,
    constraintGraphUsesLogicalMax: true,
    contactOnlyAdjacencyAlias,
    includeSleepingTopologyCache: (config.enableSleeping ?? true)
      && contactOnlyAdjacencyAlias,
    enableBroadphasePairDedup: config.enableBroadphasePairDedup ?? true,
  };
}

function configuredLogicalRecordMaxima(config: PhysicsConfig): {
  joints: number;
  springs: number;
} {
  const maxBodies = Math.max(1, Math.floor(config.maxBodies));
  return {
    joints: Math.max(
      0,
      Math.floor(config.maxJoints ?? maxBodies * DEFAULT_MAX_JOINTS_PER_BODY_SOLVER),
    ),
    springs: Math.max(
      0,
      Math.floor(config.maxSprings ?? maxBodies * DEFAULT_MAX_SPRINGS_PER_BODY_SOLVER),
    ),
  };
}

function configuredCapacityIsContactOnly(config: PhysicsConfig): boolean {
  const maxima = configuredLogicalRecordMaxima(config);
  return maxima.joints === 0 && maxima.springs === 0;
}

function capacityInputFromConfig(config: PhysicsConfig): PhysicsEngineCapacityInput {
  return {
    maxBodies: config.maxBodies,
    pairEndpointEncoding: resolvePhysicsPairEndpointEncoding(
      config.maxBodies,
      config.pairEndpointEncoding,
    ),
    maxPairsPerBodyBroadphase: config.maxPairsPerBodyBroadphase,
    maxPersistentManifolds: config.maxPersistentManifolds,
    pairManifoldSlots: config.pairManifoldSlots,
    maxContactsPerBodySolver: config.maxContactsPerBodySolver,
    maxJoints: config.maxJoints,
    maxSprings: config.maxSprings,
    maxIgnoredPairs: config.maxIgnoredPairs,
  };
}

function packedPairForBodies(i: number, j: number): number {
  return packLegacyBodyPair(i, j);
}

function hashPackedPair(key: number): number {
  let value = key >>> 0;
  value = (value ^ (value >>> 16)) >>> 0;
  value = Math.imul(value, 0x7feb352d) >>> 0;
  value = (value ^ (value >>> 15)) >>> 0;
  value = Math.imul(value, 0x846ca68b) >>> 0;
  return (value ^ (value >>> 16)) >>> 0;
}

type IgnoredPairReservation = {
  readonly key: number;
  readonly slot: number;
  readonly alreadyPresent: boolean;
};

function nextPowerOfTwo(value: number): number {
  const target = Math.max(1, Math.ceil(value));
  if (target > 0x40000000) {
    throw new Error(`Sparse ignored-pair hash capacity is too large (${target} entries requested).`);
  }
  let result = 1;
  while (result < target) result *= 2;
  return result;
}

function writeFloat32Range(
  queue: GPUQueue,
  buffer: GPUBuffer,
  bufferOffset: number,
  data: Float32Array,
): void {
  queue.writeBuffer(
    buffer,
    bufferOffset,
    data.buffer as ArrayBuffer,
    data.byteOffset,
    data.byteLength,
  );
}

function pairReferenceSequence(i: number, j: number, bodyCount: number): number {
  const a = Math.min(i, j);
  const b = Math.max(i, j);
  if (!Number.isFinite(a) || !Number.isFinite(b) || a < 0 || b <= a || b >= bodyCount) {
    return 0;
  }
  return Math.floor((a * (2 * bodyCount - a - 1)) / 2 + (b - a - 1));
}

function normalizeQuat(q: [number, number, number, number]): [number, number, number, number] {
  const len = Math.hypot(q[0], q[1], q[2], q[3]);
  if (len <= 1e-12) return [0, 0, 0, 1];
  return [q[0] / len, q[1] / len, q[2] / len, q[3] / len];
}

function conjugateQuat(q: [number, number, number, number]): [number, number, number, number] {
  return [-q[0], -q[1], -q[2], q[3]];
}

function multiplyQuat(
  a: [number, number, number, number],
  b: [number, number, number, number],
): [number, number, number, number] {
  return [
    a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
    a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
    a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
    a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
  ];
}

function rotateVector(
  q: [number, number, number, number],
  v: [number, number, number],
): [number, number, number] {
  const rotated = multiplyQuat(multiplyQuat(normalizeQuat(q), [v[0], v[1], v[2], 0]), conjugateQuat(normalizeQuat(q)));
  return [rotated[0], rotated[1], rotated[2]];
}

type DecodedAvbdContactState = {
  dualX: number;
  dualY: number;
  dualZ: number;
  dualW: number;
  dualN: number;
  lambdaN: number;
  penaltyN: number;
  penaltyT1: number;
  penaltyT2: number;
  frictionScale: number;
  shadowLambdaN: number;
  shadowT1: number;
  shadowT2: number;
  shadowFrictionScale: number;
};

function decodeAvbdContactState(
  pairContacts: Float32Array,
  p: number,
  kStart = AVBD_K_START,
): DecodedAvbdContactState {
  const dualBase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_DUAL_OFFSET);
  const penaltyBase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_PENALTY_OFFSET);
  const shadowBase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_SHADOW_OFFSET);
  const dualX = pairContacts[dualBase] ?? 0.0;
  const dualY = pairContacts[dualBase + 1] ?? 0.0;
  const dualZ = pairContacts[dualBase + 2] ?? 0.0;
  const dualW = pairContacts[dualBase + 3] ?? 0.0;
  const penaltyX = pairContacts[penaltyBase] ?? 0.0;
  const penaltyY = pairContacts[penaltyBase + 1] ?? 0.0;
  const penaltyZ = pairContacts[penaltyBase + 2] ?? 0.0;
  const penaltyW = pairContacts[penaltyBase + 3] ?? 0.0;
  const shadowLambdaN = Math.max(pairContacts[shadowBase] ?? 0.0, 0.0);
  const shadowT1 = pairContacts[shadowBase + 1] ?? 0.0;
  const shadowT2 = pairContacts[shadowBase + 2] ?? 0.0;
  const shadowFrictionScale = Math.max(pairContacts[shadowBase + 3] ?? 0.0, 0.0);
  const penaltyFrictionScale = Math.max(penaltyW, 0.0);
  const frictionScale = Math.max(
    penaltyFrictionScale > 0.0 ? penaltyFrictionScale : shadowFrictionScale,
    0.0,
  );
  const dualN = Math.min(dualX, 0.0);
  return {
    dualX,
    dualY,
    dualZ,
    dualW,
    dualN,
    lambdaN: Math.max(-dualN, 0.0),
    penaltyN: Math.max(penaltyX, kStart),
    penaltyT1: Math.max(penaltyY, kStart),
    penaltyT2: Math.max(penaltyZ, kStart),
    frictionScale,
    shadowLambdaN,
    shadowT1,
    shadowT2,
    shadowFrictionScale,
  };
}

type DecodedPairContactViews = {
  meta: Float32Array;
  metaWords: Uint32Array;
  cacheWords: Uint32Array;
  normalPen: Float32Array;
  arms: Float32Array;
  armWords: Uint32Array;
  constraintC0: Float32Array;
  pairLambdas: Float32Array;
  dual: Float32Array;
  pairContacts: Float32Array;
  pairContactWords: Uint32Array;
};

function decodePairContactViews(pairContactsRaw: ArrayBuffer, maxPairContacts: number): DecodedPairContactViews {
  const pairContacts = new Float32Array(pairContactsRaw);
  const pairContactWords = new Uint32Array(pairContactsRaw);
  const meta = new Float32Array(maxPairContacts * 4);
  const metaWords = new Uint32Array(meta.buffer);
  const normalPen = new Float32Array(maxPairContacts * 4);
  const arms = new Float32Array(maxPairContacts * 8);
  const armWords = new Uint32Array(arms.buffer);
  const constraintC0 = new Float32Array(maxPairContacts * 4);
  const pairLambdas = new Float32Array(maxPairContacts * 4);
  const dual = new Float32Array(maxPairContacts * 8);
  const cacheWords = new Uint32Array(maxPairContacts);

  for (let p = 0; p < maxPairContacts; p++) {
    const metaBase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_META_OFFSET);
    const normalBase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_NORMAL_PEN_OFFSET);
    const armABase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_ARM_A_OFFSET);
    const armBBase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_ARM_B_OFFSET);
    const c0Base = contactRecordVec4FloatIndex(p, CONTACT_RECORD_CONSTRAINT_C0_OFFSET);
    const shadowBase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_SHADOW_OFFSET);
    const dualBase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_DUAL_OFFSET);
    const penaltyBase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_PENALTY_OFFSET);
    const cacheBase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_CACHE_OFFSET);

    meta.set(pairContacts.subarray(metaBase, metaBase + 4), p * 4);
    metaWords[p * 4 + 3] = pairContactWords[metaBase + 3] ?? 0;
    normalPen.set(pairContacts.subarray(normalBase, normalBase + 4), p * 4);
    arms.set(pairContacts.subarray(armABase, armABase + 4), p * 8);
    arms.set(pairContacts.subarray(armBBase, armBBase + 4), p * 8 + 4);
    armWords[p * 8 + 3] = pairContactWords[armABase + 3] ?? 0;
    armWords[p * 8 + 7] = pairContactWords[armBBase + 3] ?? 0;
    constraintC0.set(pairContacts.subarray(c0Base, c0Base + 4), p * 4);
    pairLambdas.set(pairContacts.subarray(shadowBase, shadowBase + 4), p * 4);
    dual.set(pairContacts.subarray(dualBase, dualBase + 4), p * 8);
    dual.set(pairContacts.subarray(penaltyBase, penaltyBase + 4), p * 8 + 4);
    cacheWords[p] = pairContactWords[cacheBase] ?? 0;
  }

  return {
    meta,
    metaWords,
    cacheWords,
    normalPen,
    arms,
    armWords,
    constraintC0,
    pairLambdas,
    dual,
    pairContacts,
    pairContactWords,
  };
}

type AvbdPrimalSolveTuning = {
  relaxation?: number;
  frictionRelaxation?: number;
  inertialDiagWeight?: number;
  maxLinearCorrection?: number;
  maxAngularCorrection?: number;
};

export type DebugContactPoint = {
  x: number;
  y: number;
  z: number;
};

/** Regression-only snapshot of one canonical active AVBD contact row. */
export type DebugAvbdContactState = {
  bodyA: number;
  bodyB: number;
  logicalSlot: number;
  dual: [number, number, number, number];
  penalty: [number, number, number, number];
};

export type RigidBodyReadbackState = {
  body: number;
  position: [number, number, number];
  initialPosition: [number, number, number];
  quaternion: [number, number, number, number];
  velocity: [number, number, number];
  angularVelocity: [number, number, number];
  inverseMass: number;
};

export type JointReadbackState = {
  joint: number;
  bodyA: number | null;
  bodyB: number;
  type: 'spherical' | 'fixed';
  anchorA: [number, number, number];
  anchorB: [number, number, number];
  torqueArm: number;
  restRelative: [number, number, number, number];
  stiffnessLin: number;
  stiffnessAng: number;
  c0Lin: [number, number, number];
  c0Ang: [number, number, number];
  lambdaLin: [number, number, number];
  lambdaAng: [number, number, number];
  penaltyLin: [number, number, number];
  penaltyAng: [number, number, number];
};

export type SpringReadbackState = {
  spring: number;
  bodyA: number | null;
  bodyB: number;
  restLength: number;
  materialStiffness: number;
  currentPenalty: number;
  constraint: number;
  currentLength: number;
  penaltyIncrement: number;
  lambda: number;
};

export type ContactRenderBuffers = {
  positions: StorageBufferAttribute;
  quaternions: StorageBufferAttribute;
  pairContacts: StorageBufferAttribute;
  pairActivity: StorageBufferAttribute;
  maxPairContacts: number;
  maxActivePairContacts: number;
  pairActivityWordCount: number;
  pairActiveContactsOffset: number;
};

export type SpringRenderBuffers = {
  positions: StorageBufferAttribute;
  quaternions: StorageBufferAttribute;
  springRecords: StorageBufferAttribute;
  maxSprings: number;
  generation: number;
};

export type OnDemandConstraintRecordTelemetry = {
  logicalMax: number;
  live: number;
  hardVelocityLive: number;
  allocated: number;
  generation: number;
  growthCount: number;
  retiredPending: number;
  retiredDestroyed: number;
  gpuOnly: boolean;
  initialized: boolean;
  /** Mutable scene-description/reset staging retained only for allocated rows. */
  cpuBytes: number;
  /** One vec4 placeholder owned by the Three storage attribute. */
  gpuAttributeCpuBytes: number;
  gpuBytes: number;
};

export type PhysicsAllocationTelemetry = {
  capacityPlan: {
    mode: PhysicsEngineCapacityMode;
    cpuBytes: number;
    gpuBytes: number;
    totalBytes: number;
    largestGpuBuffer: {
      key: string;
      label: string;
      bytes: number;
    };
    limitsChecked: boolean;
    limitsOk: boolean;
  };
  actualInstalled: {
    cpuBytes: number;
    gpuBytes: number;
    totalBytes: number;
    constraintRecordStagingCpuBytes: number;
    matchesCurrentRecordCapacities: boolean;
  };
  pairContacts: {
    gpuOnly: boolean;
    initialized: boolean;
    destroyed: boolean;
    cpuBytes: number;
    gpuBytes: number;
    expectedGpuBytes: number;
    plannerGpuBytes: number;
    gpuBytesMatchPlanner: boolean | null;
    logicalVec4Count: number;
  };
  persistentContacts: {
    mode: ContactPoolMode;
    descriptorCapacity: number;
    logicalSlotRows: number;
    physicalContactRows: number;
    mapWords: number;
    mapGpuOnly: boolean;
    initialized: boolean;
    destroyed: boolean;
    cpuBytes: number;
    gpuBytes: number;
    expectedGpuBytes: number;
    gpuBytesMatchPlanner: boolean | null;
    expectedGenerationMode: number;
  };
  constraintRecords: {
    joints: OnDemandConstraintRecordTelemetry;
    springs: OnDemandConstraintRecordTelemetry;
  };
  contactOnlyAdjacency: {
    aliasActive: boolean;
    cpuArrayAliased: boolean;
    gpuAttributeAliased: boolean | null;
    savedCpuBytes: number;
    savedGpuBytes: number;
    savedBytes: number;
  };
  optionalStages: {
    sleepingTopologyCache: SleepingTopologyCacheAllocationTelemetry | null;
    restitution: RestitutionStageAllocationTelemetry | null;
    velocityProjection: VelocityConstraintProjectionAllocationTelemetry | null;
  };
};

export type AvbdBodyColorTelemetry = {
  constrainedBodies: number;
  fallbackBodies: number;
  totalBodyConstraintRefs: number;
  saturatedBodyConstraintLists: number;
  maxBodyConstraintRefs: number;
  coloredConstraintEdges: number;
  sameColorConstraintEdges: number;
};

export type AvbdBodyColoringScheduleDiagnostics = Readonly<{
  dirty: boolean;
  initialized: boolean;
  pureTailAppendPending: boolean;
  lastRounds: 0 | 1 | 4 | 16;
  lastDirtyRounds: 0 | 1 | 4 | 16;
  pureTailAppendFastPathUses: number;
  fourRoundRepairUses: number;
  initialWarmupUses: number;
}>;

/**
 * CPU-only scheduling policy for the incremental Jacobi body coloring pass.
 * A tail append preserves every existing vertex/color and supplies a seeded
 * color for the new vertex, so the paper's normal one-round update is safe;
 * the GPU conflict-closure pass still diverts any unresolved edge to Jacobi.
 */
export function resolveAvbdBodyColoringRounds(
  dirty: boolean,
  initialized: boolean,
  pureTailAppendPending: boolean,
): 1 | 4 | 16 {
  if (!dirty) return 1;
  if (!initialized) return 16;
  return pureTailAppendPending ? 1 : 4;
}

/**
 * Read-only certification view combining the CPU-latched concrete AVBD kernel
 * branch with the GPU-owned coloring and sleeping results from that world.
 */
export type PhysicsSolvePathTelemetry = Readonly<{
  avbd: AvbdSolvePathTelemetry;
  parameters: Readonly<{
    linearDualUpdateBeta: number;
    angularDualUpdateBeta: number;
    regularizationAlpha: number;
    penaltyDecayGamma: number;
    penaltyFloor: number;
    solverIterations: number;
    pairSweeps: number;
    adaptiveIterationsConfigured: boolean;
  }>;
  capacities: Readonly<{
    maxJoints: number;
    maxSprings: number;
    maxIgnoredPairs: number;
  }>;
  sleepingConfigured: boolean;
  sleepingStageAllocated: boolean;
  sleepingStageRan: boolean;
  bodyColors: AvbdBodyColorTelemetry | null;
  sleeping: SleepingIslandsTelemetry | null;
}>;

export type SubstepBroadphaseRefreshFailureReason =
  | 'dispatch-exception'
  | 'solver-dispatch-exception'
  | 'candidate-overflow'
  | 'broadphase-safety-sample-failed'
  | 'contact-capacity-overflow'
  | 'ccd-fatal'
  | 'contact-safety-sample-failed'
  | 'broadphase-not-ready'
  | 'candidate-stream-unavailable';

export type ContactSafetyFailureReason = Extract<
  SubstepBroadphaseRefreshFailureReason,
  'contact-capacity-overflow' | 'ccd-fatal' | 'contact-safety-sample-failed'
>;

export type ContactSafetyFailureState = Readonly<{
  contactCapacityOverflow: boolean;
  ccdFatal: boolean;
  standaloneSampleFailed: boolean;
}>;

/**
 * Resolves independently latched contact-safety causes into the single public
 * halt reason. A concrete GPU fatal is more specific than a later observation
 * failure; if both physical bits are set, the incomplete contact graph is the
 * primary cause while the CCD bit remains latched for diagnostics.
 */
export function classifyContactSafetyFailure(
  state: ContactSafetyFailureState,
): ContactSafetyFailureReason | null {
  if (state.contactCapacityOverflow) return 'contact-capacity-overflow';
  if (state.ccdFatal) return 'ccd-fatal';
  if (state.standaloneSampleFailed) return 'contact-safety-sample-failed';
  return null;
}

export type ContactSafetyFailureReasonSelfTestResult = Readonly<{
  ok: boolean;
  checks: number;
  failures: readonly string[];
}>;

/** GPU-free proof that safety causes cannot alias the contact-overflow reason. */
export function runContactSafetyFailureReasonSelfTest():
ContactSafetyFailureReasonSelfTestResult {
  const failures: string[] = [];
  let checks = 0;
  const check = (name: string, condition: boolean): void => {
    checks++;
    if (!condition) failures.push(name);
  };
  check(
    'healthy contact safety has no failure reason',
    classifyContactSafetyFailure({
      contactCapacityOverflow: false,
      ccdFatal: false,
      standaloneSampleFailed: false,
    }) === null,
  );
  check(
    'contact capacity remains a contact-capacity overflow',
    classifyContactSafetyFailure({
      contactCapacityOverflow: true,
      ccdFatal: false,
      standaloneSampleFailed: false,
    }) === 'contact-capacity-overflow',
  );
  check(
    'CCD fatal has its own reason',
    classifyContactSafetyFailure({
      contactCapacityOverflow: false,
      ccdFatal: true,
      standaloneSampleFailed: false,
    }) === 'ccd-fatal',
  );
  check(
    'standalone sample infrastructure failure has its own reason',
    classifyContactSafetyFailure({
      contactCapacityOverflow: false,
      ccdFatal: false,
      standaloneSampleFailed: true,
    }) === 'contact-safety-sample-failed',
  );
  check(
    'concrete CCD fatal outranks a later sample failure',
    classifyContactSafetyFailure({
      contactCapacityOverflow: false,
      ccdFatal: true,
      standaloneSampleFailed: true,
    }) === 'ccd-fatal',
  );
  check(
    'contact graph loss outranks simultaneous CCD and sample failures',
    classifyContactSafetyFailure({
      contactCapacityOverflow: true,
      ccdFatal: true,
      standaloneSampleFailed: true,
    }) === 'contact-capacity-overflow',
  );
  return Object.freeze({
    ok: failures.length === 0,
    checks,
    failures: Object.freeze(failures),
  });
}

/**
 * CPU-owned proof that a multi-substep fixed step did not reuse a candidate
 * stream produced before an earlier substep's impulses. The initial stream is
 * deliberately excluded: attempts count only the mandatory s > 0 refreshes.
 */
export type SubstepBroadphaseRefreshTelemetry = Readonly<{
  attempts: number;
  successes: number;
  failures: number;
  attemptsThisFrame: number;
  failuresThisFrame: number;
  lastFrameId: number;
  lastFixedStepId: number;
  lastSubstepIndex: number;
  lastFailureReason: SubstepBroadphaseRefreshFailureReason | null;
  stickyFailure: boolean;
}>;

/**
 * CPU-only accounting for the debug support-floor lookup. The cache is not
 * consumed by contact generation or AVBD; only debug counters receive its
 * selected body index.
 */
export type SupportFloorDebugCacheTelemetry = Readonly<{
  scanCount: number;
  cacheHits: number;
  invalidations: number;
  valid: boolean;
  body: number;
  topY: number | null;
}>;

export type PersistentContactAllocatorState = {
  highWater: number;
  freeHead: number;
  /** Monotonic compact descriptor-ring tail (compact mode only). */
  freeTail?: number;
  /** Reusable compact descriptors after resolve head normalization. */
  nextManifoldFreeCount?: number;
  liveEntries: number;
  mode: number;
  rowHighWater?: number;
  rowLive?: number;
  rowPeakLive?: number;
  epochParity?: number;
  freeCount0?: number;
  freeCount1?: number;
  freeCursor0?: number;
  freeCursor1?: number;
  nextFreeCount?: number;
  fatalBits?: number;
  firstFatalEpoch?: number;
  newRowsThisStep?: number;
  retiredRowsThisStep?: number;
  /** Descriptor candidates examined by the last compact reclaim dispatch. */
  reclaimCandidateThreads?: number;
  reclaimWorkgroups?: number;
  /** Unconsumed physical rows copied by the last free-list carry dispatch. */
  finalizeCarryThreads?: number;
  finalizeWorkgroups?: number;
  /** 1 = N-way stripe, 2 = pressure full sweep, 3 = epoch-wrap full sweep. */
  reclaimMode?: number;
  reclaimPhase?: number;
  reclaimPeriod?: number;
  reclaimedManifoldsThisStep?: number;
  reclaimedRowsThisStep?: number;
  pressureFullSweepsThisStep?: number;
  newManifoldsThisStep?: number;
  preReclaimAvailableManifolds?: number;
  preReclaimAvailableRows?: number;
};

export type PhysicsCapacityPreflightSelfCheckResult = {
  ok: boolean;
  planner: CapacityPlannerSelfTestResult;
  checks: number;
  failures: readonly string[];
};

/**
 * GPU-free contract check for the capacity planner and the allocation policy
 * installed by PhysicsEngine. It creates only small plan metadata objects; it
 * never constructs a PhysicsEngine or allocates a typed-array/GPU workload.
 */
export function runPhysicsCapacityPreflightSelfCheck(): PhysicsCapacityPreflightSelfCheckResult {
  const planner = runCapacityPlannerSelfTests();
  const nativeCollectorPolicy = runAvbdNativeCommandCollectorPolicySelfTest();
  const contactSafetyFailureReasons = runContactSafetyFailureReasonSelfTest();
  const probe = planPhysicsEngineCapacity(
    {
      maxBodies: 32,
      maxPairsPerBodyBroadphase: 8,
      maxPersistentManifolds: 64,
      pairManifoldSlots: 4,
      maxContactsPerBodySolver: 16,
      maxJoints: 0,
      maxSprings: 0,
      maxIgnoredPairs: 0,
    },
    undefined,
    {
      mode: 'legacy',
      gpuOnlyBufferKeys: LEGACY_GPU_ONLY_CAPACITY_KEYS,
      allocatedJoints: 0,
      allocatedSprings: 0,
      constraintGraphUsesLogicalMax: true,
      contactOnlyAdjacencyAlias: true,
    },
  );
  const compactProbe = planPhysicsEngineCapacity(
    {
      maxBodies: 32,
      maxPairsPerBodyBroadphase: 8,
      maxPersistentManifolds: 64,
      pairManifoldSlots: 4,
      maxContactsPerBodySolver: 16,
      maxJoints: 0,
      maxSprings: 0,
      maxIgnoredPairs: 0,
    },
    undefined,
    {
      mode: 'optimized',
      compactContactPoolCapacity: 128,
      gpuOnlyBufferKeys: COMPACT_GPU_ONLY_CAPACITY_KEYS,
      allocatedJoints: 0,
      allocatedSprings: 0,
      constraintGraphUsesLogicalMax: true,
      contactOnlyAdjacencyAlias: true,
      includeSleepingTopologyCache: true,
    },
  );
  const failures: string[] = [];
  let checks = 0;
  const check = (name: string, condition: boolean): void => {
    checks++;
    if (!condition) failures.push(name);
  };
  check('capacity planner self-test', planner.ok);
  for (const failure of planner.failures) {
    failures.push(`capacity planner: ${failure}`);
  }
  checks += nativeCollectorPolicy.checks;
  for (const failure of nativeCollectorPolicy.failures) {
    failures.push(`native command collector: ${failure}`);
  }
  checks += contactSafetyFailureReasons.checks;
  for (const failure of contactSafetyFailureReasons.failures) {
    failures.push(`contact safety reason: ${failure}`);
  }
  const pairCpu = probe.cpuBuffers.find((buffer) => buffer.key === 'pairContacts');
  const pairGpu = probe.gpuBuffers.find((buffer) => buffer.key === 'pairContacts');
  const jointCpu = probe.cpuBuffers.find((buffer) => buffer.key === 'jointRecords');
  const springCpu = probe.cpuBuffers.find((buffer) => buffer.key === 'springRecords');
  const compactPairGpu = compactProbe.gpuBuffers.find(
    (buffer) => buffer.key === 'pairContacts',
  );
  const compactMapCpu = compactProbe.cpuBuffers.find(
    (buffer) => buffer.key === 'persistentManifoldMap',
  );
  const compactMapGpu = compactProbe.gpuBuffers.find(
    (buffer) => buffer.key === 'persistentManifoldMap',
  );
  const sleepingRootCpu = compactProbe.cpuBuffers.find(
    (buffer) => buffer.key === 'sleepingCachedBodyRoot',
  );
  const sleepingRootGpu = compactProbe.gpuBuffers.find(
    (buffer) => buffer.key === 'sleepingCachedBodyRoot',
  );
  const sleepingPairCpu = compactProbe.cpuBuffers.find(
    (buffer) => buffer.key === 'sleepingCachedPairKey',
  );
  const sleepingPairGpu = compactProbe.gpuBuffers.find(
    (buffer) => buffer.key === 'sleepingCachedPairKey',
  );
  const sleepingSeenCpu = compactProbe.cpuBuffers.find(
    (buffer) => buffer.key === 'sleepingCacheSeenBits',
  );
  const sleepingSeenGpu = compactProbe.gpuBuffers.find(
    (buffer) => buffer.key === 'sleepingCacheSeenBits',
  );
  const sleepingPairSeenCpu = compactProbe.cpuBuffers.find(
    (buffer) => buffer.key === 'sleepingCachePairSeenBits',
  );
  const sleepingPairSeenGpu = compactProbe.gpuBuffers.find(
    (buffer) => buffer.key === 'sleepingCachePairSeenBits',
  );

  check('installed capacity mode is legacy fixed-slot', probe.allocation.mode === 'legacy');
  check(
    'installed GPU-only key set includes contact/joint/spring records',
    probe.allocation.gpuOnlyBufferKeys.length === 3
      && probe.allocation.gpuOnlyBufferKeys[0] === 'pairContacts'
      && probe.allocation.gpuOnlyBufferKeys[1] === 'jointRecords'
      && probe.allocation.gpuOnlyBufferKeys[2] === 'springRecords',
  );
  check(
    'legacy contact pool retains fixed manifold slots',
    probe.derived.contactPoolRowCapacity === probe.derived.maxPairContacts,
  );
  check('contact-only adjacency is aliased', probe.allocation.contactOnlyAdjacencyAlias);
  check(
    'aliased adjacency has one physical CPU/GPU owner',
    !probe.cpuBuffers.some((buffer) => buffer.key === 'bodyConstraintRefs')
      && !probe.gpuBuffers.some((buffer) => buffer.key === 'bodyConstraintRefs'),
  );
  check(
    'pairContacts CPU storage is the one-vec4 placeholder',
    pairCpu?.gpuOnlyPlaceholder === true && pairCpu.bytes === 16,
  );
  check(
    'pairContacts planner retains the full GPU allocation',
    pairGpu !== undefined
      && pairGpu.bytes === probe.derived.maxPairContacts * CONTACT_RECORD_FLOATS * 4,
  );
  check(
    'joint/spring GPU attributes use one-vec4 CPU placeholders',
    jointCpu?.gpuOnlyPlaceholder === true
      && jointCpu.bytes === 16
      && springCpu?.gpuOnlyPlaceholder === true
      && springCpu.bytes === 16,
  );
  check(
    'zero-live joint/spring plans retain one sentinel GPU row',
    probe.derived.storageMaxJoints === 1
      && probe.derived.storageMaxSprings === 1
      && probe.derived.allocatedJoints === 0
      && probe.derived.allocatedSprings === 0,
  );
  check(
    'compact engine plan uses optimized physical row capacity',
    compactProbe.allocation.mode === 'optimized'
      && compactProbe.derived.maxPairContacts === 256
      && compactProbe.derived.contactPoolRowCapacity === 128
      && compactProbe.derived.maxActivePairContacts === 128,
  );
  check(
    'compact pair-contact GPU allocation uses physical rows',
    compactPairGpu?.bytes === 128 * CONTACT_RECORD_FLOATS * 4,
  );
  check(
    'compact persistent map is GPU-only with one-u32 CPU placeholder',
    compactMapCpu?.gpuOnlyPlaceholder === true
      && compactMapCpu.bytes === Uint32Array.BYTES_PER_ELEMENT
      && compactMapGpu?.bytes
        === compactProbe.derived.persistentManifoldMapWordCount
          * Uint32Array.BYTES_PER_ELEMENT,
  );
  check(
    'compact installed GPU-only set includes persistent map exactly once',
    compactProbe.allocation.gpuOnlyBufferKeys.filter(
      (key) => key === 'persistentManifoldMap',
    ).length === 1,
  );
  check(
    'sleeping topology cache retains only four one-u32 CPU placeholders',
    sleepingRootCpu?.bytes === 4
      && sleepingRootCpu.gpuOnlyPlaceholder === true
      && sleepingPairCpu?.bytes === 4
      && sleepingPairCpu.gpuOnlyPlaceholder === true
      && sleepingSeenCpu?.bytes === 4
      && sleepingSeenCpu.gpuOnlyPlaceholder === true
      && sleepingPairSeenCpu?.bytes === 4
      && sleepingPairSeenCpu.gpuOnlyPlaceholder === true,
  );
  check(
    'sleeping topology cache GPU byte contract is root plus pair hash and two bitsets',
    sleepingRootGpu?.bytes === 32 * 4
      && sleepingPairGpu?.bytes === 128 * 4
      && sleepingSeenGpu?.bytes === 4 * 4
      && sleepingPairSeenGpu?.bytes === 4 * 4
      && compactProbe.derived.sleepingCachePairHashCapacity === 128
      && compactProbe.derived.sleepingCacheRowSeenWordCount === 4
      && compactProbe.derived.sleepingCachePairSeenWordCount === 4
      && compactProbe.derived.sleepingCacheSeenWordCount === 8,
  );

  return Object.freeze({
    ok: planner.ok
      && nativeCollectorPolicy.ok
      && contactSafetyFailureReasons.ok
      && failures.length === 0,
    planner,
    checks,
    failures: Object.freeze(failures),
  });
}

export type PhysicsStartupPrewarmResult = Readonly<{
  ok: boolean;
  state: 'ready' | 'failed';
  commandCount: number;
  uniqueNodeCount: number;
  stats?: ThreeWebGpuComputePrewarmStats;
  reason?: string;
}>;

type BatchedRendererStoragePublish = {
  attribute: StorageBufferAttribute;
  data: Float32Array | Uint32Array;
  start: number;
  end: number;
};

export class PhysicsEngine {
  readonly config: PhysicsConfig;
  readonly device: GPUDevice;

  private bodyCount = 0;
  /** Bodies whose stored f32 inverse mass is finite and strictly positive. */
  private dynamicBodyCount = 0;
  private accumulator = 0;
  private initialized = false;
  private startupPrewarmState: 'idle' | 'collecting' | 'compiling' | 'ready' | 'failed' = 'idle';
  private startupPrewarmPromise: Promise<PhysicsStartupPrewarmResult> | null = null;
  private startupPrewarmCommands: ThreeWebGpuComputeCommand[] | null = null;
  private startupPrewarmError: string | null = null;
  private startupPrewarmGeneration = 0;
  private disposed = false;
  private nativeComputePassDescriptorFactory?: NativeComputePassDescriptorFactory;
  private readonly nativeComputePreparationCache: ThreeWebGpuComputePreparationCache =
    createThreeWebGpuComputePreparationCache();
  private readonly nativeComputeCommandCache: ThreeWebGpuComputeCommand[] = [];
  private storageMutationBatchDepth = 0;
  private readonly batchedRendererStoragePublishes =
    new Map<StorageBufferAttribute, BatchedRendererStoragePublish>();
  private batchedJointRecordStart = Number.POSITIVE_INFINITY;
  private batchedJointRecordEnd = 0;
  private batchedSpringRecordStart = Number.POSITIVE_INFINITY;
  private batchedSpringRecordEnd = 0;
  private mutationBatchCompletions = 0;
  private batchedRendererPublishRequests = 0;
  private batchedRendererPublishFlushes = 0;
  private batchedJointRecordRows = 0;
  private batchedSpringRecordRows = 0;
  private nativeCommandCacheHits = 0;
  private nativeCommandCacheMisses = 0;
  private hasAdvancedSimulation = false;
  private bodyColoringDirty = true;
  private bodyColoringInitialized = false;
  private bodyColoringPureTailAppendPending = false;
  private bodyColoringLastRounds: 0 | 1 | 4 | 16 = 0;
  private bodyColoringLastDirtyRounds: 0 | 1 | 4 | 16 = 0;
  private bodyColoringPureTailAppendFastPathUses = 0;
  private bodyColoringFourRoundRepairUses = 0;
  private bodyColoringInitialWarmupUses = 0;
  private readonly maxPairs: number;
  private readonly maxCandidatePairs: number;
  private readonly contactPoolMode: ContactPoolMode;
  /** Descriptor-space ceiling (M * S); never used as a physical row count. */
  private readonly logicalMaxPairContacts: number;
  private readonly compactContactPoolLayout?: CompactContactPoolLayout;
  private readonly maxPersistentManifolds: number;
  private readonly persistentManifoldHashCapacity: number;
  private readonly maxPairContacts: number;
  private readonly pairManifoldSlots: number;
  private readonly maxPairsPerBodyBroadphase: number;
  private readonly maxContactsPerBodySolver: number;
  // The fixed per-body arrays are a fast path, not a hard graph-degree cap.
  // High-degree bodies spill into compact global endpoint pools so one large
  // projectile cannot silently lose constraints (or permanently halt a scene).
  private readonly maxBodyContactOverflowNodes: number;
  private readonly maxBodyConstraintOverflowNodes: number;
  private readonly maxJoints: number;
  private storageMaxJoints: number;
  private readonly maxJointsPerBodySolver: number;
  private readonly maxSprings: number;
  private storageMaxSprings: number;
  private readonly maxSpringsPerBodySolver: number;
  private readonly maxIgnoredPairs: number;
  private readonly ignoredPairHashCapacity: number;
  private readonly maxConstraintsPerBodySolver: number;
  private readonly bruteForceMaxBodies: number;
  private readonly maxActivePairContacts: number;
  private readonly haltOnBroadphaseFallbackOverflow: boolean;
  private maxFixedStepsPerFrame: number;
  private readonly enableBvhBuild: boolean;
  private readonly submissionLedger: PhysicsSubmissionLedger;
  private capacityPlan: PhysicsEngineCapacityPlan;
  private readonly contactOnlyAdjacencyAlias: boolean;
  private readonly bvhBuildOnce: boolean;
  private readonly bvhRebuildIntervalFrames: number;
  private readonly bvhWaitForGpuCompletion: boolean;
  private avbdPairSweeps: number;
  private solverIterations: number;
  private avbdAdaptiveIterationsConfigured: boolean;
  private readonly avbdAdaptiveMinIterations: number;
  private readonly avbdAdaptiveLinearTolerance: number;
  private readonly avbdAdaptiveAngularTolerance: number;
  private readonly avbdAdaptiveDualTolerance: number;
  private readonly avbdAdaptivePenaltyTolerance: number;
  private readonly avbdAdaptiveForceInvalidForTesting: boolean;
  private readonly avbdAdaptiveDeferPreMinResiduals: boolean;
  private readonly maxPairSolveColorCount: number;
  private activePairSolveColorCount: number;
  private avbdFrictionStatic: number;
  private collisionMargin: number;
  private contactRestitution: number;
  private restitutionVelocityThreshold: number;
  private restitutionIterations: number;
  private readonly sleepingConfigured: boolean;
  private readonly sleepFrames: number;
  private readonly sleepLinearSpeed: number;
  private readonly sleepAngularSpeed: number;
  private avbdDualUpdateBeta: number;
  private avbdAngularDualUpdateBeta: number;
  private avbdRegularizationAlpha: number;
  private avbdPenaltyDecayGamma: number;
  private avbdBodySolveMode: AvbdBodySolveMode = 'colored';
  private avbdPreventPenetratingNormalDropout = false;
  private avbdPenaltyFloor = AVBD_K_START;
  private jointCount = 0;
  private springCount = 0;
  private hardVelocityConstraintCount = 0;
  private hardJointVelocityConstraintCount = 0;
  private hardSpringVelocityConstraintCount = 0;
  private conservativeIslandCount = 0;

  // CPU-side body data arrays (SoA, vec4f per body)
  private positionsData: Float32Array;      // (x, y, z, inverseMass)
  private initialPoseData: Float32Array;    // [initial position vec4, initial quaternion vec4] per body
  private initialLinearVelData: Float32Array; // initial linear velocity vec4 per body
  private initialAngularVelData: Float32Array; // initial angular velocity vec4 per body
  private inertialPoseData: Float32Array;   // [inertial pose, current solve pose, dTheta] as 5 vec4s per body
  private velocitiesData: Float32Array;     // (vx, vy, vz, 0)
  private prevLinearVelData: Float32Array;  // previous-step linear velocity for adaptive warmstart
  private shapesData: Float32Array;         // (packed friction/group/mask/type, sizeX, sizeY, sizeZ)
  private shapesWordData: Uint32Array;
  private quaternionsData: Float32Array;    // (qx, qy, qz, qw)
  private angularVelData: Float32Array;     // (wx, wy, wz, 0)
  private inverseInertiaData: Float32Array; // (Ixx⁻¹, Iyy⁻¹, Izz⁻¹, 0)
  // 0 = not eligible, 1..N = conservative island id + 1,
  // 0xffffffff = disqualified by a finite or body-to-body constraint.
  private conservativeBodyFlagsData: Uint32Array;

  // Packed pair contact buffer (contact + cache object in one physical store).
  // The CPU array is deliberately only a one-vec4 placeholder. The full
  // allocation is injected directly into Three's backend before any TSL node
  // can bind the attribute.
  private pairContactsData: Float32Array;
  private jointRecordsData: Float32Array;
  private springRecordsData: Float32Array;
  private pairActivityData: Uint32Array;
  private readonly pairCandidateIndicesOffset: number;
  private readonly pairActiveCandidateSlotsOffset: number;
  private readonly pairActiveContactsOffset: number;
  private readonly pairSolverActiveContactsOffset: number;
  private readonly pairActivityWordCount: number;
  private readonly pairEndpointEncoding: PairEndpointEncoding;
  // Selected candidate ABI region: packed-u16 is [count, packedPair...], while
  // wide-u32 is [count, bodyA plane..., bodyB plane...].
  private pairCandidateIndicesData: Uint32Array;
  private pairVisitedBitsData: Uint32Array; // bitset over pair indices
  private pairBodyContactCountsData: Uint32Array;  // contacts per body
  private pairBodyContactIndicesData: Uint32Array; // flat contact list indices
  private bodyConstraintCountsData: Uint32Array;   // generic solver constraint count per body
  private bodyConstraintRefsData: Uint32Array;     // tagged contact/joint/spring refs per body
  private pairActiveCandidateSlotsData: Uint32Array; // [count, candidateSlot0, ...]
  private pairActiveContactsData: Uint32Array;       // [count, contactIdx0, contactIdx1, ...]
  private ignoredPairHashData: Uint32Array;          // interleaved [packedPair, state] open-addressing table
  // [allocator header, hash keys, hash poolIndex+1, and lifecycle pool arrays].
  private persistentManifoldMapData: Uint32Array;
  private ignoredPairCount = 0;
  private pairColorBodyClaimsData: Uint32Array;    // temporary body claims for coloring

  // GPU storage buffers
  private positionsAttr!: StorageBufferAttribute;
  private initialPoseAttr!: StorageBufferAttribute;
  private inertialPoseAttr!: StorageBufferAttribute;
  private velocitiesAttr!: StorageBufferAttribute;
  private prevLinearVelAttr!: StorageBufferAttribute;
  private shapesAttr!: StorageBufferAttribute;
  private quaternionsAttr!: StorageBufferAttribute;
  private angularVelAttr!: StorageBufferAttribute;
  private inverseInertiaAttr!: StorageBufferAttribute;
  private conservativeBodyFlagsAttr!: StorageBufferAttribute;

  private pairContactsAttr!: StorageBufferAttribute;
  private pairContactsGpuAllocation?: GpuOnlyStorageAllocation<Float32Array>;
  private jointRecordsAttr!: StorageBufferAttribute;
  private springRecordsAttr!: StorageBufferAttribute;
  private jointRecordsGpuAllocation?: GpuOnlyStorageAllocation<Float32Array>;
  private springRecordsGpuAllocation?: GpuOnlyStorageAllocation<Float32Array>;
  private persistentManifoldMapGpuAllocation?: GpuOnlyStorageAllocation<Uint32Array>;
  private jointRecordGeneration = 0;
  private springRecordGeneration = 0;
  private jointRecordGrowthCount = 0;
  private springRecordGrowthCount = 0;
  private jointRecordRetiredPending = 0;
  private springRecordRetiredPending = 0;
  private jointRecordRetiredDestroyed = 0;
  private springRecordRetiredDestroyed = 0;
  private readonly retiredConstraintRecordAllocations = new Set<
    GpuOnlyStorageAllocation<Float32Array>
  >();
  private rendererRef?: any;
  private pairActivityAttr!: StorageBufferAttribute;
  private pairCandidateIndicesAttr!: StorageBufferAttribute;
  private pairVisitedBitsAttr!: StorageBufferAttribute;
  private pairBodyContactCountsAttr!: StorageBufferAttribute;
  private pairBodyContactIndicesAttr!: StorageBufferAttribute;
  private bodyConstraintCountsAttr!: StorageBufferAttribute;
  private bodyConstraintRefsAttr!: StorageBufferAttribute;
  private pairActiveCandidateSlotsAttr!: StorageBufferAttribute;
  private pairActiveContactsAttr!: StorageBufferAttribute;
  private ignoredPairHashAttr!: StorageBufferAttribute;
  private persistentManifoldMapAttr!: StorageBufferAttribute;
  private pairColorBodyClaimsAttr!: StorageBufferAttribute;
  private constraintSafetyGateAttr!: StorageBufferAttribute;
  /** Shared queue-ordered state for sleeping and the certified AABB fast path. */
  private sleepingBodyStateAttr?: StorageBufferAttribute;

  // Pipeline stages
  private integration!: IntegrationStage;
  private broadPhase!: BroadPhaseStage;
  private sleepingSparseBroadphaseRequested = false;
  private sleepingSparseBroadphaseMinimumBodyCount = 1;
  private sleepingSparseBroadphaseForceFull = false;
  private contactGeneration!: ContactGenerationStage;
  private constraintSafety!: ConstraintSafetyTransactionStage;
  private avbdState!: AvbdStateStage;
  private restitutionStage?: RestitutionStage;
  private velocityConstraintProjection?: VelocityConstraintProjectionStage;
  private sleepingIslands?: SleepingIslandsStage;
  private playerControl?: PlayerControlStage;
  private pairDispatchTruncationWarned = false;
  private waitingForInitialCandidatePairs = true;
  private substepBroadphaseRefreshAttempts = 0;
  private substepBroadphaseRefreshSuccesses = 0;
  private substepBroadphaseRefreshFailures = 0;
  private substepBroadphaseRefreshAttemptsThisFrame = 0;
  private substepBroadphaseRefreshFailuresThisFrame = 0;
  private substepBroadphaseRefreshLastFrameId = -1;
  private substepBroadphaseRefreshLastFixedStepId = -1;
  private substepBroadphaseRefreshLastSubstepIndex = -1;
  private substepBroadphaseRefreshLastFailureReason:
    SubstepBroadphaseRefreshFailureReason | null = null;
  private substepBroadphaseRefreshStickyFailure = false;
  private postContactSafetySampleOwnerFrameId = -1;
  private frameId = 0;
  private sleepTopologyGeneration = 0;
  private sleepingReadbackInFlight = false;
  private sleepingTelemetryMaintenancePending = false;
  private adaptiveTelemetryMaintenancePending = false;
  private pendingSleepingCertificationTelemetry: SleepingIslandsTelemetry | null = null;
  private sleepingCertificationPromise: Promise<SleepingIslandsTelemetry | null> | null = null;
  private sleepingCertificationGeneration = 0;
  private forceSleepingCertificationTimeoutForTesting = false;
  private readonly safetyReadbackTimeoutMs: number;
  private lastSleepingReadbackFrame = -1;
  private observedGravityF32: [number, number, number] = [0.0, 0.0, 0.0];
  private debugBehaviorEnabled = false;
  private debugLogEveryNFrames = 30;
  private supportDebugReadbackInFlight = false;
  private lastSupportDebugLogFrame = -1;
  private supportDebugEveryNFrames = 30;
  private supportFloorDebugCacheValid = false;
  private supportFloorDebugBody = -1;
  private supportFloorDebugTopY = Number.NEGATIVE_INFINITY;
  private supportFloorDebugScanCount = 0;
  private supportFloorDebugCacheHits = 0;
  private supportFloorDebugInvalidations = 0;
  private prevSupportBodyCount = -1;
  private prevCandidatePairsForChurn: Set<number> | null = null;
  private prevFloorCandidatePairsForChurn: Set<number> | null = null;

  stats: PhysicsStats = {
    bodyCount: 0,
    frameCount: 0,
    simulationStepCount: 0,
    totalMs: 0,
    integrationMs: 0,
    broadPhaseMs: 0,
    solverMs: 0,
    velocityUpdateMs: 0,
    broadPhaseReady: false,
    candidatePairsEnabled: false,
    pairDispatchTruncated: false,
    avbdIterationsLast: 0,
    avbdIterationsAverage: 0,
    avbdAdaptiveEarlyExits: 0,
    nativeComputeSourcePassesLast: 0,
    nativeComputeEncodedPassesLast: 0,
    nativeComputeCommandsLast: 0,
    nativeComputeInvariantNodeReusesLast: 0,
    sleepingBodies: 0,
    sleepingIslands: 0,
    sleepingFailOpen: false,
    sleepingZeroWorkSteps: 0,
  };

  constructor(device: GPUDevice, config: PhysicsConfig) {
    this.device = device;
    this.config = config;
    this.safetyReadbackTimeoutMs = normalizeConstraintSafetyReadbackTimeoutMs(
      config.safetyReadbackTimeoutMs,
    );
    this.observedGravityF32 = [
      Math.fround(config.gravity[0]),
      Math.fround(config.gravity[1]),
      Math.fround(config.gravity[2]),
    ];
    this.contactPoolMode = configuredContactPoolMode(config);
    const logicalRecordMaxima = configuredLogicalRecordMaxima(config);
    if (this.contactPoolMode === 'compact' && config.enableBvhBuild === false) {
      throw new Error('Compact contact pools require enableBvhBuild=true for descriptor generation.');
    }
    // This is deliberately the first capacity-dependent operation in the
    // constructor. The planner is metadata-only, so a device-limit failure is
    // reported before any of the large CPU mirrors below are materialized.
    this.capacityPlan = assertPhysicsEngineCapacityFitsDevice(
      capacityInputFromConfig(config),
      {
        maxStorageBufferBindingSize: Number(device.limits.maxStorageBufferBindingSize),
        maxBufferSize: Number(device.limits.maxBufferSize),
        maxComputeWorkgroupsPerDimension: Number(
          device.limits.maxComputeWorkgroupsPerDimension,
        ),
      },
      installedCapacityAllocationOptions(
        config,
        this.contactPoolMode,
        logicalRecordMaxima.joints > 0 ? 1 : 0,
        logicalRecordMaxima.springs > 0 ? 1 : 0,
        configuredCapacityIsContactOnly(config),
      ),
    );
    this.contactOnlyAdjacencyAlias = this.capacityPlan.allocation.contactOnlyAdjacencyAlias;
    this.pairEndpointEncoding = this.capacityPlan.input.pairEndpointEncoding;
    this.submissionLedger = new PhysicsSubmissionLedger();
    const n = this.capacityPlan.input.maxBodies;
    if (this.pairEndpointEncoding === 'wide-u32') {
      // Capacity planning and BroadPhaseStage now share the exact wide layout,
      // but the installed ContactGenerationStage still decodes one packed-u16
      // word and owns packed ignored/persistent-map keys. Starting that stage
      // against endpoint planes would silently corrupt identity, so keep this
      // boundary explicit until the descriptor bridge + exact narrowphase are
      // selected by the production host.
      throw new Error(
        'PhysicsEngine selected wide-u32 broadphase candidates, but the installed '
        + 'contact-generation/persistent-map pipeline still requires packed-u16. '
        + 'Production >65K requires the exact descriptor bridge and wide contact pipeline.',
      );
    }
    this.solverIterations = config.solverIterations ?? 8;
    this.avbdAdaptiveIterationsConfigured = config.avbdAdaptiveIterations ?? false;
    this.avbdAdaptiveMinIterations = Math.max(
      1,
      Math.min(64, Math.floor(config.avbdAdaptiveMinIterations ?? 6)),
    );
    this.avbdAdaptiveLinearTolerance = Math.max(
      0.0,
      config.avbdAdaptiveLinearTolerance ?? 2.5e-5,
    );
    this.avbdAdaptiveAngularTolerance = Math.max(
      0.0,
      config.avbdAdaptiveAngularTolerance ?? 2.5e-5,
    );
    this.avbdAdaptiveDualTolerance = Math.max(
      0.0,
      config.avbdAdaptiveDualTolerance ?? 2.5e-4,
    );
    this.avbdAdaptivePenaltyTolerance = Math.max(
      0.0,
      config.avbdAdaptivePenaltyTolerance ?? 2.5e-4,
    );
    this.avbdAdaptiveForceInvalidForTesting = Boolean(
      config.avbdAdaptiveForceInvalidForTesting,
    );
    this.avbdAdaptiveDeferPreMinResiduals =
      config.avbdAdaptiveDeferPreMinResiduals ?? true;
    this.maxPairSolveColorCount = Math.max(1, Math.floor(config.pairSolveColorCount ?? 64));
    this.activePairSolveColorCount = Math.min(this.maxPairSolveColorCount, 32);
    this.maxPairsPerBodyBroadphase = this.capacityPlan.input.maxPairsPerBodyBroadphase;
    this.maxContactsPerBodySolver = this.capacityPlan.input.maxContactsPerBodySolver;
    this.maxJoints = this.capacityPlan.input.maxJoints;
    this.maxSprings = this.capacityPlan.input.maxSprings;
    this.maxJointsPerBodySolver = this.capacityPlan.derived.maxJointsPerBodySolver;
    this.maxSpringsPerBodySolver = this.capacityPlan.derived.maxSpringsPerBodySolver;
    this.maxConstraintsPerBodySolver = this.capacityPlan.derived.maxConstraintsPerBodySolver;
    this.haltOnBroadphaseFallbackOverflow = config.haltOnBroadphaseFallbackOverflow ?? true;
    this.maxFixedStepsPerFrame = Math.max(1, Math.floor(config.maxFixedStepsPerFrame ?? 2));
    this.enableBvhBuild = config.enableBvhBuild ?? true;
    this.bvhBuildOnce = config.bvhBuildOnce ?? false;
    this.bvhRebuildIntervalFrames = Math.max(1, Math.floor(config.bvhRebuildIntervalFrames ?? 1));
    this.bvhWaitForGpuCompletion = config.bvhWaitForGpuCompletion ?? false;
    // One primal color sweep followed by one dual update is the public AVBD
    // iteration. It is also the uniform-invariant graph required by native
    // command fusion. Explicit 2-4 sweep diagnostics remain supported through
    // the slower immediate Three path in substep().
    this.avbdPairSweeps = Math.max(1, Math.min(4, Math.floor(
      config.avbdPairSweeps ?? AVBD_NATIVE_COMMAND_COLLECTOR_PAIR_SWEEPS,
    )));
    this.avbdFrictionStatic = Math.max(0.0, config.avbdFriction ?? AVBD_FRICTION_STATIC);
    this.collisionMargin = Math.max(0.0, config.collisionMargin ?? AVBD_COLLISION_MARGIN);
    this.contactRestitution = Math.max(0.0, Math.min(1.0, config.contactRestitution ?? 0.0));
    this.restitutionVelocityThreshold = Math.max(0.0, config.restitutionVelocityThreshold ?? 0.2);
    this.restitutionIterations = Math.max(0, Math.min(128, Math.floor(config.restitutionIterations ?? 12)));
    this.sleepingConfigured = config.enableSleeping ?? true;
    this.sleepingSparseBroadphaseRequested = Boolean(
      config.enableSleepingSparseBroadphase,
    );
    this.sleepingSparseBroadphaseMinimumBodyCount = Math.max(
      1,
      Math.floor(config.sleepingSparseMinimumBodyCount ?? 8192),
    );
    this.sleepFrames = Math.max(1, Math.min(3600, Math.floor(config.sleepFrames ?? 60)));
    this.sleepLinearSpeed = Math.max(0.0, config.sleepLinearSpeed ?? 0.025);
    this.sleepAngularSpeed = Math.max(0.0, config.sleepAngularSpeed ?? 0.025);
    this.avbdDualUpdateBeta = Math.max(
      0.0,
      config.avbdDualUpdateBeta ?? AVBD_REFERENCE_DUAL_UPDATE_BETA,
    );
    this.avbdAngularDualUpdateBeta = Math.max(
      0.0,
      config.avbdAngularDualUpdateBeta
        ?? (config.avbdDualUpdateBeta === undefined
          ? AVBD_REFERENCE_ANGULAR_DUAL_UPDATE_BETA
          : Math.min(this.avbdDualUpdateBeta, AVBD_REFERENCE_ANGULAR_DUAL_UPDATE_BETA)),
    );
    this.avbdRegularizationAlpha = Math.max(
      0.0,
      Math.min(
        1.0,
        config.avbdRegularizationAlpha ?? AVBD_REFERENCE_REGULARIZATION_ALPHA,
      ),
    );
    this.avbdPenaltyDecayGamma = Math.max(
      0.0,
      Math.min(
        1.0,
        config.avbdPenaltyDecayGamma ?? AVBD_REFERENCE_PENALTY_DECAY_GAMMA,
      ),
    );
    this.avbdBodySolveMode = config.avbdBodySolveMode ?? 'colored';

    this.pairManifoldSlots = this.capacityPlan.input.pairManifoldSlots;
    this.maxPairs = this.capacityPlan.derived.maxPairs;
    this.maxCandidatePairs = this.capacityPlan.derived.maxCandidatePairs;
    this.maxPersistentManifolds = this.capacityPlan.input.maxPersistentManifolds;
    this.persistentManifoldHashCapacity = this.capacityPlan.derived.persistentManifoldHashCapacity;
    this.logicalMaxPairContacts = this.capacityPlan.derived.maxPairContacts;
    // Every consumer below receives the physical row capacity. Descriptor
    // addressing remains M*S exclusively inside the persistent map layout.
    this.maxPairContacts = this.capacityPlan.derived.contactPoolRowCapacity;
    this.compactContactPoolLayout = this.contactPoolMode === 'compact'
      ? planCompactContactPoolLayout({
        persistentManifoldHashCapacity: this.persistentManifoldHashCapacity,
        maxPersistentManifolds: this.maxPersistentManifolds,
        pairManifoldSlots: this.pairManifoldSlots,
        compactContactPoolCapacity: this.maxPairContacts,
      })
      : undefined;
    // Active contacts are globally compact. Their capacity must not inherit
    // the fixed per-body fast-path degree: sparse spill nodes let a high-degree
    // body borrow unused graph capacity without dropping physical constraints.
    this.maxActivePairContacts = this.capacityPlan.derived.maxActivePairContacts;
    // Reserve a sparse global spill budget instead of multiplying every body
    // by the worst observed degree. Sixteen borrowed endpoints per body covers
    // rare high-degree contacts while remaining bounded; any genuine pool
    // exhaustion is still reported by the correctness diagnostics.
    this.maxBodyContactOverflowNodes = this.capacityPlan.derived.maxBodyContactOverflowNodes;
    // Contacts copied from the contact graph plus every possible joint/spring
    // endpoint can spill into the unified solver list without a per-body cap.
    this.maxBodyConstraintOverflowNodes = this.capacityPlan.derived.maxBodyConstraintOverflowNodes;
    // Joint/spring admission remains bounded by maxJoints/maxSprings, while
    // physical record storage starts at one sentinel row and grows only with
    // the live scene. This avoids reserving every possible record up front.
    this.storageMaxJoints = 1;
    this.storageMaxSprings = 1;
    this.maxIgnoredPairs = this.capacityPlan.input.maxIgnoredPairs;
    // A maximum 50% occupied load keeps lookup probes bounded while leaving
    // tombstone headroom for runtime joint/spring removal and re-insertion.
    this.ignoredPairHashCapacity = this.capacityPlan.derived.ignoredPairHashCapacity;
    const bruteForcePairCapacity = Math.min(this.maxCandidatePairs, this.maxPersistentManifolds);
    this.bruteForceMaxBodies = Math.floor((1 + Math.sqrt(1 + 8 * bruteForcePairCapacity)) * 0.5);
    const pairActivityLayout = this.capacityPlan.derived.pairActivityLayout;
    const candidateListWords = pairActivityLayout.activeCandidateCountOffset;
    const activeContactsWords = pairActivityLayout.activeContacts.length
      + CANDIDATE_LIST_HEADER_WORDS;
    this.pairCandidateIndicesOffset = pairActivityLayout.candidateCountOffset;
    this.pairActiveCandidateSlotsOffset = pairActivityLayout.activeCandidateCountOffset;
    this.pairActiveContactsOffset = pairActivityLayout.activeContactCountOffset;
    // Narrowphase no longer needs pairActiveCandidateSlots once it has emitted
    // the canonical active-contact list. Contact-only sleeping worlds reuse
    // that segment as an awake solver list, but only when it can hold every
    // canonical active row. Otherwise AVBD keeps the established in-place-mask
    // fallback and reads pairActiveContacts directly.
    this.pairSolverActiveContactsOffset = this.sleepingConfigured
      && this.maxJoints === 0
      && this.maxSprings === 0
      && this.maxCandidatePairs >= this.maxActivePairContacts
      ? this.pairActiveCandidateSlotsOffset
      : this.pairActiveContactsOffset;
    this.pairActivityWordCount = pairActivityLayout.totalWords;
    if (
      this.pairActivityWordCount !== this.capacityPlan.derived.pairActivityWordCount
      || this.pairActiveCandidateSlotsOffset
        !== this.capacityPlan.derived.pairActivityLayout.activeCandidateCountOffset
      || this.pairActiveContactsOffset
        !== this.capacityPlan.derived.pairActivityLayout.activeContactCountOffset
    ) {
      throw new Error('PhysicsEngine pair-activity layout diverged from capacity preflight.');
    }

    this.positionsData = new Float32Array(n * 4);
    this.initialPoseData = new Float32Array(n * 8);
    this.initialLinearVelData = new Float32Array(n * 4);
    this.initialAngularVelData = new Float32Array(n * 4);
    this.inertialPoseData = new Float32Array(n * INERTIAL_POSE_FLOATS_PER_BODY);
    this.velocitiesData = new Float32Array(n * 4);
    this.prevLinearVelData = new Float32Array(n * 4);
    this.shapesData = new Float32Array(n * 4);
    this.shapesWordData = new Uint32Array(this.shapesData.buffer);
    this.quaternionsData = new Float32Array(n * 4);
    this.angularVelData = new Float32Array(n * 4);
    this.inverseInertiaData = new Float32Array(n * 4);
    this.conservativeBodyFlagsData = new Uint32Array(n);

    this.pairContactsData = new Float32Array(4);
    this.jointRecordsData = new Float32Array(this.storageMaxJoints * JOINT_RECORD_FLOATS);
    this.springRecordsData = new Float32Array(this.storageMaxSprings * SPRING_RECORD_FLOATS);
    this.pairActivityData = new Uint32Array(this.pairActivityWordCount);
    this.pairCandidateIndicesData = this.pairActivityData.subarray(
      this.pairCandidateIndicesOffset,
      this.pairCandidateIndicesOffset + candidateListWords,
    );
    // Capacity preflight owns this policy so its CPU/GPU byte report remains
    // exact. Disabled production worlds retain only the required u32 sentinel.
    this.pairVisitedBitsData = new Uint32Array(
      this.capacityPlan.derived.pairVisitedWordCount,
    );
    this.pairBodyContactCountsData = new Uint32Array(n);
    const pairBodyContactFixedWords = n * this.maxContactsPerBodySolver;
    const pairBodyContactOverflowWords = 1 + n + this.maxBodyContactOverflowNodes * 2;
    this.pairBodyContactIndicesData = new Uint32Array(
      pairBodyContactFixedWords + pairBodyContactOverflowWords,
    );
    this.bodyConstraintCountsData = new Uint32Array(n);
    const bodyConstraintFixedWords = n * this.maxConstraintsPerBodySolver;
    const bodyConstraintOverflowWords = 1 + n + this.maxBodyConstraintOverflowNodes * 2;
    const bodyConstraintRefsWordCount = bodyConstraintFixedWords + bodyConstraintOverflowWords;
    if (this.contactOnlyAdjacencyAlias) {
      if (bodyConstraintRefsWordCount !== this.pairBodyContactIndicesData.length) {
        throw new Error('Contact-only adjacency alias requires identical contact/solver layouts.');
      }
      this.bodyConstraintRefsData = this.pairBodyContactIndicesData;
    } else {
      this.bodyConstraintRefsData = new Uint32Array(bodyConstraintRefsWordCount);
    }
    this.pairActiveCandidateSlotsData = this.pairActivityData.subarray(
      this.pairActiveCandidateSlotsOffset,
      this.pairActiveCandidateSlotsOffset + candidateListWords,
    );
    this.pairActiveContactsData = this.pairActivityData.subarray(
      this.pairActiveContactsOffset,
      this.pairActiveContactsOffset + activeContactsWords,
    );
    this.ignoredPairHashData = new Uint32Array(
      this.ignoredPairHashCapacity * IGNORED_PAIR_HASH_ENTRY_WORDS,
    );
    // Compact ownership is GPU-generated. Keep only the scalar placeholder on
    // CPU; init/reset kernels populate the canonical full map allocation.
    this.persistentManifoldMapData = this.contactPoolMode === 'compact'
      ? new Uint32Array(1)
      : new Uint32Array(this.capacityPlan.derived.persistentManifoldMapWordCount);
    this.pairColorBodyClaimsData = new Uint32Array(n);
  }

  private requiresCandidatePairPath(bodyCount = this.bodyCount): boolean {
    return this.contactPoolMode === 'compact'
      || (this.enableBvhBuild && bodyCount > this.bruteForceMaxBodies);
  }

  setNativeComputePassDescriptorFactory(
    factory: NativeComputePassDescriptorFactory | undefined,
  ): void {
    this.nativeComputePassDescriptorFactory = factory;
    this.broadPhase?.setNativeComputePassDescriptorFactory(factory);
  }

  /**
   * Coalesces synchronous topology mutations into final contiguous GPU writes.
   * CPU mutation order and every stored f32/u32 bit remain unchanged.
   */
  withMutationBatch<T>(callback: () => T): T {
    this.storageMutationBatchDepth++;
    try {
      return callback();
    } finally {
      this.storageMutationBatchDepth--;
      if (this.storageMutationBatchDepth === 0) {
        this.flushBatchedMutationUploads();
        this.mutationBatchCompletions++;
      }
    }
  }

  private queueBatchedRendererStoragePublish(
    attribute: StorageBufferAttribute,
    data: Float32Array | Uint32Array,
    start: number,
    count: number,
  ): void {
    this.batchedRendererPublishRequests++;
    const end = start + count;
    const existing = this.batchedRendererStoragePublishes.get(attribute);
    if (existing) {
      if (existing.data !== data) {
        throw new Error('A renderer storage attribute changed CPU backing data inside one batch.');
      }
      existing.start = Math.min(existing.start, start);
      existing.end = Math.max(existing.end, end);
      return;
    }
    this.batchedRendererStoragePublishes.set(attribute, {
      attribute,
      data,
      start,
      end,
    });
  }

  private queueBatchedJointRecordUpload(jointIndex: number): void {
    this.batchedJointRecordRows++;
    this.batchedJointRecordStart = Math.min(this.batchedJointRecordStart, jointIndex);
    this.batchedJointRecordEnd = Math.max(this.batchedJointRecordEnd, jointIndex + 1);
  }

  private queueBatchedSpringRecordUpload(springIndex: number): void {
    this.batchedSpringRecordRows++;
    this.batchedSpringRecordStart = Math.min(this.batchedSpringRecordStart, springIndex);
    this.batchedSpringRecordEnd = Math.max(this.batchedSpringRecordEnd, springIndex + 1);
  }

  private flushBatchedJointRecordUpload(): void {
    const start = this.batchedJointRecordStart;
    const end = this.batchedJointRecordEnd;
    this.batchedJointRecordStart = Number.POSITIVE_INFINITY;
    this.batchedJointRecordEnd = 0;
    if (!Number.isFinite(start) || end <= start || !this.initialized) return;
    const allocation = this.jointRecordsGpuAllocation;
    if (!allocation) {
      throw new Error('Joint record GPU storage is unavailable after initialization.');
    }
    writeFloat32Range(
      this.device.queue,
      allocation.buffer,
      start * JOINT_RECORD_FLOATS * Float32Array.BYTES_PER_ELEMENT,
      this.jointRecordsData.subarray(
        start * JOINT_RECORD_FLOATS,
        end * JOINT_RECORD_FLOATS,
      ),
    );
  }

  private flushBatchedSpringRecordUpload(): void {
    const start = this.batchedSpringRecordStart;
    const end = this.batchedSpringRecordEnd;
    this.batchedSpringRecordStart = Number.POSITIVE_INFINITY;
    this.batchedSpringRecordEnd = 0;
    if (!Number.isFinite(start) || end <= start || !this.initialized) return;
    const allocation = this.springRecordsGpuAllocation;
    if (!allocation) {
      throw new Error('Spring record GPU storage is unavailable after initialization.');
    }
    writeFloat32Range(
      this.device.queue,
      allocation.buffer,
      start * SPRING_RECORD_FLOATS * Float32Array.BYTES_PER_ELEMENT,
      this.springRecordsData.subarray(
        start * SPRING_RECORD_FLOATS,
        end * SPRING_RECORD_FLOATS,
      ),
    );
  }

  private flushBatchedRendererStoragePublishes(): void {
    if (this.batchedRendererStoragePublishes.size === 0) return;
    const pending = Array.from(this.batchedRendererStoragePublishes.values());
    this.batchedRendererStoragePublishes.clear();
    this.batchedRendererPublishFlushes += pending.length;
    for (const publish of pending) {
      this.publishRendererStorageRangeNow(
        publish.attribute,
        publish.data,
        publish.start,
        publish.end - publish.start,
      );
    }
  }

  private flushBatchedMutationUploads(): void {
    this.flushBatchedJointRecordUpload();
    this.flushBatchedSpringRecordUpload();
    this.flushBatchedRendererStoragePublishes();
  }

  /**
   * Publish a CPU-side edit to an already materialized Three storage buffer
   * without opening a renderer command encoder or adding a submit. This is
   * required for attributes read by the native broadphase encoder: leaving a
   * normal `needsUpdate` pending can make Three upload the edit only in a
   * later compute pass, after broadphase has already consumed the old pose (or
   * overwrite a native-produced list such as pairActivity).
   */
  private publishRendererStorageRange(
    attribute: StorageBufferAttribute,
    data: Float32Array | Uint32Array,
    elementStart: number,
    elementCount: number,
  ): boolean {
    const start = Math.max(0, Math.floor(elementStart));
    const count = Math.max(0, Math.min(
      Math.floor(elementCount),
      data.length - start,
    ));
    if (count <= 0) return true;
    if (this.storageMutationBatchDepth > 0) {
      this.queueBatchedRendererStoragePublish(attribute, data, start, count);
      return true;
    }
    return this.publishRendererStorageRangeNow(attribute, data, start, count);
  }

  private publishRendererStorageRangeNow(
    attribute: StorageBufferAttribute,
    data: Float32Array | Uint32Array,
    elementStart: number,
    elementCount: number,
  ): boolean {
    const start = Math.max(0, Math.floor(elementStart));
    const count = Math.max(0, Math.min(
      Math.floor(elementCount),
      data.length - start,
    ));
    if (count <= 0) return true;
    const renderer = this.rendererRef;
    const rendererAttributes = renderer?._attributes;
    const gpuBuffer = renderer?.backend?.get?.(attribute)?.buffer as GPUBuffer | undefined;
    if (!gpuBuffer || typeof rendererAttributes?.get !== 'function') {
      attribute.addUpdateRange(start, count);
      attribute.needsUpdate = true;
      return false;
    }

    // Flush any older ranges that were queued before the renderer buffer was
    // materialized, then publish this edit. queue.writeBuffer is ordered with
    // later command-buffer submissions but creates no submission of its own.
    const pendingRanges = attribute.updateRanges.map((range) => ({
      start: Math.max(0, Math.floor(range.start)),
      count: Math.max(0, Math.floor(range.count)),
    }));
    pendingRanges.push({ start, count });
    attribute.needsUpdate = true;
    for (const range of pendingRanges) {
      const rangeCount = Math.max(0, Math.min(range.count, data.length - range.start));
      if (rangeCount <= 0) continue;
      const view = data.subarray(range.start, range.start + rangeCount);
      this.device.queue.writeBuffer(
        gpuBuffer,
        range.start * data.BYTES_PER_ELEMENT,
        view.buffer as ArrayBuffer,
        view.byteOffset,
        view.byteLength,
      );
    }
    attribute.clearUpdateRanges();
    rendererAttributes.get(attribute).version = attribute.version;
    return true;
  }

  private markBodyColoringTailAppend(): void {
    // Multiple bodies may be appended before the next fixed step. Preserve the
    // fast-path classification only when every pending invalidation is another
    // tail append and a previously solved coloring exists for the old prefix.
    this.bodyColoringPureTailAppendPending = this.bodyColoringInitialized
      && (!this.bodyColoringDirty || this.bodyColoringPureTailAppendPending);
    this.bodyColoringDirty = true;
  }

  private invalidateBodyColoring(): void {
    this.bodyColoringDirty = true;
    this.bodyColoringInitialized = false;
    this.bodyColoringPureTailAppendPending = false;
  }

  private resetBodyColoringScheduleTelemetry(): void {
    this.bodyColoringLastRounds = 0;
    this.bodyColoringLastDirtyRounds = 0;
    this.bodyColoringPureTailAppendFastPathUses = 0;
    this.bodyColoringFourRoundRepairUses = 0;
    this.bodyColoringInitialWarmupUses = 0;
  }

  addBody(desc: RigidBodyDesc): number {
    if (this.bodyCount >= this.config.maxBodies) {
      throw new Error(`Exceeded maxBodies=${this.config.maxBodies}`);
    }

    const i = this.bodyCount++;
    this.markBodyColoringTailAppend();
    this.sleepTopologyGeneration = (this.sleepTopologyGeneration + 1) >>> 0;
    this.sleepingIslands?.requestBodyAppend(i);
    const invMass = desc.mass > 0 ? 1.0 / desc.mass : 0;

    // Seed the previous-color buffer with a deterministic spread. The paper's
    // incremental Jacobi coloring is allowed to start from arbitrary colors;
    // starting every newly spawned body at color zero creates an avoidable
    // all-Jacobi first frame that can collapse tall stacks before coloring
    // converges across timesteps.
    const seedColorCount = Math.max(1, Math.min(32, this.activePairSolveColorCount));
    this.bodyConstraintCountsData[i] = (i % seedColorCount) << 16;

    this.positionsData[i * 4 + 0] = desc.position[0];
    this.positionsData[i * 4 + 1] = desc.position[1];
    this.positionsData[i * 4 + 2] = desc.position[2];
    this.positionsData[i * 4 + 3] = invMass;
    if (Number.isFinite(this.positionsData[i * 4 + 3]) && this.positionsData[i * 4 + 3] > 0.0) {
      this.dynamicBodyCount += 1;
    }
    this.initialPoseData[i * 8 + 0] = desc.position[0];
    this.initialPoseData[i * 8 + 1] = desc.position[1];
    this.initialPoseData[i * 8 + 2] = desc.position[2];
    this.initialPoseData[i * 8 + 3] = invMass;

    const inertialBase = i * INERTIAL_POSE_FLOATS_PER_BODY;
    this.inertialPoseData[inertialBase + 0] = desc.position[0];
    this.inertialPoseData[inertialBase + 1] = desc.position[1];
    this.inertialPoseData[inertialBase + 2] = desc.position[2];
    this.inertialPoseData[inertialBase + 3] = invMass;

    if (desc.linearVelocity) {
      this.velocitiesData[i * 4 + 0] = desc.linearVelocity[0];
      this.velocitiesData[i * 4 + 1] = desc.linearVelocity[1];
      this.velocitiesData[i * 4 + 2] = desc.linearVelocity[2];
      this.prevLinearVelData[i * 4 + 0] = desc.linearVelocity[0];
      this.prevLinearVelData[i * 4 + 1] = desc.linearVelocity[1];
      this.prevLinearVelData[i * 4 + 2] = desc.linearVelocity[2];
      this.initialLinearVelData[i * 4 + 0] = desc.linearVelocity[0];
      this.initialLinearVelData[i * 4 + 1] = desc.linearVelocity[1];
      this.initialLinearVelData[i * 4 + 2] = desc.linearVelocity[2];
    }

    const shapeType = desc.shapeType === 'sphere' ? SHAPE_TYPE_SPHERE : SHAPE_TYPE_BOX;
    const halfExtents = desc.shapeType === 'sphere'
      ? [desc.radius, desc.radius, desc.radius] as const
      : desc.halfExtents;

    this.shapesWordData[i * 4 + 0] = packShapeMetaWord(
      clampShapeFriction(desc.friction ?? this.avbdFrictionStatic),
      clampCollisionFilterWord(desc.collisionGroup ?? DEFAULT_COLLISION_GROUP, DEFAULT_COLLISION_GROUP),
      clampCollisionFilterWord(desc.collisionMask ?? DEFAULT_COLLISION_MASK, DEFAULT_COLLISION_MASK),
      shapeType,
    );
    this.shapesData[i * 4 + 1] = halfExtents[0];
    this.shapesData[i * 4 + 2] = halfExtents[1];
    this.shapesData[i * 4 + 3] = halfExtents[2];

    // All GPU collision/solver paths maintain unit quaternions. Normalize the
    // only unchecked ingress once so candidate filtering does not repeat two
    // inverse-square-roots for every broadphase pair on every step.
    const q = normalizeQuat(desc.quaternion ?? [0, 0, 0, 1]);
    this.quaternionsData[i * 4 + 0] = q[0];
    this.quaternionsData[i * 4 + 1] = q[1];
    this.quaternionsData[i * 4 + 2] = q[2];
    this.quaternionsData[i * 4 + 3] = q[3];
    this.initialPoseData[i * 8 + 4] = q[0];
    this.initialPoseData[i * 8 + 5] = q[1];
    this.initialPoseData[i * 8 + 6] = q[2];
    this.initialPoseData[i * 8 + 7] = q[3];

    this.inertialPoseData[inertialBase + 4] = q[0];
    this.inertialPoseData[inertialBase + 5] = q[1];
    this.inertialPoseData[inertialBase + 6] = q[2];
    this.inertialPoseData[inertialBase + 7] = q[3];
    this.inertialPoseData[inertialBase + 8] = desc.position[0];
    this.inertialPoseData[inertialBase + 9] = desc.position[1];
    this.inertialPoseData[inertialBase + 10] = desc.position[2];
    this.inertialPoseData[inertialBase + 11] = invMass;
    this.inertialPoseData[inertialBase + 12] = q[0];
    this.inertialPoseData[inertialBase + 13] = q[1];
    this.inertialPoseData[inertialBase + 14] = q[2];
    this.inertialPoseData[inertialBase + 15] = q[3];
    this.inertialPoseData[inertialBase + 16] = 0.0;
    this.inertialPoseData[inertialBase + 17] = 0.0;
    this.inertialPoseData[inertialBase + 18] = 0.0;
    this.inertialPoseData[inertialBase + 19] = 0.0;

    if (desc.angularVelocity) {
      this.angularVelData[i * 4 + 0] = desc.angularVelocity[0];
      this.angularVelData[i * 4 + 1] = desc.angularVelocity[1];
      this.angularVelData[i * 4 + 2] = desc.angularVelocity[2];
      this.initialAngularVelData[i * 4 + 0] = desc.angularVelocity[0];
      this.initialAngularVelData[i * 4 + 1] = desc.angularVelocity[1];
      this.initialAngularVelData[i * 4 + 2] = desc.angularVelocity[2];
    }

    // Keep inverse mass in inverseInertia.w so solver kernels don't need positions.
    this.inverseInertiaData[i * 4 + 3] = invMass;

    if (desc.mass > 0) {
      if (desc.lockRotation) {
        this.inverseInertiaData[i * 4 + 0] = 0;
        this.inverseInertiaData[i * 4 + 1] = 0;
        this.inverseInertiaData[i * 4 + 2] = 0;
      } else {
        const m = desc.mass;
        let ixx = 0.0;
        let iyy = 0.0;
        let izz = 0.0;
        if (desc.shapeType === 'sphere') {
          const sphereInertia = 0.4 * m * desc.radius * desc.radius;
          ixx = sphereInertia;
          iyy = sphereInertia;
          izz = sphereInertia;
        } else {
          const [hx, hy, hz] = desc.halfExtents;
          ixx = (m / 3) * (hy * hy + hz * hz);
          iyy = (m / 3) * (hx * hx + hz * hz);
          izz = (m / 3) * (hx * hx + hy * hy);
        }
        this.inverseInertiaData[i * 4 + 0] = 1.0 / ixx;
        this.inverseInertiaData[i * 4 + 1] = 1.0 / iyy;
        this.inverseInertiaData[i * 4 + 2] = 1.0 / izz;
      }
    }

    // Support runtime spawning after GPU init.
    if (this.initialized) {
      const vec4Start = i * 4;
      const vec4Count = 4;
      const initialPoseStart = i * 8;
      const initialPoseCount = 8;
      const inertialPoseStart = i * INERTIAL_POSE_FLOATS_PER_BODY;
      const inertialPoseCount = INERTIAL_POSE_FLOATS_PER_BODY;

      this.publishRendererStorageRange(this.positionsAttr, this.positionsData, vec4Start, vec4Count);
      this.publishRendererStorageRange(
        this.initialPoseAttr,
        this.initialPoseData,
        initialPoseStart,
        initialPoseCount,
      );
      this.publishRendererStorageRange(
        this.inertialPoseAttr,
        this.inertialPoseData,
        inertialPoseStart,
        inertialPoseCount,
      );
      this.publishRendererStorageRange(this.velocitiesAttr, this.velocitiesData, vec4Start, vec4Count);
      this.publishRendererStorageRange(
        this.prevLinearVelAttr,
        this.prevLinearVelData,
        vec4Start,
        vec4Count,
      );
      this.publishRendererStorageRange(this.shapesAttr, this.shapesData, vec4Start, vec4Count);
      this.publishRendererStorageRange(
        this.quaternionsAttr,
        this.quaternionsData,
        vec4Start,
        vec4Count,
      );
      this.publishRendererStorageRange(this.angularVelAttr, this.angularVelData, vec4Start, vec4Count);
      this.publishRendererStorageRange(
        this.inverseInertiaAttr,
        this.inverseInertiaData,
        vec4Start,
        vec4Count,
      );
      this.publishRendererStorageRange(
        this.bodyConstraintCountsAttr,
        this.bodyConstraintCountsData,
        i,
        1,
      );
    }

    this.waitingForInitialCandidatePairs = this.requiresCandidatePairPath();

    if (invMass === 0.0) {
      this.invalidateSupportFloorDebugCache();
    }

    return i;
  }

  private getBodyQuaternion(body: number): [number, number, number, number] {
    return normalizeQuat([
      this.quaternionsData[body * 4 + 0] ?? 0.0,
      this.quaternionsData[body * 4 + 1] ?? 0.0,
      this.quaternionsData[body * 4 + 2] ?? 0.0,
      this.quaternionsData[body * 4 + 3] ?? 1.0,
    ]);
  }

  private getBodySize(body: number): [number, number, number] {
    return [
      (this.shapesData[body * 4 + 1] ?? 0.0) * 2.0,
      (this.shapesData[body * 4 + 2] ?? 0.0) * 2.0,
      (this.shapesData[body * 4 + 3] ?? 0.0) * 2.0,
    ];
  }

  private getBodyPosition(body: number): [number, number, number] {
    return [
      this.positionsData[body * 4 + 0] ?? 0.0,
      this.positionsData[body * 4 + 1] ?? 0.0,
      this.positionsData[body * 4 + 2] ?? 0.0,
    ];
  }

  setBodyPose(
    body: number,
    position: [number, number, number],
    quaternion?: [number, number, number, number],
    linearVelocity?: [number, number, number],
    angularVelocity?: [number, number, number],
  ): void {
    if (body < 0 || body >= this.bodyCount) return;
    const supportFloorMayChange = this.positionsData[body * 4 + 3] === 0.0;
    this.sleepingIslands?.requestLocalMutation();
    // Do not flush every sleeping island for a local pose edit. The sleeping
    // stage compares this body's uploaded pose/velocity against its retained
    // history and propagates the wake only through the current contact island.

    const bodyBase = body * 4;
    const poseBase = body * 8;
    const inertialBase = body * INERTIAL_POSE_FLOATS_PER_BODY;
    this.positionsData[bodyBase + 0] = position[0];
    this.positionsData[bodyBase + 1] = position[1];
    this.positionsData[bodyBase + 2] = position[2];
    this.initialPoseData[poseBase + 0] = position[0];
    this.initialPoseData[poseBase + 1] = position[1];
    this.initialPoseData[poseBase + 2] = position[2];

    this.inertialPoseData[inertialBase + 0] = position[0];
    this.inertialPoseData[inertialBase + 1] = position[1];
    this.inertialPoseData[inertialBase + 2] = position[2];
    this.inertialPoseData[inertialBase + 8] = position[0];
    this.inertialPoseData[inertialBase + 9] = position[1];
    this.inertialPoseData[inertialBase + 10] = position[2];
    // setBodyPose also replaces the step's initial pose, so the cached
    // current-vs-initial rotation is exactly zero regardless of whether the
    // caller supplies a new quaternion.
    this.inertialPoseData[inertialBase + 16] = 0.0;
    this.inertialPoseData[inertialBase + 17] = 0.0;
    this.inertialPoseData[inertialBase + 18] = 0.0;
    this.inertialPoseData[inertialBase + 19] = 0.0;

    if (quaternion) {
      const q = normalizeQuat(quaternion);
      this.quaternionsData[bodyBase + 0] = q[0];
      this.quaternionsData[bodyBase + 1] = q[1];
      this.quaternionsData[bodyBase + 2] = q[2];
      this.quaternionsData[bodyBase + 3] = q[3];
      this.initialPoseData[poseBase + 4] = q[0];
      this.initialPoseData[poseBase + 5] = q[1];
      this.initialPoseData[poseBase + 6] = q[2];
      this.initialPoseData[poseBase + 7] = q[3];
      this.inertialPoseData[inertialBase + 4] = q[0];
      this.inertialPoseData[inertialBase + 5] = q[1];
      this.inertialPoseData[inertialBase + 6] = q[2];
      this.inertialPoseData[inertialBase + 7] = q[3];
      this.inertialPoseData[inertialBase + 12] = q[0];
      this.inertialPoseData[inertialBase + 13] = q[1];
      this.inertialPoseData[inertialBase + 14] = q[2];
      this.inertialPoseData[inertialBase + 15] = q[3];
    }

    if (linearVelocity) {
      this.velocitiesData[bodyBase + 0] = linearVelocity[0];
      this.velocitiesData[bodyBase + 1] = linearVelocity[1];
      this.velocitiesData[bodyBase + 2] = linearVelocity[2];
      this.prevLinearVelData[bodyBase + 0] = linearVelocity[0];
      this.prevLinearVelData[bodyBase + 1] = linearVelocity[1];
      this.prevLinearVelData[bodyBase + 2] = linearVelocity[2];
    }

    if (angularVelocity) {
      this.angularVelData[bodyBase + 0] = angularVelocity[0];
      this.angularVelData[bodyBase + 1] = angularVelocity[1];
      this.angularVelData[bodyBase + 2] = angularVelocity[2];
    }
    if (this.initialized) {
      this.publishRendererStorageRange(this.positionsAttr, this.positionsData, bodyBase, 4);
      this.publishRendererStorageRange(this.initialPoseAttr, this.initialPoseData, poseBase, 8);
      this.publishRendererStorageRange(
        this.inertialPoseAttr,
        this.inertialPoseData,
        inertialBase,
        INERTIAL_POSE_FLOATS_PER_BODY,
      );
      if (quaternion) {
        this.publishRendererStorageRange(
          this.quaternionsAttr,
          this.quaternionsData,
          bodyBase,
          4,
        );
      }
      if (linearVelocity) {
        this.publishRendererStorageRange(this.velocitiesAttr, this.velocitiesData, bodyBase, 4);
        this.publishRendererStorageRange(
          this.prevLinearVelAttr,
          this.prevLinearVelData,
          bodyBase,
          4,
        );
      }
      if (angularVelocity) {
        this.publishRendererStorageRange(this.angularVelAttr, this.angularVelData, bodyBase, 4);
      }
    }
    if (supportFloorMayChange) {
      this.invalidateSupportFloorDebugCache();
    }
  }

  private markWorldConservativeBody(body: number): void {
    if (body < 0 || body >= this.bodyCount) return;
    if (this.conservativeBodyFlagsData[body] !== 0xffffffff) {
      if (this.conservativeBodyFlagsData[body] === 0) {
        this.conservativeBodyFlagsData[body] = 1;
        this.conservativeIslandCount = Math.max(this.conservativeIslandCount, 1);
      }
      if (this.initialized) {
        this.publishRendererStorageRange(
          this.conservativeBodyFlagsAttr,
          this.conservativeBodyFlagsData,
          body,
          1,
        );
      }
    }
  }

  private disqualifyConservativeBody(body: number | null): void {
    if (body === null || body < 0 || body >= this.bodyCount) return;
    this.conservativeBodyFlagsData[body] = 0xffffffff;
    if (this.initialized) {
      this.publishRendererStorageRange(
        this.conservativeBodyFlagsAttr,
        this.conservativeBodyFlagsData,
        body,
        1,
      );
    }
  }

  setBodyConservativeIsland(body: number, island: number): void {
    if (body < 0 || body >= this.bodyCount) return;
    if (this.conservativeBodyFlagsData[body] === 0xffffffff) return;
    this.sleepingIslands?.requestLocalMutation();
    // A conservative flag is itself a local wake seed in SleepingIslandsStage;
    // keep disconnected islands asleep instead of forcing a global flush.
    const boundedIsland = Math.max(0, Math.min(this.config.maxBodies - 1, Math.floor(island)));
    this.conservativeBodyFlagsData[body] = boundedIsland + 1;
    this.conservativeIslandCount = Math.max(this.conservativeIslandCount, boundedIsland + 1);
    if (this.initialized) {
      this.publishRendererStorageRange(
        this.conservativeBodyFlagsAttr,
        this.conservativeBodyFlagsData,
        body,
        1,
      );
    }
  }

  private preflightInstalledRecordCapacities(
    jointCapacity: number,
    springCapacity: number,
  ): PhysicsEngineCapacityPlan {
    return assertPhysicsEngineCapacityFitsDevice(
      capacityInputFromConfig(this.config),
      {
        maxStorageBufferBindingSize: Number(this.device.limits.maxStorageBufferBindingSize),
        maxBufferSize: Number(this.device.limits.maxBufferSize),
        maxComputeWorkgroupsPerDimension: Number(
          this.device.limits.maxComputeWorkgroupsPerDimension,
        ),
      },
      installedCapacityAllocationOptions(
        this.config,
        this.contactPoolMode,
        this.maxJoints > 0 ? jointCapacity : 0,
        this.maxSprings > 0 ? springCapacity : 0,
        this.contactOnlyAdjacencyAlias,
      ),
    );
  }

  private ensureJointRecordCpuCapacity(requiredRows: number): void {
    if (requiredRows <= this.storageMaxJoints) return;
    const nextCapacity = nextPowerOfTwo(requiredRows);
    const nextPlan = this.preflightInstalledRecordCapacities(
      nextCapacity,
      this.storageMaxSprings,
    );
    const next = new Float32Array(nextCapacity * JOINT_RECORD_FLOATS);
    next.set(this.jointRecordsData);
    this.jointRecordsData = next;
    this.storageMaxJoints = nextCapacity;
    this.capacityPlan = nextPlan;
  }

  private ensureSpringRecordCpuCapacity(requiredRows: number): void {
    if (requiredRows <= this.storageMaxSprings) return;
    const nextCapacity = nextPowerOfTwo(requiredRows);
    const nextPlan = this.preflightInstalledRecordCapacities(
      this.storageMaxJoints,
      nextCapacity,
    );
    const next = new Float32Array(nextCapacity * SPRING_RECORD_FLOATS);
    next.set(this.springRecordsData);
    this.springRecordsData = next;
    this.storageMaxSprings = nextCapacity;
    this.capacityPlan = nextPlan;
  }

  private retireConstraintRecordAllocation(
    allocation: GpuOnlyStorageAllocation<Float32Array>,
    kind: 'joint' | 'spring',
  ): void {
    this.retiredConstraintRecordAllocations.add(allocation);
    if (kind === 'joint') this.jointRecordRetiredPending++;
    else this.springRecordRetiredPending++;
    void this.device.queue.onSubmittedWorkDone()
      .catch(() => undefined)
      .then(() => {
        allocation.destroy();
        this.retiredConstraintRecordAllocations.delete(allocation);
        if (kind === 'joint') {
          this.jointRecordRetiredPending = Math.max(0, this.jointRecordRetiredPending - 1);
          this.jointRecordRetiredDestroyed++;
        } else {
          this.springRecordRetiredPending = Math.max(0, this.springRecordRetiredPending - 1);
          this.springRecordRetiredDestroyed++;
        }
      });
  }

  private rebuildConstraintStagesForRecordGrowth(
    nextJointRecords: StorageBufferAttribute,
    nextSpringRecords: StorageBufferAttribute,
    nextJointCapacity: number,
    nextSpringCapacity: number,
  ): {
    avbdState: AvbdStateStage;
    velocityProjection?: VelocityConstraintProjectionStage;
  } {
    const avbdState = this.createAvbdStateStage(
      nextJointRecords,
      nextSpringRecords,
      nextJointCapacity,
      nextSpringCapacity,
    );
    const velocityProjection = this.velocityConstraintProjection && this.restitutionStage
      ? this.createVelocityConstraintProjection(
        nextJointRecords,
        nextSpringRecords,
        nextJointCapacity,
        nextSpringCapacity,
        this.restitutionStage,
      )
      : undefined;
    return { avbdState, velocityProjection };
  }

  private uploadJointRecord(jointIndex: number): void {
    if (!this.initialized) return;
    const renderer = this.rendererRef;
    const previous = this.jointRecordsGpuAllocation;
    if (!renderer || !previous) {
      throw new Error('Joint record GPU storage is unavailable after initialization.');
    }
    const requiredRows = jointIndex + 1;
    const previousCapacity = previous.count / JOINT_RECORD_VEC4S;
    const row = this.jointRecordsData.subarray(
      jointIndex * JOINT_RECORD_FLOATS,
      requiredRows * JOINT_RECORD_FLOATS,
    );
    if (requiredRows <= previousCapacity) {
      if (this.storageMutationBatchDepth > 0) {
        this.queueBatchedJointRecordUpload(jointIndex);
        return;
      }
      writeFloat32Range(
        this.device.queue,
        previous.buffer,
        jointIndex * JOINT_RECORD_FLOATS * Float32Array.BYTES_PER_ELEMENT,
        row,
      );
      return;
    }
    this.flushBatchedJointRecordUpload();

    const next = createGpuOnlyStorageAttribute(renderer, this.device, {
      scalarType: 'float32',
      count: this.storageMaxJoints * JOINT_RECORD_VEC4S,
      itemSize: 4,
      label: `AVBD joint records generation ${this.jointRecordGeneration + 1}`,
    });
    try {
      const encoder = this.device.createCommandEncoder({
        label: 'AVBD grow joint record storage',
      });
      encoder.copyBufferToBuffer(previous.buffer, 0, next.buffer, 0, previous.gpuByteLength);
      this.submissionLedger.withActualSubmitScope(
        this.device.queue,
        {
          frameId: this.frameId,
          submissionClass: 'topology',
          label: 'joint-record-growth-copy',
        },
        () => this.device.queue.submit([encoder.finish()]),
      );
      this.submissionLedger.record('topology', 'joint-record-growth-copy', {
        frameId: this.frameId,
      });
      writeFloat32Range(
        this.device.queue,
        next.buffer,
        jointIndex * JOINT_RECORD_FLOATS * Float32Array.BYTES_PER_ELEMENT,
        row,
      );
      const stages = this.rebuildConstraintStagesForRecordGrowth(
        next.attribute,
        this.springRecordsAttr,
        this.storageMaxJoints,
        this.storageMaxSprings,
      );
      const previousVelocityProjection = this.velocityConstraintProjection;
      this.jointRecordsGpuAllocation = next;
      this.jointRecordsAttr = next.attribute;
      this.avbdState = stages.avbdState;
      this.velocityConstraintProjection = stages.velocityProjection;
      previousVelocityProjection?.dispose();
      this.jointRecordGeneration++;
      this.jointRecordGrowthCount++;
      this.invalidateBodyColoring();
      this.retireConstraintRecordAllocation(previous, 'joint');
    } catch (error) {
      this.retireConstraintRecordAllocation(next, 'joint');
      throw error;
    }
  }

  private uploadSpringRecord(springIndex: number): void {
    if (!this.initialized) return;
    const renderer = this.rendererRef;
    const previous = this.springRecordsGpuAllocation;
    if (!renderer || !previous) {
      throw new Error('Spring record GPU storage is unavailable after initialization.');
    }
    const requiredRows = springIndex + 1;
    const previousCapacity = previous.count / SPRING_RECORD_VEC4S;
    const row = this.springRecordsData.subarray(
      springIndex * SPRING_RECORD_FLOATS,
      requiredRows * SPRING_RECORD_FLOATS,
    );
    if (requiredRows <= previousCapacity) {
      if (this.storageMutationBatchDepth > 0) {
        this.queueBatchedSpringRecordUpload(springIndex);
        return;
      }
      writeFloat32Range(
        this.device.queue,
        previous.buffer,
        springIndex * SPRING_RECORD_FLOATS * Float32Array.BYTES_PER_ELEMENT,
        row,
      );
      return;
    }
    this.flushBatchedSpringRecordUpload();

    const next = createGpuOnlyStorageAttribute(renderer, this.device, {
      scalarType: 'float32',
      count: this.storageMaxSprings * SPRING_RECORD_VEC4S,
      itemSize: 4,
      label: `AVBD spring records generation ${this.springRecordGeneration + 1}`,
    });
    try {
      const encoder = this.device.createCommandEncoder({
        label: 'AVBD grow spring record storage',
      });
      encoder.copyBufferToBuffer(previous.buffer, 0, next.buffer, 0, previous.gpuByteLength);
      this.submissionLedger.withActualSubmitScope(
        this.device.queue,
        {
          frameId: this.frameId,
          submissionClass: 'topology',
          label: 'spring-record-growth-copy',
        },
        () => this.device.queue.submit([encoder.finish()]),
      );
      this.submissionLedger.record('topology', 'spring-record-growth-copy', {
        frameId: this.frameId,
      });
      writeFloat32Range(
        this.device.queue,
        next.buffer,
        springIndex * SPRING_RECORD_FLOATS * Float32Array.BYTES_PER_ELEMENT,
        row,
      );
      const stages = this.rebuildConstraintStagesForRecordGrowth(
        this.jointRecordsAttr,
        next.attribute,
        this.storageMaxJoints,
        this.storageMaxSprings,
      );
      const previousVelocityProjection = this.velocityConstraintProjection;
      this.springRecordsGpuAllocation = next;
      this.springRecordsAttr = next.attribute;
      this.avbdState = stages.avbdState;
      this.velocityConstraintProjection = stages.velocityProjection;
      previousVelocityProjection?.dispose();
      this.springRecordGeneration++;
      this.springRecordGrowthCount++;
      this.invalidateBodyColoring();
      this.retireConstraintRecordAllocation(previous, 'spring');
    } catch (error) {
      this.retireConstraintRecordAllocation(next, 'spring');
      throw error;
    }
  }

  private preflightIgnoredPairReservation(
    bodyA: number | null,
    bodyB: number,
    disableCollision: boolean,
  ): IgnoredPairReservation | null {
    if (!disableCollision || bodyA === null || bodyA === bodyB) return null;
    const key = packedPairForBodies(bodyA, bodyB);
    if (key < 0) return null;
    const existingSlot = this.findIgnoredPairSlot(key);
    if (existingSlot >= 0) {
      return { key, slot: existingSlot, alreadyPresent: true };
    }
    if (this.ignoredPairCount >= this.maxIgnoredPairs) {
      throw new Error(
        `Exceeded maxIgnoredPairs=${this.maxIgnoredPairs} while ignoring body pair ${bodyA}/${bodyB}.`,
      );
    }
    const slot = this.findIgnoredPairInsertSlot(key);
    if (slot < 0) {
      throw new Error(
        `Sparse ignored-pair hash overflow at capacity=${this.ignoredPairHashCapacity}.`,
      );
    }
    return { key, slot, alreadyPresent: false };
  }

  private commitIgnoredPairReservation(reservation: IgnoredPairReservation | null): void {
    if (!reservation || reservation.alreadyPresent) return;
    const base = reservation.slot * IGNORED_PAIR_HASH_ENTRY_WORDS;
    this.ignoredPairHashData[base] = reservation.key >>> 0;
    this.ignoredPairHashData[base + 1] = IGNORED_PAIR_HASH_STATE_OCCUPIED;
    this.ignoredPairCount++;
    this.markIgnoredPairHashSlotDirty(reservation.slot);
  }

  private appendJoint(desc: JointDesc): number {
    if (this.jointCount >= this.maxJoints) {
      throw new Error(`Exceeded maxJoints=${this.maxJoints}`);
    }
    if (desc.bodyB < 0 || desc.bodyB >= this.bodyCount) {
      throw new Error(`Invalid joint bodyB=${desc.bodyB}`);
    }
    if (desc.bodyA !== null && (desc.bodyA < 0 || desc.bodyA >= this.bodyCount)) {
      throw new Error(`Invalid joint bodyA=${desc.bodyA}`);
    }

    const jointIndex = this.jointCount;
    const ignoredPairReservation = this.preflightIgnoredPairReservation(
      desc.bodyA,
      desc.bodyB,
      desc.disableCollision ?? true,
    );
    this.ensureJointRecordCpuCapacity(jointIndex + 1);
    const bodyA = desc.bodyA ?? WORLD_BODY_INDEX;
    const bodyB = desc.bodyB;
    const type = desc.type === 'fixed' ? 1 : 0;
    const stiffness = Math.max(desc.stiffness ?? Number.POSITIVE_INFINITY, AVBD_K_START);
    const hardVelocityConstraint = !Number.isFinite(stiffness);
    const bodyASize = desc.bodyA === null ? [0.0, 0.0, 0.0] : this.getBodySize(desc.bodyA);
    const bodyBSize = this.getBodySize(bodyB);
    const torqueArm = (
      (bodyASize[0] + bodyBSize[0]) * (bodyASize[0] + bodyBSize[0]) +
      (bodyASize[1] + bodyBSize[1]) * (bodyASize[1] + bodyBSize[1]) +
      (bodyASize[2] + bodyBSize[2]) * (bodyASize[2] + bodyBSize[2])
    );

    const qA = desc.bodyA === null ? [0, 0, 0, 1] as [number, number, number, number] : this.getBodyQuaternion(desc.bodyA);
    const qB = this.getBodyQuaternion(bodyB);
    const restRelative = normalizeQuat(multiplyQuat(conjugateQuat(qA), qB));

    const metaBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_META_OFFSET);
    const metaWords = new Uint32Array(this.jointRecordsData.buffer, metaBase * 4, 4);
    metaWords[0] = bodyA >>> 0;
    metaWords[1] = bodyB >>> 0;
    metaWords[2] = type >>> 0;
    metaWords[3] = 1;

    const anchorABase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_ANCHOR_A_OFFSET);
    this.jointRecordsData[anchorABase + 0] = desc.anchorA[0];
    this.jointRecordsData[anchorABase + 1] = desc.anchorA[1];
    this.jointRecordsData[anchorABase + 2] = desc.anchorA[2];
    this.jointRecordsData[anchorABase + 3] = torqueArm;

    const anchorBBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_ANCHOR_B_OFFSET);
    this.jointRecordsData[anchorBBase + 0] = desc.anchorB[0];
    this.jointRecordsData[anchorBBase + 1] = desc.anchorB[1];
    this.jointRecordsData[anchorBBase + 2] = desc.anchorB[2];
    this.jointRecordsData[anchorBBase + 3] = 0.0;

    const restBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_REST_RELATIVE_ROTATION_OFFSET);
    this.jointRecordsData[restBase + 0] = restRelative[0];
    this.jointRecordsData[restBase + 1] = restRelative[1];
    this.jointRecordsData[restBase + 2] = restRelative[2];
    this.jointRecordsData[restBase + 3] = restRelative[3];

    const stiffnessBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_STIFFNESS_OFFSET);
    this.jointRecordsData[stiffnessBase + 0] = stiffness;
    this.jointRecordsData[stiffnessBase + 1] = type === 1 ? stiffness : 0.0;
    this.jointRecordsData[stiffnessBase + 2] = 0.0;
    this.jointRecordsData[stiffnessBase + 3] = 0.0;

    const c0LinBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_C0_LIN_OFFSET);
    const c0AngBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_C0_ANG_OFFSET);
    const lambdaLinBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_LAMBDA_LIN_OFFSET);
    const lambdaAngBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_LAMBDA_ANG_OFFSET);
    const penaltyLinBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_PENALTY_LIN_OFFSET);
    const penaltyAngBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_PENALTY_ANG_OFFSET);
    this.jointRecordsData.fill(0.0, c0LinBase, c0LinBase + 4);
    this.jointRecordsData.fill(0.0, c0AngBase, c0AngBase + 4);
    this.jointRecordsData.fill(0.0, lambdaLinBase, lambdaLinBase + 4);
    this.jointRecordsData.fill(0.0, lambdaAngBase, lambdaAngBase + 4);
    this.jointRecordsData.fill(0.0, penaltyLinBase, penaltyLinBase + 4);
    this.jointRecordsData.fill(0.0, penaltyAngBase, penaltyAngBase + 4);

    this.uploadJointRecord(jointIndex);
    this.commitIgnoredPairReservation(ignoredPairReservation);
    if (desc.bodyA === null && hardVelocityConstraint) {
      this.markWorldConservativeBody(bodyB);
    } else {
      this.disqualifyConservativeBody(desc.bodyA);
      this.disqualifyConservativeBody(bodyB);
    }
    if (hardVelocityConstraint) {
      this.hardVelocityConstraintCount++;
      this.hardJointVelocityConstraintCount++;
    }
    this.sleepTopologyGeneration = (this.sleepTopologyGeneration + 1) >>> 0;
    this.sleepingIslands?.requestWakeAll();
    this.invalidateBodyColoring();
    // Publish admission last. Every fallible capacity/hash/GPU operation has
    // completed, so a rejected append cannot expose a half-created record.
    this.jointCount = jointIndex + 1;

    return jointIndex;
  }

  private appendSpring(desc: SpringDesc): number {
    if (this.springCount >= this.maxSprings) {
      throw new Error(`Exceeded maxSprings=${this.maxSprings}`);
    }
    if (desc.bodyB < 0 || desc.bodyB >= this.bodyCount) {
      throw new Error(`Invalid spring bodyB=${desc.bodyB}`);
    }
    if (desc.bodyA !== null && (desc.bodyA < 0 || desc.bodyA >= this.bodyCount)) {
      throw new Error(`Invalid spring bodyA=${desc.bodyA}`);
    }

    const springIndex = this.springCount;
    const ignoredPairReservation = this.preflightIgnoredPairReservation(
      desc.bodyA,
      desc.bodyB,
      desc.disableCollision ?? false,
    );
    this.ensureSpringRecordCpuCapacity(springIndex + 1);
    const bodyA = desc.bodyA ?? WORLD_BODY_INDEX;
    const bodyB = desc.bodyB;
    const requestedStiffness = desc.stiffness ?? 100.0;
    const rigidDistance = !Number.isFinite(requestedStiffness);
    const stiffness = rigidDistance
      ? AVBD_HARD_CONSTRAINT_PENALTY_MAX
      : Math.max(requestedStiffness, 0.0);

    const qA = desc.bodyA === null ? [0, 0, 0, 1] as [number, number, number, number] : this.getBodyQuaternion(desc.bodyA);
    const qB = this.getBodyQuaternion(bodyB);
    const posA = desc.bodyA === null ? desc.anchorA : this.getBodyPosition(desc.bodyA);
    const posB = this.getBodyPosition(bodyB);
    const worldAnchorA = desc.bodyA === null
      ? desc.anchorA
      : (() => {
        const offset = rotateVector(qA, desc.anchorA);
        return [
          posA[0] + offset[0],
          posA[1] + offset[1],
          posA[2] + offset[2],
        ] as [number, number, number];
      })();
    const worldAnchorB = (() => {
      const offset = rotateVector(qB, desc.anchorB);
      return [
        posB[0] + offset[0],
        posB[1] + offset[1],
        posB[2] + offset[2],
      ] as [number, number, number];
    })();
    const restLength = Math.max(
      desc.restLength ?? Math.hypot(
        worldAnchorA[0] - worldAnchorB[0],
        worldAnchorA[1] - worldAnchorB[1],
        worldAnchorA[2] - worldAnchorB[2],
      ),
      0.0,
    );

    const metaBase = springRecordVec4FloatIndex(springIndex, SPRING_RECORD_META_OFFSET);
    const metaWords = new Uint32Array(this.springRecordsData.buffer, metaBase * 4, 4);
    metaWords[0] = bodyA >>> 0;
    metaWords[1] = bodyB >>> 0;
    metaWords[2] = 1;
    metaWords[3] = rigidDistance ? 1 : 0;

    const anchorABase = springRecordVec4FloatIndex(springIndex, SPRING_RECORD_ANCHOR_A_OFFSET);
    this.springRecordsData[anchorABase + 0] = desc.anchorA[0];
    this.springRecordsData[anchorABase + 1] = desc.anchorA[1];
    this.springRecordsData[anchorABase + 2] = desc.anchorA[2];
    this.springRecordsData[anchorABase + 3] = restLength;

    const anchorBBase = springRecordVec4FloatIndex(springIndex, SPRING_RECORD_ANCHOR_B_OFFSET);
    this.springRecordsData[anchorBBase + 0] = desc.anchorB[0];
    this.springRecordsData[anchorBBase + 1] = desc.anchorB[1];
    this.springRecordsData[anchorBBase + 2] = desc.anchorB[2];
    this.springRecordsData[anchorBBase + 3] = stiffness;

    const stateBase = springRecordVec4FloatIndex(springIndex, SPRING_RECORD_STATE_OFFSET);
    this.springRecordsData[stateBase + 0] = stiffness > 0.0
      ? Math.min(stiffness, this.avbdPenaltyFloor)
      : 0.0;
    this.springRecordsData[stateBase + 1] = 0.0;
    this.springRecordsData[stateBase + 2] = restLength;
    this.springRecordsData[stateBase + 3] = 0.0;

    this.uploadSpringRecord(springIndex);
    this.commitIgnoredPairReservation(ignoredPairReservation);
    if (desc.bodyA === null && rigidDistance) {
      this.markWorldConservativeBody(bodyB);
    } else {
      this.disqualifyConservativeBody(desc.bodyA);
      this.disqualifyConservativeBody(bodyB);
    }
    if (rigidDistance) {
      this.hardVelocityConstraintCount++;
      this.hardSpringVelocityConstraintCount++;
    }
    this.sleepTopologyGeneration = (this.sleepTopologyGeneration + 1) >>> 0;
    this.sleepingIslands?.requestWakeAll();
    this.invalidateBodyColoring();
    this.springCount = springIndex + 1;

    return springIndex;
  }

  private findIgnoredPairSlot(key: number): number {
    const mask = this.ignoredPairHashCapacity - 1;
    let slot = hashPackedPair(key) & mask;
    for (let probe = 0; probe < this.ignoredPairHashCapacity; probe++) {
      const base = slot * IGNORED_PAIR_HASH_ENTRY_WORDS;
      const state = this.ignoredPairHashData[base + 1] ?? IGNORED_PAIR_HASH_STATE_EMPTY;
      if (state === IGNORED_PAIR_HASH_STATE_EMPTY) return -1;
      if (
        state === IGNORED_PAIR_HASH_STATE_OCCUPIED
        && (this.ignoredPairHashData[base] ?? 0) === key
      ) {
        return slot;
      }
      slot = (slot + 1) & mask;
    }
    return -1;
  }

  private findIgnoredPairInsertSlot(key: number): number {
    const mask = this.ignoredPairHashCapacity - 1;
    let slot = hashPackedPair(key) & mask;
    let firstTombstone = -1;
    for (let probe = 0; probe < this.ignoredPairHashCapacity; probe++) {
      const base = slot * IGNORED_PAIR_HASH_ENTRY_WORDS;
      const state = this.ignoredPairHashData[base + 1] ?? IGNORED_PAIR_HASH_STATE_EMPTY;
      if (state === IGNORED_PAIR_HASH_STATE_EMPTY) {
        return firstTombstone >= 0 ? firstTombstone : slot;
      }
      if (state === IGNORED_PAIR_HASH_STATE_TOMBSTONE && firstTombstone < 0) {
        firstTombstone = slot;
      }
      slot = (slot + 1) & mask;
    }
    return firstTombstone;
  }

  private markIgnoredPairHashSlotDirty(slot: number): void {
    if (!this.initialized) return;
    const base = slot * IGNORED_PAIR_HASH_ENTRY_WORDS;
    this.publishRendererStorageRange(
      this.ignoredPairHashAttr,
      this.ignoredPairHashData,
      base,
      IGNORED_PAIR_HASH_ENTRY_WORDS,
    );
  }

  private setPairCollisionIgnored(bodyA: number, bodyB: number, ignored: boolean): void {
    const key = packedPairForBodies(bodyA, bodyB);
    if (key < 0) return;

    const existingSlot = this.findIgnoredPairSlot(key);
    if (ignored) {
      if (existingSlot >= 0) return;
      if (this.ignoredPairCount >= this.maxIgnoredPairs) {
        throw new Error(
          `Exceeded maxIgnoredPairs=${this.maxIgnoredPairs} while ignoring body pair ${bodyA}/${bodyB}.`,
        );
      }
      const slot = this.findIgnoredPairInsertSlot(key);
      if (slot < 0) {
        throw new Error(
          `Sparse ignored-pair hash overflow at capacity=${this.ignoredPairHashCapacity}.`,
        );
      }
      const base = slot * IGNORED_PAIR_HASH_ENTRY_WORDS;
      this.ignoredPairHashData[base] = key >>> 0;
      this.ignoredPairHashData[base + 1] = IGNORED_PAIR_HASH_STATE_OCCUPIED;
      this.ignoredPairCount++;
      this.markIgnoredPairHashSlotDirty(slot);
      return;
    }

    if (existingSlot < 0) return;
    const base = existingSlot * IGNORED_PAIR_HASH_ENTRY_WORDS;
    this.ignoredPairHashData[base + 1] = IGNORED_PAIR_HASH_STATE_TOMBSTONE;
    this.ignoredPairCount = Math.max(0, this.ignoredPairCount - 1);
    this.markIgnoredPairHashSlotDirty(existingSlot);
  }

  setBodyPairCollisionIgnored(bodyA: number, bodyB: number, ignored = true): void {
    if (bodyA < 0 || bodyA >= this.bodyCount) return;
    if (bodyB < 0 || bodyB >= this.bodyCount) return;
    if (bodyA === bodyB) return;
    this.setPairCollisionIgnored(bodyA, bodyB, ignored);
    this.sleepTopologyGeneration = (this.sleepTopologyGeneration + 1) >>> 0;
    this.sleepingIslands?.requestReset();
  }

  setBodyCollisionFilter(body: number, collisionGroup: number, collisionMask: number): void {
    if (body < 0 || body >= this.bodyCount) return;
    this.sleepTopologyGeneration = (this.sleepTopologyGeneration + 1) >>> 0;
    this.sleepingIslands?.requestReset();
    const base = body * 4;
    const currentMeta = this.shapesWordData[base] ?? 0;
    const currentFriction = decodeShapeFrictionWord(currentMeta);
    const currentShapeType = decodeShapeTypeWord(currentMeta);
    this.shapesWordData[base] = packShapeMetaWord(
      currentFriction,
      clampCollisionFilterWord(collisionGroup, DEFAULT_COLLISION_GROUP),
      clampCollisionFilterWord(collisionMask, DEFAULT_COLLISION_MASK),
      currentShapeType,
    );
    if (this.initialized) {
      this.publishRendererStorageRange(this.shapesAttr, this.shapesData, base, 1);
    }
  }

  addSphericalJoint(
    bodyA: number | null,
    bodyB: number,
    anchorA: [number, number, number],
    anchorB: [number, number, number],
    stiffness = Number.POSITIVE_INFINITY,
    disableCollision = true,
  ): number {
    return this.appendJoint({
      type: 'spherical',
      bodyA,
      bodyB,
      anchorA,
      anchorB,
      stiffness,
      disableCollision,
    });
  }

  addFixedJoint(
    bodyA: number | null,
    bodyB: number,
    anchorA: [number, number, number],
    anchorB: [number, number, number],
    stiffness = Number.POSITIVE_INFINITY,
    disableCollision = true,
  ): number {
    return this.appendJoint({
      type: 'fixed',
      bodyA,
      bodyB,
      anchorA,
      anchorB,
      stiffness,
      disableCollision,
    });
  }

  addSpring(
    bodyA: number | null,
    bodyB: number,
    anchorA: [number, number, number],
    anchorB: [number, number, number],
    stiffness = 100.0,
    restLength?: number,
    disableCollision = true,
  ): number {
    return this.appendSpring({
      bodyA,
      bodyB,
      anchorA,
      anchorB,
      stiffness,
      restLength,
      disableCollision,
    });
  }

  addDistanceConstraint(
    bodyA: number | null,
    bodyB: number,
    anchorA: [number, number, number],
    anchorB: [number, number, number],
    restLength?: number,
    disableCollision = true,
  ): number {
    // A hard distance row starts at the same global kStart as every other AVBD
    // hard constraint. Its penalty then follows the paper's adaptive dual
    // update and Eq. 19 warm start; resetSimulationToInitialPose() restores the
    // identical value, independent of endpoint ordering or substep count.
    return this.appendSpring({
      bodyA,
      bodyB,
      anchorA,
      anchorB,
      stiffness: Number.POSITIVE_INFINITY,
      restLength,
      disableCollision,
    });
  }

  private createAvbdStateStage(
    jointRecords: StorageBufferAttribute,
    springRecords: StorageBufferAttribute,
    jointCapacity: number,
    springCapacity: number,
  ): AvbdStateStage {
    const stage = new AvbdStateStage(
      this.pairContactsAttr,
      jointRecords,
      springRecords,
      this.positionsAttr,
      this.initialPoseAttr,
      this.inertialPoseAttr,
      this.quaternionsAttr,
      this.velocitiesAttr,
      this.prevLinearVelAttr,
      this.angularVelAttr,
      this.inverseInertiaAttr,
      this.bodyConstraintCountsAttr,
      this.bodyConstraintRefsAttr,
      this.pairBodyContactCountsAttr,
      this.pairBodyContactIndicesAttr,
      this.pairColorBodyClaimsAttr,
      this.pairActivityAttr,
      this.contactGeneration.getActiveManifoldsAttribute(),
      this.persistentManifoldMapAttr,
      this.maxPairSolveColorCount,
      this.config.maxBodies,
      this.maxPairContacts,
      Math.max(1, jointCapacity),
      Math.max(1, springCapacity),
      this.maxActivePairContacts,
      this.maxConstraintsPerBodySolver,
      this.maxContactsPerBodySolver,
      this.pairManifoldSlots,
      this.pairActiveContactsOffset,
      this.pairSolverActiveContactsOffset,
      this.pairActivityWordCount,
      this.maxPersistentManifolds,
      this.compactContactPoolLayout?.slotRow.offset ?? 0,
      this.compactContactPoolLayout?.outputMask.offset ?? 0,
      this.maxJoints > 0 || this.maxSprings > 0,
      this.config.avbdCacheContactTangentInvLength ?? true,
      this.config.avbdDenseContactSolverState ?? true,
      this.config.avbdSpecializeReferenceFinalizer ?? true,
      this.config.avbdCooperativeBodySolve ?? false,
      (this.config.avbdManifoldCapture ?? false)
        && this.contactPoolMode === 'compact',
      this.config.avbdCaptureBodyDeltaCache ?? false,
      this.config.avbdPackedContactSolverGeometry ?? true,
      // Keep the exact fused kernel available for diagnostics, but leave it
      // opt-in. 50K A/B shows a driver/occupancy cliff once the contact set is
      // mature even though the resulting state is bit-identical.
      this.config.avbdFuseFinalCaptureCommit ?? false,
    );
    stage.setDebugEnabled(this.debugBehaviorEnabled);
    stage.setDebugLogInterval(this.debugLogEveryNFrames);
    stage.setFriction(1.0, 1.0);
    stage.setCollisionMargin(this.collisionMargin);
    stage.setDualUpdateBetas(
      this.avbdDualUpdateBeta,
      this.avbdAngularDualUpdateBeta,
    );
    stage.setPenaltyDecayGamma(this.avbdPenaltyDecayGamma);
    stage.setPenaltyFloor(this.avbdPenaltyFloor);
    stage.setPreventPenetratingNormalDropout(this.avbdPreventPenetratingNormalDropout);
    return stage;
  }

  private createVelocityConstraintProjection(
    jointRecords: StorageBufferAttribute,
    springRecords: StorageBufferAttribute,
    jointCapacity: number,
    springCapacity: number,
    restitutionStage: RestitutionStage,
  ): VelocityConstraintProjectionStage {
    return new VelocityConstraintProjectionStage(
      jointRecords,
      springRecords,
      this.positionsAttr,
      this.quaternionsAttr,
      this.velocitiesAttr,
      this.angularVelAttr,
      this.inverseInertiaAttr,
      this.bodyConstraintCountsAttr,
      this.bodyConstraintRefsAttr,
      this.config.maxBodies,
      Math.max(1, jointCapacity),
      Math.max(1, springCapacity),
      this.maxConstraintsPerBodySolver,
      restitutionStage.getVelocityScratchAttribute(),
    );
  }

  private initGPU(renderer: any): void {
    if (this.initialized) return;
    const n = this.config.maxBodies;

    try {

    this.positionsAttr = new StorageBufferAttribute(this.positionsData, 4);
    this.initialPoseAttr = new StorageBufferAttribute(this.initialPoseData, 4);
    this.inertialPoseAttr = new StorageBufferAttribute(this.inertialPoseData, 4);
    this.velocitiesAttr = new StorageBufferAttribute(this.velocitiesData, 4);
    this.prevLinearVelAttr = new StorageBufferAttribute(this.prevLinearVelData, 4);
    this.shapesAttr = new StorageBufferAttribute(this.shapesData, 4);
    this.quaternionsAttr = new StorageBufferAttribute(this.quaternionsData, 4);
    this.angularVelAttr = new StorageBufferAttribute(this.angularVelData, 4);
    this.inverseInertiaAttr = new StorageBufferAttribute(this.inverseInertiaData, 4);
    this.conservativeBodyFlagsAttr = new StorageBufferAttribute(
      this.conservativeBodyFlagsData,
      1,
    );
    if (this.sleepingConfigured && this.maxJoints === 0 && this.maxSprings === 0) {
      this.sleepingBodyStateAttr = new StorageBufferAttribute(new Uint32Array(n), 1);
      this.sleepingBodyStateAttr.name = 'Sleeping Island Body State';
    }

    // pairContacts is by far the largest fixed-capacity physics allocation.
    // Materialize and inject its real GPUBuffer before constructing any stage,
    // so every TSL binding sees the same GPU-only attribute from the outset.
    this.pairContactsGpuAllocation = createGpuOnlyStorageAttribute(
      renderer,
      this.device,
      {
        scalarType: 'float32',
        count: this.capacityPlan.derived.contactPoolRowCapacity * CONTACT_RECORD_VEC4S,
        itemSize: 4,
        label: 'AVBD pair contacts (GPU-only)',
      },
    );
    const plannedPairContacts = this.capacityPlan.gpuBuffers.find(
      (buffer) => buffer.key === 'pairContacts',
    );
    if (
      plannedPairContacts === undefined
      || plannedPairContacts.bytes !== this.pairContactsGpuAllocation.gpuByteLength
      || this.capacityPlan.derived.contactPoolRowCapacity !== this.maxPairContacts
    ) {
      this.pairContactsGpuAllocation.destroy();
      throw new Error(
        'PhysicsEngine pairContacts allocation does not match its capacity preflight.',
      );
    }
    this.pairContactsAttr = this.pairContactsGpuAllocation.attribute;
    this.pairContactsData = this.pairContactsAttr.array as Float32Array;
    this.jointRecordsGpuAllocation = createGpuOnlyStorageAttribute(
      renderer,
      this.device,
      {
        scalarType: 'float32',
        count: this.storageMaxJoints * JOINT_RECORD_VEC4S,
        itemSize: 4,
        label: 'AVBD joint records (GPU-only, live capacity)',
      },
    );
    this.springRecordsGpuAllocation = createGpuOnlyStorageAttribute(
      renderer,
      this.device,
      {
        scalarType: 'float32',
        count: this.storageMaxSprings * SPRING_RECORD_VEC4S,
        itemSize: 4,
        label: 'AVBD spring records (GPU-only, live capacity)',
      },
    );
    this.jointRecordsAttr = this.jointRecordsGpuAllocation.attribute;
    this.springRecordsAttr = this.springRecordsGpuAllocation.attribute;
    this.jointRecordGeneration++;
    this.springRecordGeneration++;
    if (this.jointCount > 0) {
      writeFloat32Range(
        this.device.queue,
        this.jointRecordsGpuAllocation.buffer,
        0,
        this.jointRecordsData.subarray(0, this.jointCount * JOINT_RECORD_FLOATS),
      );
    }
    if (this.springCount > 0) {
      writeFloat32Range(
        this.device.queue,
        this.springRecordsGpuAllocation.buffer,
        0,
        this.springRecordsData.subarray(0, this.springCount * SPRING_RECORD_FLOATS),
      );
    }
    this.pairActivityAttr = new StorageBufferAttribute(this.pairActivityData, 1);
    this.pairCandidateIndicesAttr = this.pairActivityAttr;
    this.pairVisitedBitsAttr = new StorageBufferAttribute(this.pairVisitedBitsData, 1);
    this.pairBodyContactCountsAttr = new StorageBufferAttribute(this.pairBodyContactCountsData, 1);
    this.pairBodyContactIndicesAttr = new StorageBufferAttribute(this.pairBodyContactIndicesData, 1);
    this.bodyConstraintCountsAttr = new StorageBufferAttribute(this.bodyConstraintCountsData, 1);
    this.bodyConstraintRefsAttr = this.contactOnlyAdjacencyAlias
      ? this.pairBodyContactIndicesAttr
      : new StorageBufferAttribute(this.bodyConstraintRefsData, 1);
    this.pairActiveCandidateSlotsAttr = this.pairActivityAttr;
    this.pairActiveContactsAttr = this.pairActivityAttr;
    this.ignoredPairHashAttr = new StorageBufferAttribute(this.ignoredPairHashData, 1);
    if (this.contactPoolMode === 'compact') {
      const compactLayout = this.compactContactPoolLayout!;
      this.persistentManifoldMapGpuAllocation = createGpuOnlyStorageAttribute(
        renderer,
        this.device,
        {
          scalarType: 'uint32',
          count: compactLayout.totalWords,
          itemSize: 1,
          label: 'AVBD compact persistent contact map (GPU-only)',
        },
      );
      const plannedPersistentMap = this.capacityPlan.gpuBuffers.find(
        (buffer) => buffer.key === 'persistentManifoldMap',
      );
      if (
        plannedPersistentMap === undefined
        || plannedPersistentMap.bytes
          !== this.persistentManifoldMapGpuAllocation.gpuByteLength
        || compactLayout.totalWords
          !== this.capacityPlan.derived.persistentManifoldMapWordCount
      ) {
        this.persistentManifoldMapGpuAllocation.destroy();
        throw new Error(
          'PhysicsEngine compact persistent-map allocation does not match capacity preflight.',
        );
      }
      this.persistentManifoldMapAttr = this.persistentManifoldMapGpuAllocation.attribute;
      this.persistentManifoldMapData = this.persistentManifoldMapAttr.array as Uint32Array;
    } else {
      this.persistentManifoldMapAttr = new StorageBufferAttribute(
        this.persistentManifoldMapData,
        1,
      );
    }
    this.pairColorBodyClaimsAttr = new StorageBufferAttribute(this.pairColorBodyClaimsData, 1);

    this.integration = new IntegrationStage(
      this.positionsAttr,
      this.initialPoseAttr,
      this.inertialPoseAttr,
      this.velocitiesAttr,
      this.prevLinearVelAttr,
      this.quaternionsAttr,
      this.angularVelAttr,
      this.config.gravity,
      n,
    );

    // Word 0 is the scene-cumulative safety gate. Broadphase keeps its hot
    // candidate append counter in a dedicated allocation and publishes only
    // the cold retained-sleeping prefix into word 1 after traversal.
    this.constraintSafetyGateAttr = new StorageBufferAttribute(new Uint32Array(2), 1);
    this.constraintSafetyGateAttr.name = 'AVBD Constraint Safety Gate';
    this.constraintSafety = new ConstraintSafetyTransactionStage({
      renderer,
      device: this.device,
      safetyGate: this.constraintSafetyGateAttr,
      positions: this.positionsAttr,
      initialPose: this.initialPoseAttr,
      quaternions: this.quaternionsAttr,
      velocities: this.velocitiesAttr,
      prevLinearVelocities: this.prevLinearVelAttr,
      angularVelocities: this.angularVelAttr,
      pairBodyContactCounts: this.pairBodyContactCountsAttr,
      bodyConstraintCounts: this.bodyConstraintCountsAttr,
      maxBodies: n,
    });

    this.contactGeneration = new ContactGenerationStage(
      this.device,
      this.positionsAttr,
      this.quaternionsAttr,
      this.shapesAttr,
      this.pairContactsAttr,
      this.pairActivityAttr,
      this.pairBodyContactCountsAttr,
      this.pairBodyContactIndicesAttr,
      n,
      this.maxPairContacts,
      this.pairManifoldSlots,
      this.maxContactsPerBodySolver,
      this.maxActivePairContacts,
      this.pairCandidateIndicesOffset,
      this.pairActiveCandidateSlotsOffset,
      this.pairActiveContactsOffset,
      this.ignoredPairHashAttr,
      this.ignoredPairHashCapacity,
      this.persistentManifoldMapAttr,
      this.persistentManifoldHashCapacity,
      this.maxPersistentManifolds,
      this.pairActivityWordCount,
      this.initialPoseAttr,
      this.inertialPoseAttr,
      this.config.enableCcd ?? true,
      this.contactPoolMode === 'compact'
        ? {
          persistentMode: 'compact',
          compactContactPoolCapacity: this.maxPairContacts,
          compactContactPoolLayout: this.compactContactPoolLayout,
          submissionLedger: this.submissionLedger,
          enableCcdDiagnostics: this.config.enableCcdDiagnostics === true,
          enableActiveManifoldStream: this.config.avbdManifoldCapture === true,
          constraintSafetyGate: this.constraintSafetyGateAttr,
          fatalBodyDispatch: this.constraintSafety.getFatalBodyDispatchAttribute(),
          safetyReadbackTimeoutMs: this.safetyReadbackTimeoutMs,
        }
        : {
          persistentMode: 'legacy',
          submissionLedger: this.submissionLedger,
          enableCcdDiagnostics: this.config.enableCcdDiagnostics === true,
          constraintSafetyGate: this.constraintSafetyGateAttr,
          fatalBodyDispatch: this.constraintSafety.getFatalBodyDispatchAttribute(),
          safetyReadbackTimeoutMs: this.safetyReadbackTimeoutMs,
        },
    );
    this.contactGeneration.setDebugEnabled(this.debugBehaviorEnabled);
    this.contactGeneration.setDebugLogInterval(this.debugLogEveryNFrames);
    this.contactGeneration.setCollisionMargin(this.collisionMargin);

    this.broadPhase = new BroadPhaseStage(
      this.device,
      this.positionsAttr,
      this.velocitiesAttr,
      this.angularVelAttr,
      this.quaternionsAttr,
      this.shapesAttr,
      this.pairActivityAttr,
      this.pairCandidateIndicesAttr,
      this.pairVisitedBitsAttr,
      this.ignoredPairHashAttr,
      n,
      this.maxCandidatePairs,
      this.maxPairsPerBodyBroadphase,
      this.ignoredPairHashCapacity,
      {
        enableBvhBuild: this.enableBvhBuild,
        buildOnce: this.bvhBuildOnce,
        rebuildIntervalFrames: this.bvhRebuildIntervalFrames,
        waitForGpuCompletion: this.bvhWaitForGpuCompletion,
        // Capacity, not the initial body count, determines whether an emitter
        // or projectile can cross from brute force into LBVH later. Prewarm in
        // that case as well so a runtime append never reaches the cold async
        // build path and submits outside the observed animation-frame fence.
        prewarm: this.requiresCandidatePairPath(n),
        gravity: this.config.gravity,
        submissionLedger: this.submissionLedger,
        enablePairVisitedDedup: this.capacityPlan.allocation.enableBroadphasePairDedup,
        pairEndpointEncoding: this.pairEndpointEncoding,
        safetyGate: this.constraintSafetyGateAttr,
        sleepingBodyState: this.sleepingBodyStateAttr,
        safetyReadbackTimeoutMs: this.safetyReadbackTimeoutMs,
        onConstraintSafetyBits: (bits) => {
          this.contactGeneration.latchConstraintSafetyBits(bits);
        },
      },
    );
    this.broadPhase.setNativeComputePassDescriptorFactory(
      this.nativeComputePassDescriptorFactory,
    );
    this.broadPhase.setDebugEnabled(this.debugBehaviorEnabled);
    this.broadPhase.setDebugLogInterval(this.debugLogEveryNFrames);

    this.avbdState = this.createAvbdStateStage(
      this.jointRecordsAttr,
      this.springRecordsAttr,
      this.storageMaxJoints,
      this.storageMaxSprings,
    );

    if (this.contactRestitution > 0.0 && this.restitutionIterations > 0) {
      this.ensureRestitutionStage(renderer);
    }
    // Sleeping is deliberately contact-only in this first conservative GPU
    // implementation. Scenes whose configured capacity can create joints or
    // springs do not pay for its state buffers or shader graph at all.
    if (this.sleepingConfigured && this.maxJoints === 0 && this.maxSprings === 0) {
      this.sleepingIslands = new SleepingIslandsStage({
        renderer,
        device: this.device,
        positions: this.positionsAttr,
        quaternions: this.quaternionsAttr,
        shapes: this.shapesAttr,
        velocities: this.velocitiesAttr,
        angularVelocities: this.angularVelAttr,
        initialPose: this.initialPoseAttr,
        inertialPose: this.inertialPoseAttr,
        adaptiveSleepState: this.avbdState.getAdaptiveSleepStateAttribute(),
        conservativeBodyFlags: this.conservativeBodyFlagsAttr,
        bodyConstraintCounts: this.bodyConstraintCountsAttr,
        pairContacts: this.pairContactsAttr,
        pairActivity: this.pairActivityAttr,
        pairBodyContactCounts: this.pairBodyContactCountsAttr,
        pairBodyContactIndices: this.pairBodyContactIndicesAttr,
        bodyState: this.sleepingBodyStateAttr,
        maxBodies: n,
        maxPairContacts: this.maxPairContacts,
        maxActivePairContacts: this.maxActivePairContacts,
        maxCachedPairs: this.maxPersistentManifolds,
        maxPairContactsPerBody: this.maxContactsPerBodySolver,
        pairActiveContactsOffset: this.pairActiveContactsOffset,
        pairSolverContactsOffset: this.pairSolverActiveContactsOffset,
        pairSolverContactsCapacity: this.maxCandidatePairs,
        options: {
          gravity: this.config.gravity,
          sleepFrames: this.sleepFrames,
          sleepLinearSpeed: this.sleepLinearSpeed,
          sleepAngularSpeed: this.sleepAngularSpeed,
        },
      });
      this.broadPhase.attachSleepingAabbCertificate(
        () => this.sleepingIslands!.getSparseBroadphaseCertificate(
          this.bodyCount,
          this.sleepTopologyGeneration,
        ),
      );
      this.constraintSafety.attachSleepingState({
        safetyGate: this.constraintSafetyGateAttr,
        ...this.sleepingIslands.getSafetyTransactionAttributes(),
      });
      const sparseAwakeScratchWords = n + Math.ceil(n / 32) + 2;
      if (
        this.contactPoolMode === 'compact'
        && this.compactContactPoolLayout
        && this.maxActivePairContacts >= sparseAwakeScratchWords
      ) {
        this.broadPhase.attachSleepingSparseState({
          bodyState: this.sleepingIslands.getBodyStateAttribute(),
          persistentManifoldMap: this.persistentManifoldMapAttr,
          compactLayout: this.compactContactPoolLayout,
          maxPersistentManifolds: this.maxPersistentManifolds,
          pairManifoldSlots: this.pairManifoldSlots,
          retainedPairScratchOffset: this.pairActiveCandidateSlotsOffset,
          awakeBodyScratchOffset: this.pairActiveContactsOffset,
          awakeBodyScratchCapacity: this.maxActivePairContacts,
          getCertificate: () => this.sleepingIslands!.getSparseBroadphaseCertificate(
            this.bodyCount,
            this.sleepTopologyGeneration,
          ),
        });
      }
      this.broadPhase.setSleepingSparseModeForTesting(
        this.sleepingSparseBroadphaseRequested,
        this.sleepingSparseBroadphaseMinimumBodyCount,
      );
      this.broadPhase.setForceFullSleepingSparseForTesting(
        this.sleepingSparseBroadphaseForceFull,
      );
    }
    // ContactGeneration already stores the official pair material coefficient
    // sqrt(muA * muB) in each manifold. Solver uniforms are therefore unit
    // multipliers; applying the UI/material value again would square friction.
    this.contactGeneration.setFriction(1.0);

    this.playerControl = new PlayerControlStage(
      this.positionsAttr,
      this.velocitiesAttr,
      this.angularVelAttr,
      this.pairBodyContactCountsAttr,
      this.pairBodyContactIndicesAttr,
      this.pairContactsAttr,
      n,
      this.maxPairContacts,
      this.maxContactsPerBodySolver,
    );
      // Publish the initialized state only after every raw allocation and
      // stage has been constructed. A capacity/backend failure must never
      // leave the next step believing a half-built pipeline is usable.
      this.rendererRef = renderer;
      this.initialized = true;
    } catch (error) {
      (this.broadPhase as BroadPhaseStage | undefined)?.dispose?.();
      (this.constraintSafety as ConstraintSafetyTransactionStage | undefined)?.dispose?.();
      this.sleepingIslands?.dispose();
      this.sleepingIslands = undefined;
      this.velocityConstraintProjection?.dispose();
      this.restitutionStage?.dispose();
      this.pairContactsGpuAllocation?.destroy();
      this.jointRecordsGpuAllocation?.destroy();
      this.springRecordsGpuAllocation?.destroy();
      this.persistentManifoldMapGpuAllocation?.destroy();
      this.pairContactsGpuAllocation = undefined;
      this.jointRecordsGpuAllocation = undefined;
      this.springRecordsGpuAllocation = undefined;
      this.persistentManifoldMapGpuAllocation = undefined;
      this.restitutionStage = undefined;
      this.velocityConstraintProjection = undefined;
      this.playerControl = undefined;
      this.rendererRef = undefined;
      this.initialized = false;
      throw error;
    }
  }

  /**
   * Rebuild the complete swept candidate stream after an earlier substep has
   * changed velocities. Queue ordering makes the native AABB/refit pass see
   * the preceding AVBD submission without a CPU readback. Reusing frameId is
   * intentional: quality sampling/rebuild cooldowns remain fixed-step scoped,
   * while the AABB kernel still executes against the latest GPU pose.
   */
  private submitRendererComputeFallback(
    renderer: any,
    commands: readonly ThreeWebGpuComputeCommand[],
    label: string,
    logicalStepId: number,
    substepIndex: number,
  ): void {
    let groupStart = 0;
    while (groupStart < commands.length) {
      const passIndex = commands[groupStart].passIndex;
      let groupEnd = groupStart + 1;
      while (
        groupEnd < commands.length
        && commands[groupEnd].passIndex === passIndex
      ) {
        groupEnd++;
      }

      const group = commands.slice(groupStart, groupEnd);
      const originalCounts = new Map<any, unknown>();
      try {
        // renderer.compute() accepts only one explicit dispatch argument for
        // the whole call. Snapshotting it into each node.count lets a single
        // grouped fallback reproduce both that argument and the per-node
        // count path without merging any original compute-pass boundary.
        for (const command of group) {
          if (!originalCounts.has(command.node)) {
            originalCounts.set(command.node, command.node.count);
          }
          if (command.dispatchSize !== undefined && command.dispatchSize !== null) {
            command.node.count = command.dispatchSize;
          }
        }
        const nodes = group.map((command) => command.node);
        this.submissionLedger.withActualSubmitScope(
          this.device.queue,
          {
            frameId: this.frameId,
            submissionClass: 'critical',
            label,
            fixedStepId: logicalStepId,
            fixedStepSpan: 1,
            substepIndex,
          },
          () => renderer.compute(nodes),
        );
        this.submissionLedger.record('critical', label, {
          frameId: this.frameId,
          fixedStepId: logicalStepId,
          fixedStepSpan: 1,
          substepIndex,
        });
      } finally {
        for (const [node, count] of originalCounts) node.count = count;
      }
      groupStart = groupEnd;
    }
  }

  /**
   * Encode every original renderer compute pass into one native command
   * buffer. The pinned r183 bridge materializes cold pipelines without queue
   * work; the exact renderer fallback remains only for unsupported/private-ABI
   * or deliberately forced diagnostic paths.
   */
  private submitAvbdComputeCommands(
    renderer: any,
    commands: readonly ThreeWebGpuComputeCommand[],
    label: string,
    logicalStepId: number,
    substepIndex: number,
  ): Readonly<{ ok: true } | { ok: false; error: unknown }> {
    this.stats.nativeComputeSourcePassesLast = 0;
    this.stats.nativeComputeEncodedPassesLast = 0;
    this.stats.nativeComputeCommandsLast = 0;
    this.stats.nativeComputeInvariantNodeReusesLast = 0;
    if (commands.length === 0) return { ok: true };
    let prepared: ReturnType<typeof prepareThreeWebGpuCompute>;
    try {
      prepared = prepareThreeWebGpuCompute(
        renderer,
        commands,
        { preparationCache: this.nativeComputePreparationCache },
      );
    } catch (error) {
      // The bridge normally returns an explicit failure, but a future Three
      // private getter could itself throw before its internal rollback guard.
      // Never let that ABI drift escape the render loop.
      return { ok: false, error };
    }
    if (prepared.ok) {
      try {
        const encoder = this.device.createCommandEncoder({
          label: `${label}-encoder`,
        });
        const recordStats = recordPreparedThreeWebGpuCompute(
          encoder,
          prepared,
          label,
          {
            computePassDescriptorFactory: this.nativeComputePassDescriptorFactory,
          },
        );
        this.stats.nativeComputeSourcePassesLast = recordStats.sourcePassCount;
        this.stats.nativeComputeEncodedPassesLast = recordStats.encodedPassCount;
        this.stats.nativeComputeCommandsLast = recordStats.commandCount;
        this.stats.nativeComputeInvariantNodeReusesLast =
          prepared.stats.invariantNodeReuses;
        const commandBuffer = encoder.finish();
        this.submissionLedger.withActualSubmitScope(
          this.device.queue,
          {
            frameId: this.frameId,
            submissionClass: 'critical',
            label,
            fixedStepId: logicalStepId,
            fixedStepSpan: 1,
            substepIndex,
          },
          () => this.device.queue.submit([commandBuffer]),
        );
        this.submissionLedger.record('critical', label, {
          frameId: this.frameId,
          fixedStepId: logicalStepId,
          fixedStepSpan: 1,
          substepIndex,
        });
        return { ok: true };
      } catch (error) {
        // Recording never reached the queue. Do not leave the attempted
        // native graph in the public "last submission" telemetry when the
        // complete renderer fallback below is the path that actually runs.
        this.stats.nativeComputeSourcePassesLast = 0;
        this.stats.nativeComputeEncodedPassesLast = 0;
        this.stats.nativeComputeCommandsLast = 0;
        this.stats.nativeComputeInvariantNodeReusesLast = 0;
        // The abandoned native recording did not execute. Undo Three's call
        // counters before replaying the complete pass-faithful fallback.
        try {
          prepared.rollbackAccounting();
        } catch (rollbackError) {
          return {
            ok: false,
            error: new Error(
              `Native AVBD command recording and accounting rollback both failed: ${String(rollbackError)}`,
              { cause: error },
            ),
          };
        }
      }
    }
    try {
      this.submitRendererComputeFallback(
        renderer,
        commands,
        label,
        logicalStepId,
        substepIndex,
      );
      return { ok: true };
    } catch (error) {
      // A fallback call can fail after one of its earlier pass groups already
      // submitted. The fixed step is therefore uncertifiable: contain the
      // synchronous exception and let the caller latch the same correctness
      // halt used for a failed current-state broadphase refresh.
      return { ok: false, error };
    }
  }

  private getContactSafetyFailureReason(): ContactSafetyFailureReason | null {
    return classifyContactSafetyFailure({
      contactCapacityOverflow: this.contactGeneration.hasCapacityOverflow(),
      ccdFatal: this.contactGeneration.hasCcdFatal(),
      standaloneSampleFailed:
        this.contactGeneration.hasStandaloneSafetyReadbackInfrastructureFailure(),
    });
  }

  private failClosedAvbdComputeSubmission(
    logicalStepId: number,
    substepIndex: number,
    error: unknown,
    failureReason: SubstepBroadphaseRefreshFailureReason = 'dispatch-exception',
  ): void {
    this.substepBroadphaseRefreshLastFrameId = this.frameId;
    this.substepBroadphaseRefreshLastFixedStepId = logicalStepId;
    this.substepBroadphaseRefreshLastSubstepIndex = substepIndex;
    this.substepBroadphaseRefreshLastFailureReason = failureReason;
    this.substepBroadphaseRefreshStickyFailure = true;
    this.stats.candidatePairsEnabled = false;
    this.stats.pairDispatchTruncated = true;
    this.accumulator = 0.0;
    if (!this.pairDispatchTruncationWarned) {
      console.error(
        `AVBD compute submission failed closed at fixedStep=${logicalStepId} `
        + `substep=${substepIndex}. Physics is paused until reset.`,
        error,
      );
      this.pairDispatchTruncationWarned = true;
    }
  }

  private refreshBroadphaseForSubstep(
    renderer: any,
    pairCount: number,
    subDt: number,
    logicalStepId: number,
    substepIndex: number,
    commands: readonly ThreeWebGpuComputeCommand[],
  ): boolean {
    this.substepBroadphaseRefreshAttempts++;
    this.substepBroadphaseRefreshAttemptsThisFrame++;
    this.substepBroadphaseRefreshLastFrameId = this.frameId;
    this.substepBroadphaseRefreshLastFixedStepId = logicalStepId;
    this.substepBroadphaseRefreshLastSubstepIndex = substepIndex;

    const refreshStart = performance.now();
    let dispatchError: unknown = null;
    const computeTail: BroadphaseComputeTail = {
      commands,
      combinedSubmissionLabel: 'broadphase-avbd-substep-fused',
      substepIndex,
      fused: false,
      broadphaseSubmitted: false,
      fallbackReason: null,
    };
    try {
      this.broadPhase.dispatch(
        renderer,
        this.bodyCount,
        pairCount,
        this.frameId,
        subDt,
        logicalStepId,
        1,
        computeTail,
      );
    } catch (error) {
      dispatchError = error;
    }
    this.stats.broadPhaseMs += Math.max(0.0, performance.now() - refreshStart);

    const candidateOverflow = this.broadPhase.hasCandidateOverflow();
    const contactSafetyFailureReason = this.getContactSafetyFailureReason();
    const broadphaseReady = this.broadPhase.isReady();
    const candidatePairsAvailable = this.broadPhase.hasCandidatePairs();
    let failureReason: SubstepBroadphaseRefreshFailureReason | null = null;
    if (dispatchError !== null) {
      failureReason = 'dispatch-exception';
    } else if (contactSafetyFailureReason !== null) {
      failureReason = contactSafetyFailureReason;
    } else if (candidateOverflow) {
      failureReason = this.broadPhase.hasFatalSampleInfrastructureFailure()
        ? 'broadphase-safety-sample-failed'
        : 'candidate-overflow';
    } else if (!broadphaseReady) {
      failureReason = 'broadphase-not-ready';
    } else if (!candidatePairsAvailable || !computeTail.broadphaseSubmitted) {
      failureReason = 'candidate-stream-unavailable';
    }

    this.stats.broadPhaseReady = broadphaseReady;
    this.stats.candidatePairsEnabled = candidatePairsAvailable
      && !candidateOverflow
      && contactSafetyFailureReason === null;
    if (failureReason === null) {
      if (!computeTail.fused && commands.length > 0) {
        try {
          this.submitRendererComputeFallback(
            renderer,
            commands,
            'physics-avbd-substep-native-bridge-fallback',
            logicalStepId,
            substepIndex,
          );
        } catch (error) {
          dispatchError = error;
          failureReason = 'dispatch-exception';
        }
      }
    }
    if (failureReason === null) {
      if (computeTail.fused) {
        // BroadPhaseStage appends its shared safety-gate copy after the solver
        // tail. The same mapped ring therefore covers broadphase, contact, CCD,
        // compact-allocation and rollback failures without a second submit.
        this.postContactSafetySampleOwnerFrameId = this.frameId;
      }
      this.substepBroadphaseRefreshSuccesses++;
      return true;
    }

    // This is stronger than the optional initial broadphase overflow policy:
    // once any part of a fixed step has executed, continuing with the old
    // stream would silently omit impulse-created pairs. Keep the failure
    // latched until an explicit scene/physics reset and never report this
    // partially advanced state as a completed fixed step.
    this.substepBroadphaseRefreshFailures++;
    this.substepBroadphaseRefreshFailuresThisFrame++;
    this.substepBroadphaseRefreshLastFailureReason = failureReason;
    this.substepBroadphaseRefreshStickyFailure = true;
    this.stats.candidatePairsEnabled = false;
    this.stats.pairDispatchTruncated = true;
    this.accumulator = 0.0;
    if (!this.pairDispatchTruncationWarned) {
      console.error(
        `Substep broadphase refresh failed closed at fixedStep=${logicalStepId} ` +
        `substep=${substepIndex} reason=${failureReason}. Physics is paused until reset.`,
        ...(dispatchError === null ? [] : [dispatchError]),
      );
      this.pairDispatchTruncationWarned = true;
    }
    return false;
  }

  getStartupPrewarmState(): Readonly<{
    state: 'idle' | 'collecting' | 'compiling' | 'ready' | 'failed';
    error: string | null;
  }> {
    return Object.freeze({
      state: this.startupPrewarmState,
      error: this.startupPrewarmError,
    });
  }

  /**
   * Build the exact configured substep graph once without encoding or
   * submitting it, then asynchronously compile every unique Three compute
   * pipeline. Production calls this only after the render pipelines are warm;
   * regression pages that do not opt in keep the established first-step path.
   */
  prewarmStartupPipelines(
    renderer: any,
    onProgress?: (completedNodes: number, totalNodes: number) => void,
  ): Promise<PhysicsStartupPrewarmResult> {
    if (this.startupPrewarmPromise) return this.startupPrewarmPromise;
    if (this.startupPrewarmState === 'ready') {
      return Promise.resolve(Object.freeze({
        ok: true,
        state: 'ready',
        commandCount: 0,
        uniqueNodeCount: 0,
      }));
    }
    if (this.startupPrewarmState === 'failed') {
      return Promise.resolve(Object.freeze({
        ok: false,
        state: 'failed',
        commandCount: 0,
        uniqueNodeCount: 0,
        reason: this.startupPrewarmError ?? 'startup prewarm previously failed',
      }));
    }

    if (this.disposed) {
      return Promise.resolve(Object.freeze({
        ok: false,
        state: 'failed',
        commandCount: 0,
        uniqueNodeCount: 0,
        reason: 'startup prewarm cancelled because PhysicsEngine is disposed',
      }));
    }

    const prewarmGeneration = ++this.startupPrewarmGeneration;
    const isCurrentPrewarm = (): boolean => (
      !this.disposed && prewarmGeneration === this.startupPrewarmGeneration
    );
    const cancelledResult = (): PhysicsStartupPrewarmResult => Object.freeze({
      ok: false,
      state: 'failed',
      commandCount: 0,
      uniqueNodeCount: 0,
      reason: 'startup prewarm cancelled because PhysicsEngine was disposed',
    });

    this.startupPrewarmPromise = (async (): Promise<PhysicsStartupPrewarmResult> => {
      // Let the status overlay and already-warmed presentation path paint
      // before constructing the configured TSL compute graph.
      await new Promise<void>((resolve) => {
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => resolve());
        } else {
          setTimeout(resolve, 0);
        }
      });
      if (!isCurrentPrewarm()) return cancelledResult();

      try {
        this.startupPrewarmState = 'collecting';
        this.initGPU(renderer);
        if (!isCurrentPrewarm()) return cancelledResult();

        // initGPU normally runs inside the first observed step. Prewarming
        // moves it earlier, so publish the same initial CPU mirrors explicitly
        // before Three materializes their backend bindings.
        this.positionsAttr.needsUpdate = true;
        this.initialPoseAttr.needsUpdate = true;
        this.inertialPoseAttr.needsUpdate = true;
        this.velocitiesAttr.needsUpdate = true;
        this.prevLinearVelAttr.needsUpdate = true;
        this.shapesAttr.needsUpdate = true;
        this.quaternionsAttr.needsUpdate = true;
        this.angularVelAttr.needsUpdate = true;
        this.inverseInertiaAttr.needsUpdate = true;
        this.pairActivityAttr.needsUpdate = true;
        this.pairCandidateIndicesAttr.needsUpdate = true;
        this.pairVisitedBitsAttr.needsUpdate = true;
        this.pairBodyContactCountsAttr.needsUpdate = true;
        this.pairBodyContactIndicesAttr.needsUpdate = true;
        this.bodyConstraintCountsAttr.needsUpdate = true;
        if (!this.contactOnlyAdjacencyAlias) this.bodyConstraintRefsAttr.needsUpdate = true;
        this.pairActiveCandidateSlotsAttr.needsUpdate = true;
        this.pairActiveContactsAttr.needsUpdate = true;
        this.ignoredPairHashAttr.needsUpdate = true;
        if (this.contactPoolMode === 'legacy') {
          this.persistentManifoldMapAttr.needsUpdate = true;
        }
        this.pairColorBodyClaimsAttr.needsUpdate = true;

        const pairCount = (this.bodyCount * (this.bodyCount - 1)) / 2;
        const pairGenerationDispatchCount = Math.min(pairCount, this.maxCandidatePairs);
        const pairWarmStartDispatchCount = Math.min(
          pairCount * this.pairManifoldSlots,
          this.maxActivePairContacts,
        );
        this.startupPrewarmCommands = [];
        this.substep(
          this.config.deltaTime / this.config.substeps,
          renderer,
          pairCount,
          pairGenerationDispatchCount,
          pairWarmStartDispatchCount,
          this.requiresCandidatePairPath(),
          0,
          0,
          true,
        );
        const commands = this.startupPrewarmCommands;
        this.startupPrewarmCommands = null;
        if (!isCurrentPrewarm()) return cancelledResult();

        // Collection mutates only graph uniforms and small CPU scheduling
        // latches. Restore their initial semantics without submitting a fake
        // simulation step or resetting the physical body arrays.
        this.contactGeneration.reset();
        this.avbdState.resetAdaptiveIterationStats();
        this.sleepingIslands?.requestReset();
        this.invalidateBodyColoring();
        this.resetBodyColoringScheduleTelemetry();
        this.stats.integrationMs = 0;
        this.stats.solverMs = 0;
        this.stats.velocityUpdateMs = 0;

        if (!commands || commands.length === 0) {
          throw new Error('startup graph collection produced no compute commands');
        }
        let extraPassIndex = commands.reduce(
          (maximum, command) => Math.max(maximum, command.passIndex),
          -1,
        ) + 1;
        for (const node of this.broadPhase.getStartupComputeNodes()) {
          commands.push({
            node,
            passIndex: extraPassIndex++,
            dispatchSize: 1,
            uniformStateInvariant: true,
          });
        }
        const uniqueNodeCount = new Set(commands.map((command) => command.node)).size;
        if (isCurrentPrewarm()) onProgress?.(0, uniqueNodeCount);
        this.startupPrewarmState = 'compiling';
        const prewarm = await prewarmThreeWebGpuCompute(renderer, commands, {
          // Eight concurrent native compilations reduced cold startup by an
          // order of magnitude on the production graph while retaining a
          // browser frame between batches for input/status responsiveness.
          yieldEveryNodes: 8,
          onProgress: (completedNodes, totalNodes) => {
            if (isCurrentPrewarm()) onProgress?.(completedNodes, totalNodes);
          },
          yieldToMainThread: async () => {
            await new Promise<void>((resolve) => {
              if (typeof requestAnimationFrame === 'function') {
                requestAnimationFrame(() => resolve());
              } else {
                setTimeout(resolve, 0);
              }
            });
          },
        });
        if (!isCurrentPrewarm()) return cancelledResult();
        if (!prewarm.ok) throw new Error(prewarm.reason);
        // Raw candidate/LBVH/sparse pipelines start asynchronously during
        // initGPU. Do not publish readiness until those non-Three pipelines
        // have also finished, otherwise the first large-scene step would only
        // move the old freeze from shader compilation to broadphase startup.
        await this.broadPhase.waitForStartupPrewarm();
        if (!isCurrentPrewarm()) return cancelledResult();
        onProgress?.(uniqueNodeCount, uniqueNodeCount);
        this.startupPrewarmState = 'ready';
        this.startupPrewarmError = null;
        return Object.freeze({
          ok: true,
          state: 'ready',
          commandCount: commands.length,
          uniqueNodeCount,
          stats: prewarm.stats,
        });
      } catch (error) {
        if (!isCurrentPrewarm()) return cancelledResult();
        const reason = error instanceof Error ? error.message : String(error);
        this.startupPrewarmCommands = null;
        this.startupPrewarmState = 'failed';
        this.startupPrewarmError = reason;
        return Object.freeze({
          ok: false,
          state: 'failed',
          commandCount: 0,
          uniqueNodeCount: 0,
          reason,
        });
      }
    })().finally(() => {
      if (prewarmGeneration === this.startupPrewarmGeneration) {
        this.startupPrewarmPromise = null;
      }
    });
    return this.startupPrewarmPromise;
  }

  step(realDt: number, renderer: any): void {
    if (this.bodyCount === 0) return;
    // A pending async prewarm has already inserted Three pipeline wrappers in
    // its cache. Entering the synchronous compute path before their native
    // GPU pipelines resolve would be both a correctness error and a cold-start
    // stall. A failed prewarm stays fail-closed until the engine is rebuilt.
    if (
      this.startupPrewarmState === 'collecting'
      || this.startupPrewarmState === 'compiling'
      || this.startupPrewarmState === 'failed'
    ) {
      return;
    }
    const observedFrameId = this.frameId + 1;
    // Count real GPUQueue.submit calls made synchronously by this physics
    // step. Nested scopes below provide semantic attribution; this outer
    // catch-all makes any newly introduced submit visible as unattributed
    // instead of letting the intent ledger silently miss it.
    this.submissionLedger.withActualSubmitScope(
      this.device.queue,
      { frameId: observedFrameId },
      () => this.stepWithObservedSubmits(realDt, renderer),
    );
  }

  private stepWithObservedSubmits(realDt: number, renderer: any): void {
    if (this.bodyCount === 0) return;
    this.frameId++;
    this.stats.frameCount = this.frameId;
    this.submissionLedger.beginFrame(this.frameId);
    this.substepBroadphaseRefreshAttemptsThisFrame = 0;
    this.substepBroadphaseRefreshFailuresThisFrame = 0;
    this.postContactSafetySampleOwnerFrameId = -1;

    const firstFrame = !this.initialized;
    this.initGPU(renderer);

    if (firstFrame) {
      this.positionsAttr.needsUpdate = true;
      this.initialPoseAttr.needsUpdate = true;
      this.inertialPoseAttr.needsUpdate = true;
      this.velocitiesAttr.needsUpdate = true;
      this.prevLinearVelAttr.needsUpdate = true;
      this.shapesAttr.needsUpdate = true;
      this.quaternionsAttr.needsUpdate = true;
      this.angularVelAttr.needsUpdate = true;
      this.inverseInertiaAttr.needsUpdate = true;

      this.pairActivityAttr.needsUpdate = true;
      this.pairCandidateIndicesAttr.needsUpdate = true;
      this.pairVisitedBitsAttr.needsUpdate = true;
      this.pairBodyContactCountsAttr.needsUpdate = true;
      this.pairBodyContactIndicesAttr.needsUpdate = true;
      this.bodyConstraintCountsAttr.needsUpdate = true;
      if (!this.contactOnlyAdjacencyAlias) this.bodyConstraintRefsAttr.needsUpdate = true;
      this.pairActiveCandidateSlotsAttr.needsUpdate = true;
      this.pairActiveContactsAttr.needsUpdate = true;
      this.ignoredPairHashAttr.needsUpdate = true;
      if (this.contactPoolMode === 'legacy') {
        this.persistentManifoldMapAttr.needsUpdate = true;
      }
      this.pairColorBodyClaimsAttr.needsUpdate = true;
    }

    // Direct config mutation is retained for backwards compatibility, but it
    // may not silently preserve an all-sleeping certificate. Compare exact
    // f32 gravity every frame and wake before consulting the zero-work path.
    this.synchronizeGravityMutation();

    // GPU map callbacks only publish sticky maintenance flags. Consume a
    // fatal-topology rebuild request here, while the outer queue observer and
    // this frame's ledger are active, before the certified halt returns.
    if (this.broadPhase.servicePendingMaintenance(this.frameId)) {
      this.accumulator = Math.min(
        this.accumulator + Math.min(realDt, 0.05),
        this.config.deltaTime,
      );
      this.submissionLedger.setFrameWorkload(
        this.frameId,
        0,
        this.config.substeps,
        false,
      );
      this.stats.integrationMs = 0.0;
      this.stats.broadPhaseMs = this.broadPhase.getLastBuildMs();
      this.stats.solverMs = 0.0;
      this.stats.velocityUpdateMs = 0.0;
      this.stats.totalMs = 0.0;
      this.stats.bodyCount = this.bodyCount;
      return;
    }

    // The per-dispatch fatal word is copied in the existing broadphase submit
    // and mapped asynchronously. Check the callback-latched result before
    // starting another fixed step, so a confirmed incomplete candidate stream
    // cannot be followed by more physics work. Pending samples deliberately do
    // not form a per-frame CPU/GPU fence; ring exhaustion itself fails closed.
    const broadphaseFatalAtFrameStart = this.broadPhase.hasCandidateOverflow();
    const contactSafetyFailureAtFrameStart = this.getContactSafetyFailureReason();
    if (broadphaseFatalAtFrameStart || contactSafetyFailureAtFrameStart !== null) {
      const safetySampleFailed = this.broadPhase.hasFatalSampleInfrastructureFailure();
      this.substepBroadphaseRefreshLastFrameId = this.frameId;
      this.substepBroadphaseRefreshLastFixedStepId = this.stats.simulationStepCount;
      this.substepBroadphaseRefreshLastSubstepIndex = 0;
      this.substepBroadphaseRefreshLastFailureReason =
        contactSafetyFailureAtFrameStart
        ?? (safetySampleFailed
          ? 'broadphase-safety-sample-failed'
          : 'candidate-overflow');
      this.substepBroadphaseRefreshStickyFailure = true;
      this.stats.candidatePairsEnabled = false;
      this.stats.pairDispatchTruncated = true;
      if (!this.pairDispatchTruncationWarned) {
        console.error(
          contactSafetyFailureAtFrameStart === 'contact-capacity-overflow'
            ? 'Contact/compact capacity overflowed. Physics is paused until reset.'
            : contactSafetyFailureAtFrameStart === 'ccd-fatal'
              ? 'CCD reported an unresolved fatal state. Physics is paused until reset.'
              : contactSafetyFailureAtFrameStart === 'contact-safety-sample-failed'
                ? 'Contact safety sample failed. Physics is paused until reset.'
                : safetySampleFailed
              ? 'Broadphase safety sample failed. Physics is paused until reset.'
              : 'Broadphase candidate dispatch was incomplete. Physics is paused until reset.',
        );
        this.pairDispatchTruncationWarned = true;
      }
    }

    // Contact safety callbacks only publish a pending CPU state. Service at
    // most one detail-map phase in this fully observed, zero-core maintenance
    // tick. This sits after fatal classification (so diagnostics report the
    // correct sticky cause) but before the halt return below, allowing detail
    // maps to progress without ever running another physics transaction.
    if (this.contactGeneration.hasPendingDetailReadback()) {
      this.submissionLedger.setFrameWorkload(
        this.frameId,
        0,
        this.config.substeps,
        false,
      );
      if (this.contactGeneration.servicePendingDetailReadback(renderer, this.frameId)) {
        this.accumulator = Math.min(
          this.accumulator + Math.min(realDt, 0.05),
          this.config.deltaTime,
        );
        this.stats.integrationMs = 0.0;
        this.stats.broadPhaseMs = 0.0;
        this.stats.solverMs = 0.0;
        this.stats.velocityUpdateMs = 0.0;
        this.stats.totalMs = 0.0;
        this.stats.bodyCount = this.bodyCount;
        return;
      }
    }

    // Capacity loss is a correctness failure, not a reason to keep paying for
    // another broadphase dispatch every animation tick. Preserve the explicit
    // halted state until reset while keeping frame/UI telemetry responsive.
    if (
      this.substepBroadphaseRefreshStickyFailure
      || (this.stats.pairDispatchTruncated && this.haltOnBroadphaseFallbackOverflow)
    ) {
      this.accumulator = 0.0;
      this.stats.totalMs = 0.0;
      this.stats.broadPhaseMs = 0.0;
      this.stats.bodyCount = this.bodyCount;
      return;
    }

    this.accumulator += Math.min(realDt, 0.05);
    const start = performance.now();

    if (
      this.pendingSleepingCertificationTelemetry !== null
      || this.sleepingTelemetryMaintenancePending
      || this.adaptiveTelemetryMaintenancePending
    ) {
      this.submissionLedger.setFrameWorkload(
        this.frameId,
        0,
        this.config.substeps,
        false,
      );
      if (this.servicePendingDiagnosticMaintenance(renderer)) {
        // A maintenance tick owns at most three optional maps and no solver
        // work. Retain at most one fixed quantum so this bounded cadence never
        // turns into a catch-up spiral on the following animation frame.
        this.accumulator = Math.min(this.accumulator, this.config.deltaTime);
        this.stats.integrationMs = 0.0;
        this.stats.broadPhaseMs = 0.0;
        this.stats.solverMs = 0.0;
        this.stats.velocityUpdateMs = 0.0;
        this.stats.totalMs = performance.now() - start;
        this.stats.bodyCount = this.bodyCount;
        return;
      }
    }

    const stepsAvailable = Math.floor(this.accumulator / this.config.deltaTime);
    // A broadphase candidate stream is valid for one collision substep.
    // Contact impulses can reverse a body's motion, so later substeps refresh
    // below and later catch-up fixed steps cannot share a pre-solve stream at
    // all. Run at most one fixed step per animation frame; backlog is clamped
    // rather than advancing with a stale constraint graph.
    const stepsToRun = Math.min(stepsAvailable, this.maxFixedStepsPerFrame, 1);

    let advancedSimulation = false;
    const sleepingFrameParams = {
      bodyCount: this.bodyCount,
      dynamicBodyCount: this.dynamicBodyCount,
      frameId: this.frameId,
      jointCount: this.jointCount,
      springCount: this.springCount,
      restitutionActive: this.contactRestitution > 0.0 && this.restitutionIterations > 0,
      topologyGeneration: this.sleepTopologyGeneration,
      gravity: this.config.gravity,
    };
    if (
      stepsToRun > 0
      && this.sleepingIslands?.canSkipAllPhysics(sleepingFrameParams)
    ) {
      // A queue-ordered GPU map has already proved that every dynamic body was
      // sleeping before and after an exact cached-root step. With no mutation,
      // topology change or incompatible stage, the next fixed step is the
      // identity map. Advance logical time without AABB/LBVH/contact/AVBD work.
      this.accumulator -= this.config.deltaTime;
      if (stepsAvailable > stepsToRun) {
        this.accumulator = Math.min(this.accumulator, this.config.deltaTime);
      }
      this.stats.simulationStepCount++;
      this.sleepingIslands.recordZeroWorkFixedStep();
      this.stats.sleepingZeroWorkSteps++;
      this.submissionLedger.setFrameWorkload(
        this.frameId,
        1,
        this.config.substeps,
        true,
      );
      this.hasAdvancedSimulation = true;
      this.stats.integrationMs = 0.0;
      this.stats.broadPhaseMs = 0.0;
      this.stats.solverMs = 0.0;
      this.stats.velocityUpdateMs = 0.0;
      this.stats.avbdIterationsLast = 0;
      this.stats.totalMs = performance.now() - start;
      this.stats.bodyCount = this.bodyCount;
      this.servicePendingSleepingCertification(renderer);
      return;
    }
    if (stepsToRun > 0) {
      const pairCount = (this.bodyCount * (this.bodyCount - 1)) / 2;
      let pairGenerationDispatchCount = Math.min(pairCount, this.maxCandidatePairs);
      // Contact solve/warmstart run over a compact active-contact list. The
      // list is bounded by the per-body contact list capacity.
      const pairWarmStartDispatchCount = Math.min(
        pairCount * this.pairManifoldSlots,
        this.maxActivePairContacts,
      );

      const subDt = this.config.deltaTime / this.config.substeps;
      const requiresCandidatePairs = this.requiresCandidatePairPath();
      const nativeCollectorPolicy = getAvbdNativeCommandCollectorPolicy(
        this.avbdBodySolveMode,
        this.avbdPairSweeps,
        {
          restitutionActive: this.contactRestitution > 0.0 && this.restitutionIterations > 0,
          jointCount: this.jointCount,
          springCount: this.springCount,
        },
      );
      let candidatePairsAvailable = false;
      let broadPhaseReady = true;
      let candidateOverflowDetected = false;
      let fuseInitialBroadphaseWithSolve = false;
      if (requiresCandidatePairs) {
        fuseInitialBroadphaseWithSolve = nativeCollectorPolicy.eligible
          && this.broadPhase.canFuseSolverTailForBodyCount(this.bodyCount);
        if (fuseInitialBroadphaseWithSolve) {
          // The command collector does not read candidate data on the CPU.
          // Defer the exact current-state AABB/refit/candidate graph until the
          // solver tail is available, then record both in queue order into one
          // command buffer. The previously published stream is used only as a
          // CPU-side readiness certificate and is never consumed by this step.
          candidatePairsAvailable = true;
          broadPhaseReady = true;
          candidateOverflowDetected = false;
          this.stats.broadPhaseMs = 0.0;
        } else {
          const t0 = performance.now();
          let initialBroadphaseError: unknown = null;
          try {
            this.submissionLedger.withActualSubmitScope(
              this.device.queue,
              {
                frameId: this.frameId,
                submissionClass: 'critical',
                label: 'broadphase-substep-0',
                fixedStepId: this.stats.simulationStepCount,
                fixedStepSpan: 1,
                substepIndex: 0,
              },
              () => this.broadPhase.dispatch(
                renderer,
                this.bodyCount,
                pairCount,
                this.frameId,
                // This stream belongs only to substep 0. Later substeps refresh it
                // after the preceding solve because AVBD/restitution impulses can
                // create pairs that no pre-solve velocity sweep could predict.
                subDt,
                this.stats.simulationStepCount,
                1,
              ),
            );
          } catch (error) {
            initialBroadphaseError = error;
          }
          this.stats.broadPhaseMs = Math.max(
            performance.now() - t0,
            this.broadPhase.getLastBuildMs(),
          );
          if (initialBroadphaseError !== null) {
            // A synchronous encoder/submit failure may occur after only part of
            // the broadphase command graph was prepared. Continuing would use a
            // stale or absent candidate stream, so fail closed exactly like a
            // later substep refresh and leave the UI/render loop responsive.
            this.substepBroadphaseRefreshLastFrameId = this.frameId;
            this.substepBroadphaseRefreshLastFixedStepId = this.stats.simulationStepCount;
            this.substepBroadphaseRefreshLastSubstepIndex = 0;
            this.substepBroadphaseRefreshLastFailureReason = 'dispatch-exception';
            this.substepBroadphaseRefreshStickyFailure = true;
            this.stats.broadPhaseReady = false;
            this.stats.candidatePairsEnabled = false;
            this.stats.pairDispatchTruncated = true;
            this.accumulator = 0.0;
            this.stats.totalMs = performance.now() - start;
            this.stats.bodyCount = this.bodyCount;
            this.stats.frameCount = this.frameId;
            if (!this.pairDispatchTruncationWarned) {
              console.error(
                `Initial broadphase dispatch failed closed at fixedStep=${this.stats.simulationStepCount}. `
                + 'Physics is paused until reset.',
                initialBroadphaseError,
              );
              this.pairDispatchTruncationWarned = true;
            }
            return;
          }
          candidatePairsAvailable = this.broadPhase.hasCandidatePairs();
          broadPhaseReady = this.broadPhase.isReady();
          candidateOverflowDetected = this.broadPhase.hasCandidateOverflow();
        }
      } else {
        // Below the globally safe brute-force threshold, the exact pair range
        // is cheaper and needs no AABB update, LBVH refit, candidate submit, or
        // periodic rebuild. This removes three wasted submissions per true
        // step from the default scenes.
        this.stats.broadPhaseMs = 0.0;
      }
      const contactSafetyFailureDetected = this.getContactSafetyFailureReason();
      const useCandidatePairs = candidatePairsAvailable && requiresCandidatePairs;
      // A BVH that is still being built is a deliberate no-advance state, not
      // evidence that constraints were dropped.  Keep the sticky correctness
      // latch reserved for actual capacity loss, or for a ready broadphase
      // that still cannot supply the required sparse candidate stream.
      const readyWithoutSafeCandidates = requiresCandidatePairs
        && broadPhaseReady
        && !candidatePairsAvailable;
      const pairDispatchTruncated = candidateOverflowDetected
        || contactSafetyFailureDetected !== null
        || readyWithoutSafeCandidates;
      if (this.waitingForInitialCandidatePairs && requiresCandidatePairs) {
        if (!candidatePairsAvailable) {
          this.stats.broadPhaseReady = broadPhaseReady;
          this.stats.candidatePairsEnabled = useCandidatePairs;
          this.stats.pairDispatchTruncated = pairDispatchTruncated;
          this.accumulator = 0;
          this.stats.totalMs = performance.now() - start;
          this.stats.bodyCount = this.bodyCount;
          this.stats.frameCount = this.frameId;
          return;
        }
        this.waitingForInitialCandidatePairs = false;
      } else if (!requiresCandidatePairs) {
        this.waitingForInitialCandidatePairs = false;
      }

      // Runtime body growth can deliberately suspend the compact candidate
      // pass while a large (> exact-tail limit) replacement tree is built.
      // This is the same no-advance state as the initial build, not permission
      // to fall back to a bounded brute-force prefix for a large scene. Doing
      // so would advance with an incomplete constraint graph. Keep the
      // capacity latch clear, discard catch-up time, and retry next frame.
      const waitingForBroadphase = requiresCandidatePairs
        && !candidatePairsAvailable
        && !broadPhaseReady;
      if (waitingForBroadphase) {
        this.stats.broadPhaseReady = false;
        this.stats.candidatePairsEnabled = false;
        this.stats.pairDispatchTruncated = false;
        this.accumulator = 0;
        this.stats.totalMs = performance.now() - start;
        this.stats.bodyCount = this.bodyCount;
        this.stats.frameCount = this.frameId;
        return;
      }

      if (pairDispatchTruncated && !this.pairDispatchTruncationWarned) {
        if (candidateOverflowDetected) {
          console.error(
            this.broadPhase.hasFatalSampleInfrastructureFailure()
              ? 'Broadphase safety sample failed. Pausing physics instead of solving an uncertified constraint graph.'
              : 'Broadphase traversal/candidate capacity overflowed. Pausing physics instead of silently dropping contacts.',
          );
        } else if (contactSafetyFailureDetected !== null) {
          const contactSafetyMessage = contactSafetyFailureDetected === 'contact-capacity-overflow'
            ? 'Contact/body-list capacity overflowed.'
            : contactSafetyFailureDetected === 'ccd-fatal'
              ? 'CCD reported an unresolved fatal state.'
              : 'Contact safety sample infrastructure failed.';
          console.error(
            `${contactSafetyMessage} Pausing physics instead of solving an uncertified constraint graph.`,
          );
        } else if (waitingForBroadphase) {
          console.warn(
            `Broadphase BVH is still building at bodyCount=${this.bodyCount}. ` +
            `Pausing physics until candidate pairs become available (brute-force safe limit=${this.bruteForceMaxBodies}).`,
          );
        } else {
          console.error(
            `Broadphase candidate pairs are disabled and bodyCount (${this.bodyCount}) exceeds brute-force safe limit (${this.bruteForceMaxBodies}). ` +
            'Pausing physics to avoid incomplete collisions.',
          );
        }
        this.pairDispatchTruncationWarned = true;
      } else if (!pairDispatchTruncated) {
        this.pairDispatchTruncationWarned = false;
      }
      this.stats.broadPhaseReady = broadPhaseReady;
      this.stats.candidatePairsEnabled = useCandidatePairs;
      this.stats.pairDispatchTruncated = pairDispatchTruncated;
      if (pairDispatchTruncated && this.haltOnBroadphaseFallbackOverflow) {
        this.accumulator = 0;
        this.stats.totalMs = performance.now() - start;
        this.stats.bodyCount = this.bodyCount;
        this.stats.frameCount = this.frameId;
        return;
      }

      if (useCandidatePairs) {
        // Contact generation launches the compact stream's capacity bound;
        // its first kernel gates each invocation by the GPU-written count
        // header, so stale tail slots never participate.
        const candidateDispatchSpan = Math.min(
          this.maxCandidatePairs,
          this.bodyCount * this.maxPairsPerBodyBroadphase,
        );
        pairGenerationDispatchCount = candidateDispatchSpan;
      }

      // Publish completion, not merely intent. A later substep broadphase can
      // fail closed after earlier substeps have submitted; that partial state
      // must never be certified as one completed fixed step.
      this.submissionLedger.setFrameWorkload(
        this.frameId,
        0,
        this.config.substeps,
        false,
      );
      const firstLogicalStepId = this.stats.simulationStepCount;
      let completedFixedSteps = 0;
      let fixedStepCompleted = true;
      for (let stepIndex = 0; stepIndex < stepsToRun; stepIndex++) {
        const logicalStepId = firstLogicalStepId + stepIndex;
        for (let s = 0; s < this.config.substeps; s++) {
          const substepCompleted = this.substep(
            subDt,
            renderer,
            pairCount,
            pairGenerationDispatchCount,
            pairWarmStartDispatchCount,
            useCandidatePairs,
            logicalStepId,
            s,
            false,
            s === 0 && fuseInitialBroadphaseWithSolve,
          );
          if (!substepCompleted) {
            fixedStepCompleted = false;
            break;
          }
        }
        if (!fixedStepCompleted) break;
        this.accumulator -= this.config.deltaTime;
        this.stats.simulationStepCount++;
        completedFixedSteps++;
        advancedSimulation = true;
      }
      this.submissionLedger.setFrameWorkload(
        this.frameId,
        completedFixedSteps,
        this.config.substeps,
        completedFixedSteps > 0,
      );
      if (completedFixedSteps > 0) {
        // Optional maps are scheduled only after the complete fixed-step
        // command graph is known. In particular, a four-substep sparse world
        // already owns the full five-submit production budget here, so
        // sleeping/adaptive telemetry cannot accidentally become submit 6/7
        // merely because substep zero temporarily had only two submissions.
        this.pollPostFixedStepDiagnostics(
          renderer,
          firstLogicalStepId + completedFixedSteps - 1,
          pairGenerationDispatchCount,
          pairWarmStartDispatchCount,
          useCandidatePairs,
        );
      }
    }

    if (!advancedSimulation) {
      // Promise callbacks never submit directly. Idle animation ticks provide
      // an observed, zero-core-work maintenance cycle for the optional safety
      // gate proof needed by exact all-sleeping zero-work.
      this.servicePendingSleepingCertification(renderer);
    }

    if (stepsAvailable > stepsToRun) {
      // Clamp backlog to avoid runaway catch-up and repeated heavy broadphase work.
      this.accumulator = Math.min(this.accumulator, this.config.deltaTime);
    }

    this.stats.totalMs = performance.now() - start;
    this.stats.bodyCount = this.bodyCount;
    if (advancedSimulation) {
      this.logSupportDiagnostics(renderer);
    }
  }

  /**
   * Releases the raw WebGPU buffers owned directly by the broadphase stage.
   * Three.js-backed storage attributes remain renderer-managed and are
   * released once this engine and its visuals are no longer referenced.
   */
  dispose(): void {
    if (this.disposed) return;
    this.invalidateSleepingCertification();
    this.disposed = true;
    this.startupPrewarmGeneration++;
    this.startupPrewarmPromise = null;
    this.startupPrewarmCommands = null;
    if (this.initialized) this.broadPhase.dispose();
    this.contactGeneration?.dispose();
    this.constraintSafety?.dispose();
    this.sleepingIslands?.dispose();
    // Projection borrows restitution's velocity scratch, so retire the
    // non-owner first and destroy the shared raw buffers exactly once.
    this.velocityConstraintProjection?.dispose();
    this.restitutionStage?.dispose();
    this.pairContactsGpuAllocation?.destroy();
    this.jointRecordsGpuAllocation?.destroy();
    this.springRecordsGpuAllocation?.destroy();
    this.persistentManifoldMapGpuAllocation?.destroy();
    for (const allocation of this.retiredConstraintRecordAllocations) allocation.destroy();
    this.retiredConstraintRecordAllocations.clear();

    // A navigated page may stay alive in the browser's back/forward cache.
    // Keeping this engine reachable must not also retain its large CPU mirrors
    // (the 64k contact mirror alone is hundreds of MiB), nor the node graph that
    // references every renderer-owned storage attribute. The engine is
    // terminal after dispose(), so sever those references eagerly instead of
    // depending on document/GC timing.
    const attributes = new Set([
      this.positionsAttr,
      this.initialPoseAttr,
      this.inertialPoseAttr,
      this.velocitiesAttr,
      this.prevLinearVelAttr,
      this.shapesAttr,
      this.quaternionsAttr,
      this.angularVelAttr,
      this.inverseInertiaAttr,
      this.conservativeBodyFlagsAttr,
      this.pairContactsAttr,
      this.jointRecordsAttr,
      this.springRecordsAttr,
      this.pairActivityAttr,
      this.pairCandidateIndicesAttr,
      this.pairVisitedBitsAttr,
      this.pairBodyContactCountsAttr,
      this.pairBodyContactIndicesAttr,
      this.bodyConstraintCountsAttr,
      this.bodyConstraintRefsAttr,
      this.pairActiveCandidateSlotsAttr,
      this.pairActiveContactsAttr,
      this.ignoredPairHashAttr,
      this.persistentManifoldMapAttr,
      this.pairColorBodyClaimsAttr,
      this.constraintSafetyGateAttr,
    ]);
    for (const attribute of attributes) {
      const array = (attribute as any)?.array;
      if (array instanceof Float32Array) (attribute as any).array = new Float32Array(0);
      else if (array instanceof Uint32Array) (attribute as any).array = new Uint32Array(0);
    }

    this.positionsData = new Float32Array(0);
    this.initialPoseData = new Float32Array(0);
    this.initialLinearVelData = new Float32Array(0);
    this.initialAngularVelData = new Float32Array(0);
    this.inertialPoseData = new Float32Array(0);
    this.velocitiesData = new Float32Array(0);
    this.prevLinearVelData = new Float32Array(0);
    this.shapesData = new Float32Array(0);
    this.shapesWordData = new Uint32Array(0);
    this.quaternionsData = new Float32Array(0);
    this.angularVelData = new Float32Array(0);
    this.inverseInertiaData = new Float32Array(0);
    this.conservativeBodyFlagsData = new Uint32Array(0);
    this.pairContactsData = new Float32Array(0);
    this.jointRecordsData = new Float32Array(0);
    this.springRecordsData = new Float32Array(0);
    this.pairActivityData = new Uint32Array(0);
    this.pairCandidateIndicesData = new Uint32Array(0);
    this.pairVisitedBitsData = new Uint32Array(0);
    this.pairBodyContactCountsData = new Uint32Array(0);
    this.pairBodyContactIndicesData = new Uint32Array(0);
    this.bodyConstraintCountsData = new Uint32Array(0);
    this.bodyConstraintRefsData = new Uint32Array(0);
    this.pairActiveCandidateSlotsData = new Uint32Array(0);
    this.pairActiveContactsData = new Uint32Array(0);
    this.ignoredPairHashData = new Uint32Array(0);
    this.persistentManifoldMapData = new Uint32Array(0);
    this.pairColorBodyClaimsData = new Uint32Array(0);
    this.prevCandidatePairsForChurn = null;
    this.prevFloorCandidatePairsForChurn = null;
    this.bodyCount = 0;
    this.dynamicBodyCount = 0;

    for (const field of [
      'integration',
      'broadPhase',
      'contactGeneration',
      'avbdState',
      'restitutionStage',
      'velocityConstraintProjection',
      'sleepingIslands',
      'playerControl',
      'positionsAttr',
      'initialPoseAttr',
      'inertialPoseAttr',
      'velocitiesAttr',
      'prevLinearVelAttr',
      'shapesAttr',
      'quaternionsAttr',
      'angularVelAttr',
      'inverseInertiaAttr',
      'conservativeBodyFlagsAttr',
      'pairContactsAttr',
      'jointRecordsAttr',
      'springRecordsAttr',
      'pairActivityAttr',
      'pairCandidateIndicesAttr',
      'pairVisitedBitsAttr',
      'pairBodyContactCountsAttr',
      'pairBodyContactIndicesAttr',
      'bodyConstraintCountsAttr',
      'bodyConstraintRefsAttr',
      'pairActiveCandidateSlotsAttr',
      'pairActiveContactsAttr',
      'ignoredPairHashAttr',
      'persistentManifoldMapAttr',
      'pairColorBodyClaimsAttr',
      'constraintSafetyGateAttr',
      'constraintSafety',
    ] as const) {
      (this as any)[field] = undefined;
    }
  }

  private substep(
    dt: number,
    renderer: any,
    pairCount: number,
    pairGenerationDispatchCount: number,
    pairWarmStartDispatchCount: number,
    candidatePairsEnabled: boolean,
    logicalStepId: number,
    substepIndex: number,
    startupPrewarmOnly = false,
    refreshBroadphaseBeforeSolve = false,
  ): boolean {
    // The initial graph starts from deterministic seeds and receives a bounded
    // convergence warm-up. The paper specifies one incremental Jacobi greedy
    // sweep per timestep; the converged color is written back to
    // bodyConstraintCounts and becomes the next timestep's seed.
    const coloringWasDirty = this.bodyColoringDirty;
    const pureTailAppendFastPath = coloringWasDirty
      && this.bodyColoringInitialized
      && this.bodyColoringPureTailAppendPending;
    const coloringRounds = resolveAvbdBodyColoringRounds(
      coloringWasDirty,
      this.bodyColoringInitialized,
      this.bodyColoringPureTailAppendPending,
    );
    const mainPairSweeps = this.avbdPairSweeps;
    const splitPhysicalVelocity = this.contactRestitution > 0.0
      && this.restitutionIterations > 0;
    const nativeCollectorPolicy = getAvbdNativeCommandCollectorPolicy(
      this.avbdBodySolveMode,
      mainPairSweeps,
      {
        restitutionActive: splitPhysicalVelocity,
        jointCount: this.jointCount,
        springCount: this.springCount,
      },
    );
    const computeCommands: ThreeWebGpuComputeCommand[] = startupPrewarmOnly
      ? []
      : this.nativeComputeCommandCache;
    let computeCommandCount = 0;
    let computePassIndex = 0;
    const collectingCommandRenderer = {
      compute: (nodes: any | any[], dispatch?: any): void => {
        const list = Array.isArray(nodes) ? nodes : [nodes];
        const passIndex = computePassIndex++;
        for (const node of list) {
          const requestedDispatch = dispatch !== undefined && dispatch !== null
            ? dispatch
            : node.count;
          const dispatchSize: ThreeWebGpuDispatchSize = Array.isArray(requestedDispatch)
            ? [
              requestedDispatch[0],
              requestedDispatch[1],
              requestedDispatch[2],
            ] as readonly [number, number?, number?]
            : requestedDispatch as ThreeWebGpuDispatchSize;
          const cachedCommand = computeCommands[computeCommandCount];
          if (
            cachedCommand?.node !== node
            || cachedCommand.passIndex !== passIndex
            || cachedCommand.uniformStateInvariant !== true
            || !sameThreeWebGpuDispatchSize(cachedCommand.dispatchSize, dispatchSize)
          ) {
            this.nativeCommandCacheMisses++;
            computeCommands[computeCommandCount] = {
              node,
              passIndex,
              dispatchSize,
              // The collector is selected only for the one-sweep colored graph
              // after all known per-pass mutable-uniform extensions are excluded
              // by getAvbdNativeCommandCollectorPolicy().
              uniformStateInvariant: true,
            };
          } else {
            this.nativeCommandCacheHits++;
          }
          computeCommandCount++;
        }
      },
    };
    const immediateSubmissionLabel = `physics-avbd-immediate-${nativeCollectorPolicy.reason}`;
    const immediateCommandRenderer = {
      compute: (nodes: any | any[], dispatch?: any): void => {
        this.submissionLedger.withActualSubmitScope(
          this.device.queue,
          {
            frameId: this.frameId,
            submissionClass: 'critical',
            label: immediateSubmissionLabel,
            fixedStepId: logicalStepId,
            fixedStepSpan: 1,
            substepIndex,
          },
          () => dispatch === undefined || dispatch === null
            ? renderer.compute(nodes)
            : renderer.compute(nodes, dispatch),
        );
        this.submissionLedger.record('critical', immediateSubmissionLabel, {
          frameId: this.frameId,
          fixedStepId: logicalStepId,
          fixedStepSpan: 1,
          substepIndex,
        });
      },
      getArrayBufferAsync: typeof renderer?.getArrayBufferAsync === 'function'
        ? (...args: any[]): Promise<ArrayBuffer> => renderer.getArrayBufferAsync(...args)
        : undefined,
    };
    // Three's ComputeNode uniforms are mutable objects backed by one GPU
    // uniform allocation. Deferring serial bodyIndexBase or multi-sweep
    // sweepOffset changes would make every occurrence observe the last value.
    // Keep the official one-sweep colored graph fused; execute explicit
    // diagnostic extensions immediately so every Three submit snapshots the
    // intended parameter values and ordering.
    const commandRenderer = startupPrewarmOnly
      ? collectingCommandRenderer
      : nativeCollectorPolicy.eligible
      ? collectingCommandRenderer
      : immediateCommandRenderer;
    let t0: number;
    try {
    // The fused path prepends this refresh to the deferred solver graph at the
    // end of the function. An immediate graph cannot be reordered afterward,
    // so refresh s>0 before integration/contact generation consumes it.
    if (
      !nativeCollectorPolicy.eligible
      && (substepIndex > 0 || refreshBroadphaseBeforeSolve)
      && candidatePairsEnabled
      && !this.refreshBroadphaseForSubstep(
        renderer,
        pairCount,
        dt,
        logicalStepId,
        substepIndex,
        Object.freeze([]),
      )
    ) {
      return false;
    }
    t0 = performance.now();
    const restitutionStage = splitPhysicalVelocity
      ? this.ensureRestitutionStage(renderer)
      : null;
    this.integration.dispatch(
      commandRenderer,
      this.bodyCount,
      dt,
      splitPhysicalVelocity ? 0.5 : 1.0,
    );
    this.stats.integrationMs = performance.now() - t0;

    t0 = performance.now();
    this.contactGeneration.setFloorDebugBody(
      this.debugBehaviorEnabled ? this.resolveSupportFloorDebugCache().body : -1,
    );
    this.contactGeneration.dispatch(
      commandRenderer,
      this.bodyCount,
      pairCount,
      pairGenerationDispatchCount,
      candidatePairsEnabled,
      this.frameId,
    );
    // ContactGeneration has now reduced broadphase/contact/CCD/compact
    // correctness sources into the shared GPU gate. Healthy frames execute a
    // zero-dispatch indirect call; fatal frames snapshot angular velocity and
    // erase incomplete body adjacency before sleeping or AVBD can consume it.
    this.constraintSafety.captureAndGate(commandRenderer);
    const sleepingFrameParams = {
      bodyCount: this.bodyCount,
      dynamicBodyCount: this.dynamicBodyCount,
      frameId: this.frameId,
      jointCount: this.jointCount,
      springCount: this.springCount,
      restitutionActive: splitPhysicalVelocity,
      topologyGeneration: this.sleepTopologyGeneration,
      gravity: this.config.gravity,
    };
    // Contact generation has already built the exact current graph. Sleeping
    // islands consume that graph and perform same-command-buffer wake-up
    // before AVBD prepares/color-compacts its body lists.
    this.sleepingIslands?.beforeSolve(commandRenderer, sleepingFrameParams);
    // The fused integration pass snapshots x^t and seeds the AVBD primal guess
    // toward x^(t+1) before narrowphase; contact generation still reads x^t
    // from the current position/quaternion buffers.
    const solveAlpha = this.avbdRegularizationAlpha;
    const mainTangentialAlpha = solveAlpha;
    const mainIterations = this.solverIterations;
    const adaptiveIterationPolicyEnabled = this.avbdAdaptiveIterationsConfigured
      && this.contactRestitution <= 0.0
      && mainPairSweeps === 1
      && this.avbdBodySolveMode === 'colored';
    this.avbdState.setAdaptiveIterationPolicy({
      enabled: adaptiveIterationPolicyEnabled,
      minIterations: this.avbdAdaptiveMinIterations,
      linearTolerance: this.avbdAdaptiveLinearTolerance,
      angularTolerance: this.avbdAdaptiveAngularTolerance,
      dualTolerance: this.avbdAdaptiveDualTolerance,
      penaltyTolerance: this.avbdAdaptivePenaltyTolerance,
      forceInvalidForTesting: this.avbdAdaptiveForceInvalidForTesting,
      deferPreMinResiduals: this.avbdAdaptiveDeferPreMinResiduals,
    });
    const mainSolveTuning = {
      relaxation: 1.0,
      maxLinearCorrection: 1.0e9,
      maxAngularCorrection: 1.0e9,
    };
    const dualUpdateBeta = this.avbdDualUpdateBeta;
    const angularDualUpdateBeta = this.avbdAngularDualUpdateBeta;
    const warmstartScale = solveAlpha * this.avbdPenaltyDecayGamma;
    if (this.contactRestitution > 0.0 && this.restitutionIterations > 0) {
      // Keep a physical velocity channel separate from AVBD's positional
      // stabilization velocity. The strict restitution=0 path below remains
      // the reference x-difference velocity update.
      restitutionStage!.capturePhysicalVelocities(
        commandRenderer,
        this.bodyCount,
        this.config.gravity,
        dt,
      );
      restitutionStage!.initializeImpacts(
        commandRenderer,
        pairWarmStartDispatchCount,
        this.bodyCount,
        this.contactRestitution,
        this.restitutionVelocityThreshold,
      );
    }
    this.avbdState.prepare(
      commandRenderer,
      this.bodyCount,
      pairWarmStartDispatchCount,
      this.jointCount,
      this.springCount,
      this.activePairSolveColorCount,
      warmstartScale,
      dt,
      this.avbdBodySolveMode,
      coloringRounds,
      mainIterations > 0,
    );
    this.bodyColoringLastRounds = coloringRounds;
    if (coloringWasDirty) {
      this.bodyColoringLastDirtyRounds = coloringRounds;
      if (pureTailAppendFastPath) {
        this.bodyColoringPureTailAppendFastPathUses++;
      } else if (coloringRounds === 4) {
        this.bodyColoringFourRoundRepairUses++;
      } else if (coloringRounds === 16) {
        this.bodyColoringInitialWarmupUses++;
      }
    }
    this.bodyColoringDirty = false;
    this.bodyColoringInitialized = true;
    this.bodyColoringPureTailAppendPending = false;
    this.avbdState.setDualUpdateBetas(dualUpdateBeta, angularDualUpdateBeta);
    this.avbdState.clearPhaseDebugCounters(commandRenderer);
    this.avbdState.primalSolveAndCaptureIterations(
      commandRenderer,
      mainIterations,
      this.bodyCount,
      pairWarmStartDispatchCount,
      this.jointCount,
      this.springCount,
      mainPairSweeps,
      this.activePairSolveColorCount,
      solveAlpha,
      dt,
      1.0,
      mainTangentialAlpha,
      mainSolveTuning,
      0,
      this.avbdBodySolveMode,
    );
    this.avbdState.finalizeVelocities(commandRenderer, this.bodyCount, dt);
    // Eligibility is measured from the converged AVBD state, then sleep flags
    // become the next substep's zero-work body mask. This remains in the same
    // native compute submission as integration/contact/solve/finalization.
    this.sleepingIslands?.afterSolve(commandRenderer, sleepingFrameParams);
    if (this.contactRestitution > 0.0 && this.restitutionIterations > 0) {
      const velocityStart = performance.now();
      restitutionStage!.restorePhysicalVelocities(commandRenderer, this.bodyCount);
      const velocityProjection = this.hardVelocityConstraintCount > 0
        ? this.ensureVelocityConstraintProjection(restitutionStage!)
        : null;
      // First satisfy the ideal bilateral tangent space.
      velocityProjection?.dispatch(
        commandRenderer,
        this.bodyCount,
        this.activePairSolveColorCount,
        2,
        1.0,
      );
      // Contact restitution and hard bilateral constraints share the physical
      // velocity channel. Alternate them so a string/joint projection cannot
      // leave a newly closing contact (and vice versa).
      const coupledRounds = Math.max(1, Math.min(4, this.restitutionIterations));
      const contactIterationsPerRound = Math.floor(this.restitutionIterations / coupledRounds);
      const extraContactIterations = this.restitutionIterations % coupledRounds;
      for (let round = 0; round < coupledRounds; round++) {
        restitutionStage!.solve(
          commandRenderer,
          pairWarmStartDispatchCount,
          this.bodyCount,
          contactIterationsPerRound + (round < extraContactIterations ? 1 : 0),
        );
        velocityProjection?.dispatch(
          commandRenderer,
          this.bodyCount,
          this.activePairSolveColorCount,
          1,
          1.0,
        );
      }
      // Apply one homogeneous energy-momentum correction per explicitly
      // tagged conservative island. It runs after contact exchange, so it
      // preserves the island total instead of restoring each body's old share.
      restitutionStage!.correctConservativeEnergies(
        commandRenderer,
        this.bodyCount,
        this.conservativeIslandCount,
        this.config.gravity,
      );
      this.stats.velocityUpdateMs = performance.now() - velocityStart;
    } else {
      this.stats.velocityUpdateMs = 0;
    }
    this.avbdState.capturePhaseDebug(
      commandRenderer,
      pairWarmStartDispatchCount,
      solveAlpha,
      0,
      1.0,
      mainTangentialAlpha,
    );
    // Physical-state transaction boundary: if any earlier GPU validator
    // failed, restore the exact pre-substep committed pose and velocities in
    // queue order before rendering can observe partially solved work.
    this.constraintSafety.rollback(commandRenderer);
    computeCommands.length = computeCommandCount;
    if (startupPrewarmOnly) {
      // Pipeline prewarm only needs the final unique node graph. It must stop
      // before broadphase refresh, command encoding, queue submission, sleep
      // certification, or any public simulation-step accounting.
      this.startupPrewarmCommands = computeCommands;
      return false;
    }
    if (
      nativeCollectorPolicy.eligible
      && (substepIndex > 0 || refreshBroadphaseBeforeSolve)
      && candidatePairsEnabled
    ) {
      if (!this.refreshBroadphaseForSubstep(
        renderer,
        pairCount,
        dt,
        logicalStepId,
        substepIndex,
        computeCommands,
      )) {
        return false;
      }
    } else if (nativeCollectorPolicy.eligible && computeCommands.length > 0) {
      const submission = this.submitAvbdComputeCommands(
        renderer,
        computeCommands,
        'physics-avbd-substep',
        logicalStepId,
        substepIndex,
      );
      if (!submission.ok) {
        this.failClosedAvbdComputeSubmission(
          logicalStepId,
          substepIndex,
          submission.error,
        );
        return false;
      }
    }
    this.sleepingIslands?.confirmSubmittedFrame(this.frameId);
    this.hasAdvancedSimulation = true;
    const adaptiveStats = this.avbdState.getAdaptiveIterationStats();
    // Configuration only means the adaptive path is eligible to be selected.
    // The concrete AVBD kernels can still select the fixed reference/generic
    // fallback for hard constraints, a non-reference profile, or another
    // unsupported solve shape. Drive the live counters from the solve-path
    // latch so the UI never reports stale adaptive iterations for a fixed
    // fallback step.
    const adaptiveSelectedLast = this.avbdState
      .getSolvePathTelemetry()
      .adaptiveSelectedLast;
    const hasAdaptiveSample = adaptiveSelectedLast
      && adaptiveStats.adaptiveSteps > 0;
    this.stats.avbdIterationsLast = hasAdaptiveSample
      ? adaptiveStats.executedIterationsLast
      : mainIterations;
    this.stats.avbdIterationsAverage = hasAdaptiveSample
      ? adaptiveStats.averageIterations
      : mainIterations;
    this.stats.avbdAdaptiveEarlyExits = adaptiveStats.adaptiveSteps > 0
      ? adaptiveStats.earlyExits
      : 0;
    this.stats.solverMs = performance.now() - t0;
    return true;
    } catch (error) {
      // Immediate Three is intentionally used only for diagnostic modes with
      // mutable uniforms. A synchronous failure can follow earlier successful
      // passes, so never certify or continue that partially advanced step.
      this.failClosedAvbdComputeSubmission(
        logicalStepId,
        substepIndex,
        error,
        'solver-dispatch-exception',
      );
      return false;
    }
  }

  private pollPostFixedStepDiagnostics(
    renderer: any,
    logicalStepId: number,
    pairGenerationDispatchCount: number,
    pairWarmStartDispatchCount: number,
    candidatePairsEnabled: boolean,
  ): void {
    const finalSubstepIndex = Math.max(0, this.config.substeps - 1);
    this.maybePollSleepingTelemetry(renderer);
    // The gate proof is now serviced in an observed maintenance tick, so a
    // one-substep frame may safely use the remaining slot for adaptive stats.
    // A saturated five-submit frame defers this optional map deterministically.
    const adaptiveBudgetAvailable = this.submissionLedger
      .snapshot(this.frameId).totalOwnedSubmitIntents < 4;
    const adaptiveReadbackSubmitted = adaptiveBudgetAvailable
      ? this.submissionLedger.withActualSubmitScope(
        this.device.queue,
        {
          frameId: this.frameId,
          submissionClass: 'diagnostic',
          label: 'adaptive-iteration-readback',
          fixedStepId: logicalStepId,
          fixedStepSpan: 1,
          substepIndex: finalSubstepIndex,
        },
        () => this.avbdState.maybePollAdaptiveIterationStats(renderer, this.frameId),
      )
      : false;
    if (
      !adaptiveBudgetAvailable
      && this.avbdAdaptiveIterationsConfigured
      && this.frameId > 0
      && this.frameId % 120 === 0
    ) {
      this.adaptiveTelemetryMaintenancePending = true;
    }
    if (adaptiveReadbackSubmitted) {
      this.adaptiveTelemetryMaintenancePending = false;
      this.submissionLedger.record('diagnostic', 'adaptive-iteration-readback', {
        frameId: this.frameId,
      });
    }

    // Candidate-path worlds copy the post-contact shared safety gate into the
    // broadphase staging ring inside the same native command encoder. Exact
    // small worlds have no such encoder, so retain their one-map sparse poll.
    const standaloneSafetyPollRequired = !candidatePairsEnabled
      || this.postContactSafetySampleOwnerFrameId !== this.frameId
      || this.contactGeneration.requiresStandaloneSafetyPollForTesting();
    const standaloneSafetyPollForcedForTesting = this.contactGeneration
      .requiresStandaloneSafetyPollForTesting();
    if (
      standaloneSafetyPollRequired
      && (
        standaloneSafetyPollForcedForTesting
        || this.submissionLedger.snapshot(this.frameId).totalOwnedSubmitIntents < 5
      )
    ) {
      this.submissionLedger.withActualSubmitScope(
        this.device.queue,
        {
          frameId: this.frameId,
          submissionClass: 'diagnostic',
          label: 'contact-capacity-readback-batch',
          fixedStepId: logicalStepId,
          fixedStepSpan: 1,
          substepIndex: finalSubstepIndex,
        },
        () => this.contactGeneration.pollCapacityLoss(
          renderer,
          this.frameId,
          this.bodyCount,
          pairGenerationDispatchCount,
        ),
      );
    }
    this.avbdState.maybeLogDebug(
      renderer,
      this.frameId,
      pairWarmStartDispatchCount,
      this.bodyCount,
    );
  }

  getBodyCount(): number {
    return this.bodyCount;
  }

  getCpuOptimizationTelemetry(): Readonly<{
    mutationBatchCompletions: number;
    rendererPublishRequestsBatched: number;
    rendererPublishFlushes: number;
    jointRecordRowsBatched: number;
    springRecordRowsBatched: number;
    nativeCommandCacheHits: number;
    nativeCommandCacheMisses: number;
    nativePreparedCommandCapacity: number;
  }> {
    return Object.freeze({
      mutationBatchCompletions: this.mutationBatchCompletions,
      rendererPublishRequestsBatched: this.batchedRendererPublishRequests,
      rendererPublishFlushes: this.batchedRendererPublishFlushes,
      jointRecordRowsBatched: this.batchedJointRecordRows,
      springRecordRowsBatched: this.batchedSpringRecordRows,
      nativeCommandCacheHits: this.nativeCommandCacheHits,
      nativeCommandCacheMisses: this.nativeCommandCacheMisses,
      nativePreparedCommandCapacity: this.nativeComputePreparationCache.commands.length,
    });
  }

  readContactCapacityLossAsync(renderer: any): Promise<{
    pairBodyListOverflowWrites: number;
    pairBodyListDroppedContacts: number;
    activeContactListOverflowWrites: number;
    persistentManifoldPoolOverflowWrites: number;
    persistentManifoldPoolReclaims: number;
    persistentManifoldHashProtocolFailures: number;
    compactFatalBits: number;
    compactFirstFatalEpoch: number;
  } | null> {
    return this.contactGeneration.readCapacityLossAsync(renderer);
  }

  /** Regression-only hook for the same GPU word used by a non-finite CCD sweep. */
  injectCcdFatalForTesting(bits = 1): void {
    this.contactGeneration.injectCcdFatalForTesting(bits);
  }

  /** Regression-only watchdog injections; production paths never call these. */
  injectBroadphaseFatalSampleReadbackTimeoutForTesting(): void {
    this.broadPhase.injectFatalSampleReadbackTimeoutForTesting();
  }

  injectSleepingCertificationTimeoutForTesting(): void {
    if (!this.sleepingIslands) throw new Error('Sleeping-island stage is not allocated.');
    this.forceSleepingCertificationTimeoutForTesting = true;
  }

  injectContactSafetyReadbackFailureForTesting(
    mode: 'sync' | 'reject' | 'timeout',
  ): void {
    this.contactGeneration.injectSafetyReadbackFailureForTesting(mode);
  }

  requestContactSafetyPollForTesting(): void {
    this.contactGeneration.requestStandaloneSafetyPollForTesting();
  }

  hasContactSafetyReadbackInfrastructureFailure(): boolean {
    return this.contactGeneration.hasSafetyReadbackInfrastructureFailure();
  }

  /** Regression-only readback of the scene-cumulative shared GPU safety gate. */
  readConstraintSafetyGateAsync(renderer: any): Promise<number> {
    return this.contactGeneration.readConstraintSafetyGateAsync(renderer);
  }

  /** Regression-only snapshot: [fatalBits, retainedSleepingPrefix]. */
  readConstraintSafetyWordsAsync(renderer: any): Promise<Uint32Array> {
    return this.contactGeneration.readConstraintSafetyWordsAsync(renderer);
  }

  /** Regression-only readback of the GPU-written fatal rollback dispatch record. */
  readConstraintSafetyFatalDispatchAsync(renderer: any): Promise<Uint32Array> {
    return this.contactGeneration.readFatalBodyDispatchAsync(renderer);
  }

  getBroadphaseCapacityDiagnostics(): {
    frame: number;
    bodies: number;
    candidates: number;
    capacity: number;
    capacityDrops: number;
    visitBudgetDrops: number;
    stackDrops: number;
  } | null {
    const snapshot = this.broadPhase.getLastDebugSnapshot();
    if (!snapshot) return null;
    return {
      frame: snapshot.frame,
      bodies: snapshot.bodies,
      candidates: snapshot.candidates,
      capacity: snapshot.capacity,
      capacityDrops: snapshot.capacityDrops,
      visitBudgetDrops: snapshot.visitBudgetDrops,
      stackDrops: snapshot.stackDrops,
    };
  }

  async readBroadphaseCapacityDiagnosticsAsync(): Promise<{
    frame: number;
    bodies: number;
    candidates: number;
    capacity: number;
    capacityDrops: number;
    visitBudgetDrops: number;
    stackDrops: number;
    ignoredPairProbeFallbacks: number;
  } | null> {
    const snapshot = await this.broadPhase.readDebugSnapshotAsync();
    if (!snapshot) return null;
    return {
      frame: snapshot.frame,
      bodies: snapshot.bodies,
      candidates: snapshot.candidates,
      capacity: snapshot.capacity,
      capacityDrops: snapshot.capacityDrops,
      visitBudgetDrops: snapshot.visitBudgetDrops,
      stackDrops: snapshot.stackDrops,
      ignoredPairProbeFallbacks: snapshot.ignoredPairProbeFallbacks,
    };
  }

  /** Full post-queue broadphase telemetry used by performance/correctness gates. */
  readBroadphaseDebugSnapshotAsync(): Promise<BroadphaseDebugSnapshot | null> {
    return this.broadPhase.readDebugSnapshotAsync();
  }

  /**
   * Read-only snapshot of the compact broadphase candidate stream. The
   * returned values use the production packed-pair layout (low 16 bits body
   * A, high 16 bits body B) and are filtered to valid, ordered live-body
   * pairs. This is intentionally a diagnostics API: it neither regenerates
   * candidates nor mutates broadphase state.
   */
  async readBroadphaseCandidatePairsAsync(renderer: any): Promise<number[] | null> {
    if (!this.initialized) return null;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return null;

    const activityRaw = await renderer.getArrayBufferAsync(this.pairActivityAttr);
    const activity = new Uint32Array(activityRaw);
    const count = Math.min(
      activity[this.pairCandidateIndicesOffset] ?? 0,
      this.maxCandidatePairs,
    );
    const pairs: number[] = [];
    for (let slot = 0; slot < count; slot++) {
      const packed = activity[this.pairCandidateIndicesOffset + 1 + slot] ?? 0xffffffff;
      const bodyA = packed & 0xffff;
      const bodyB = packed >>> 16;
      if (bodyA >= bodyB || bodyB >= this.bodyCount) continue;
      pairs.push(packed >>> 0);
    }
    return pairs;
  }

  /** CPU-only LBVH build/publication/quality lifecycle telemetry. */
  getBroadphaseLifecycleSnapshot(): BroadphaseLifecycleSnapshot {
    return this.broadPhase.getLifecycleSnapshot();
  }

  /** CPU-only allocation/use proof for the optional broadphase pair dedup. */
  getBroadphasePairDedupTelemetry(): BroadphasePairDedupTelemetry {
    return this.broadPhase.getPairDedupTelemetry();
  }

  /** Regression-only control for the retained-contact sparse closure. */
  setSleepingSparseBroadphaseForTesting(
    enabled: boolean,
    minimumBodyCount = 1,
    forceFull = false,
  ): void {
    this.sleepingSparseBroadphaseRequested = enabled;
    this.sleepingSparseBroadphaseMinimumBodyCount = Math.max(
      1,
      Math.floor(minimumBodyCount),
    );
    this.sleepingSparseBroadphaseForceFull = forceFull;
    if (!this.initialized) return;
    this.broadPhase.setSleepingSparseModeForTesting(
      enabled,
      this.sleepingSparseBroadphaseMinimumBodyCount,
    );
    this.broadPhase.setForceFullSleepingSparseForTesting(forceFull);
  }

  getSleepingSparseBroadphaseDiagnostics(): BroadphaseSleepingSparseDiagnostics {
    return this.broadPhase.getSleepingSparseDiagnostics();
  }

  readSleepingSparseScratchDiagnosticsAsync(
    renderer: any,
  ): Promise<BroadphaseSleepingSparseScratchDiagnostics | null> {
    return this.broadPhase.readSleepingSparseScratchDiagnosticsAsync(renderer);
  }

  /** CPU-only fatal-sample ring telemetry; never records or submits GPU work. */
  getBroadphaseFatalSampleDiagnostics(): BroadphaseFatalSampleDiagnostics {
    if (!this.initialized) {
      return {
        pendingSamples: 0,
        totalPendingSamples: 0,
        backpressureSkips: 0,
        infrastructureFailure: false,
        lastCompletedSampleFrame: -1,
      };
    }
    return this.broadPhase.getFatalSampleDiagnostics();
  }

  readPersistentAllocatorStateAsync(
    renderer: any,
  ): Promise<PersistentContactAllocatorState | null> {
    return this.contactGeneration.readPersistentAllocatorStateAsync(renderer);
  }

  /** Regression-only hook for validating compact contact epoch wrap. */
  setContactManifoldEpochForTesting(epoch: number): void {
    this.sleepingIslands?.requestLocalMutation();
    this.contactGeneration.setManifoldEpochForTesting(epoch);
  }

  /** Regression-only compact-header corruption used to prove fail-closed validation. */
  injectCompactPersistentHighWaterForTesting(highWater: number): void {
    if (
      !this.initialized
      || this.contactPoolMode !== 'compact'
      || !this.persistentManifoldMapGpuAllocation
      || this.persistentManifoldMapGpuAllocation.destroyed
    ) {
      throw new Error('Compact persistent-map allocation is unavailable.');
    }
    if (!Number.isSafeInteger(highWater) || highWater < 0 || highWater > 0xffff_ffff) {
      throw new RangeError('Compact persistent high-water injection must be a u32.');
    }
    this.device.queue.writeBuffer(
      this.persistentManifoldMapGpuAllocation.buffer,
      0,
      new Uint32Array([highWater >>> 0]),
    );
  }

  readBodyColorDiagnosticsAsync(renderer: any): Promise<AvbdBodyColorTelemetry | null> {
    return this.avbdState.readBodyColorDiagnosticsAsync(renderer, this.bodyCount);
  }

  /** CPU-only evidence for coloring-round selection; records no GPU work. */
  getAvbdBodyColoringScheduleDiagnostics(): AvbdBodyColoringScheduleDiagnostics {
    return Object.freeze({
      dirty: this.bodyColoringDirty,
      initialized: this.bodyColoringInitialized,
      pureTailAppendPending: this.bodyColoringPureTailAppendPending,
      lastRounds: this.bodyColoringLastRounds,
      lastDirtyRounds: this.bodyColoringLastDirtyRounds,
      pureTailAppendFastPathUses: this.bodyColoringPureTailAppendFastPathUses,
      fourRoundRepairUses: this.bodyColoringFourRoundRepairUses,
      initialWarmupUses: this.bodyColoringInitialWarmupUses,
    });
  }

  async readSolvePathTelemetryAsync(renderer: any): Promise<PhysicsSolvePathTelemetry> {
    // Keep the renderer readbacks ordered. Three's WebGPU backend owns mutable
    // command state, so overlapping two diagnostic maps is not a useful part
    // of the solver-path certification.
    const bodyColors = await this.readBodyColorDiagnosticsAsync(renderer);
    const sleeping = await this.readSleepingIslandsTelemetryAsync(renderer);
    return {
      avbd: this.avbdState.getSolvePathTelemetry(),
      parameters: {
        linearDualUpdateBeta: this.avbdDualUpdateBeta,
        angularDualUpdateBeta: this.avbdAngularDualUpdateBeta,
        regularizationAlpha: this.avbdRegularizationAlpha,
        penaltyDecayGamma: this.avbdPenaltyDecayGamma,
        penaltyFloor: this.avbdPenaltyFloor,
        solverIterations: this.solverIterations,
        pairSweeps: this.avbdPairSweeps,
        adaptiveIterationsConfigured: this.avbdAdaptiveIterationsConfigured,
      },
      capacities: {
        maxJoints: this.maxJoints,
        maxSprings: this.maxSprings,
        maxIgnoredPairs: this.maxIgnoredPairs,
      },
      sleepingConfigured: this.sleepingConfigured,
      sleepingStageAllocated: this.sleepingIslands !== undefined,
      sleepingStageRan: sleeping !== null
        && sleeping.substepEpoch > 0
        && sleeping.awakeFastSubsteps + sleeping.fullDsuSubsteps > 0,
      bodyColors,
      sleeping,
    };
  }

  getAdaptiveIterationStats(): AvbdAdaptiveIterationStats {
    return this.avbdState?.getAdaptiveIterationStats?.() ?? {
      enabledLast: false,
      executedIterationsLast: 0,
      minIterationsLast: 0,
      maxIterationsLast: 0,
      earlyExitLast: false,
      fallbackBodiesLast: 0,
      invalidLast: false,
      bodyResidualViolationLast: false,
      fallbackBodyResidualViolationLast: false,
      contactResidualViolationLast: false,
      adaptiveSteps: 0,
      totalIterations: 0,
      averageIterations: 0,
      earlyExits: 0,
      failOpenSteps: 0,
      fallbackFailOpenSteps: 0,
      invalidFailOpenSteps: 0,
    };
  }

  readAdaptiveIterationStatsAsync(
    renderer: any,
  ): Promise<AvbdAdaptiveIterationStats | null> {
    if (!this.avbdState) return Promise.resolve(null);
    return this.avbdState.readAdaptiveIterationStatsAsync(renderer);
  }

  private rejectSleepingSafetyReadbackTimeout(
    certificationGeneration: number,
    sleepingStage: SleepingIslandsStage,
    error: Error,
  ): void {
    if (
      certificationGeneration !== this.sleepingCertificationGeneration
      || this.disposed
    ) return;
    // Zero-work is an optional optimization. Until this proof succeeds the
    // ordinary AVBD path remains authoritative, so an unavailable/slow map
    // must reject only the certificate rather than freezing the simulation.
    // Invalidate the still-running chain so its late callback cannot re-arm.
    this.sleepingCertificationGeneration++;
    sleepingStage.rejectZeroWorkCertification();
    if (this.debugBehaviorEnabled) {
      console.warn(
        'Sleeping zero-work safety certification timed out; continuing with full AVBD.',
        error,
      );
    }
  }

  private invalidateSleepingCertification(): void {
    this.sleepingCertificationGeneration++;
    this.sleepingTelemetryMaintenancePending = false;
    this.adaptiveTelemetryMaintenancePending = false;
    this.pendingSleepingCertificationTelemetry = null;
    this.sleepingCertificationPromise = null;
    this.forceSleepingCertificationTimeoutForTesting = false;
  }

  private servicePendingSleepingCertification(renderer: any): boolean {
    const telemetry = this.pendingSleepingCertificationTelemetry;
    if (telemetry !== null && this.sleepingIslands?.getZeroWorkDiagnostics().eligible) {
      // The previously launched telemetry completed after a newer gate proof
      // had already armed exact identity. Discard the redundant request so a
      // certified zero-work step remains literally zero-submit.
      this.pendingSleepingCertificationTelemetry = null;
      return false;
    }
    if (
      telemetry === null
      || this.sleepingCertificationPromise !== null
      || this.disposed
      || this.submissionLedger.snapshot(this.frameId).totalOwnedSubmitIntents >= 5
    ) {
      return false;
    }
    this.pendingSleepingCertificationTelemetry = null;
    void this.startSleepingZeroWorkCertification(renderer, telemetry);
    return true;
  }

  private servicePendingDiagnosticMaintenance(renderer: any): boolean {
    const combineAdaptiveCadence = (
      this.sleepingTelemetryMaintenancePending
      || this.pendingSleepingCertificationTelemetry !== null
    )
      && this.avbdAdaptiveIterationsConfigured
      && this.frameId > 0
      && this.frameId % 120 === 0;
    let submitted = this.servicePendingSleepingCertification(renderer);
    if (this.sleepingTelemetryMaintenancePending) {
      submitted = this.maybePollSleepingTelemetry(renderer, true) || submitted;
    }
    if (this.adaptiveTelemetryMaintenancePending || combineAdaptiveCadence) {
      const adaptiveSubmitted = this.submissionLedger.withActualSubmitScope(
        this.device.queue,
        {
          frameId: this.frameId,
          submissionClass: 'diagnostic',
          label: 'adaptive-iteration-readback',
        },
        () => this.avbdState.maybePollAdaptiveIterationStats(
          renderer,
          this.frameId,
          true,
        ),
      );
      if (adaptiveSubmitted) {
        this.adaptiveTelemetryMaintenancePending = false;
        this.submissionLedger.record('diagnostic', 'adaptive-iteration-readback', {
          frameId: this.frameId,
        });
        submitted = true;
      }
    }
    return submitted;
  }

  private startSleepingZeroWorkCertification(
    renderer: any,
    candidateTelemetry?: SleepingIslandsTelemetry,
    trackSubmissionInFrame = true,
  ): Promise<SleepingIslandsTelemetry | null> {
    if (!this.sleepingIslands) return Promise.resolve(null);
    if (this.sleepingCertificationPromise) return this.sleepingCertificationPromise;
    const sleepingStage = this.sleepingIslands;
    const requiresBroadphaseProof = this.requiresCandidatePairPath();
    const certificationGeneration = ++this.sleepingCertificationGeneration;
    const certificationFrameId = this.frameId;
    const certificationSimulationStep = this.stats.simulationStepCount;
    const certificationTopologyGeneration = this.sleepTopologyGeneration;
    const certificationBodyCount = this.bodyCount;
    const certificationZeroWork = sleepingStage.getZeroWorkDiagnostics();
    // A certified identity step advances logical frame/step counters without
    // submitting a new sleeping pass. In that state the newest physical GPU
    // snapshot is intentionally still completedFrameId, not this.frameId.
    // Remember it so an explicit telemetry read does not revoke a valid
    // all-sleeping certificate merely because logical time advanced.
    const certificationPhysicalFrameId = certificationZeroWork.eligible
      && certificationZeroWork.completedFrameSubmitted
      ? certificationZeroWork.completedFrameId
      : certificationFrameId;
    const forceTimeout = this.forceSleepingCertificationTimeoutForTesting;
    this.forceSleepingCertificationTimeoutForTesting = false;
    const certify = async (): Promise<SleepingIslandsTelemetry | null> => {
      const beginGateReadback = (): Promise<number> => (
        this.contactGeneration.readConstraintSafetyGateAsync(renderer)
      );
      const gateReadback = trackSubmissionInFrame
        ? this.submissionLedger.withActualSubmitScope(
          this.device.queue,
          {
            frameId: this.frameId,
            submissionClass: 'diagnostic',
            label: 'sleeping-safety-gate-readback',
          },
          beginGateReadback,
        )
        : beginGateReadback();
      if (trackSubmissionInFrame) {
        this.submissionLedger.record('diagnostic', 'sleeping-safety-gate-readback', {
          frameId: this.frameId,
        });
      }
      const reads = Promise.all([
        candidateTelemetry
          ? Promise.resolve(candidateTelemetry)
          : sleepingStage.readTelemetryAsync(renderer),
        gateReadback,
      ]);
      const [telemetry, safetyGateBits] = forceTimeout
        ? await Promise.all([
          reads,
          new Promise<void>((resolve) => {
            setTimeout(resolve, this.safetyReadbackTimeoutMs * 3);
          }),
        ]).then(([values]) => values)
        : await reads;
      if (
        certificationGeneration !== this.sleepingCertificationGeneration
        || this.disposed
      ) return null;
      if (telemetry === null) {
        sleepingStage.rejectZeroWorkCertification();
        return telemetry;
      }
      const currentZeroWork = sleepingStage.getZeroWorkDiagnostics();
      const certifiedIdentityAdvanced = certificationZeroWork.eligible
        && currentZeroWork.eligible
        && certificationZeroWork.completedFrameSubmitted
        && currentZeroWork.completedFrameSubmitted
        && currentZeroWork.completedFrameId === certificationZeroWork.completedFrameId
        && currentZeroWork.completedMutationEpoch === certificationZeroWork.completedMutationEpoch
        && currentZeroWork.mutationEpoch === certificationZeroWork.mutationEpoch;
      // Never apply a certificate to a state that advanced while its maps were
      // pending. Certified zero-submit identity steps are the sole exception:
      // their exact CPU certificate proves that the physical GPU snapshot did
      // not advance. The full solver otherwise continued meanwhile, so
      // rejection is safe and the next periodic probe can try again.
      if (
        (
          this.stats.simulationStepCount !== certificationSimulationStep
          && !certifiedIdentityAdvanced
        )
        || telemetry.frameId !== certificationPhysicalFrameId
        || this.sleepTopologyGeneration !== certificationTopologyGeneration
        || this.bodyCount !== certificationBodyCount
      ) {
        sleepingStage.rejectZeroWorkCertification();
        return telemetry;
      }
      const contactSafetyClear = (safetyGateBits & CONSTRAINT_SAFETY_ALL_FATAL_BITS) === 0
        && this.getContactSafetyFailureReason() === null;
      const fatalSamples = this.broadPhase.getFatalSampleDiagnostics();
      const broadphaseSafetyClear = !requiresBroadphaseProof || (
        !fatalSamples.infrastructureFailure
        && !this.broadPhase.hasCandidateOverflow()
      );
      sleepingStage.certifyZeroWork(
        telemetry,
        contactSafetyClear && broadphaseSafetyClear,
      );
      return telemetry;
    };
    const certificationWork = certify()
      .catch((error) => {
        if (
          certificationGeneration === this.sleepingCertificationGeneration
          && !this.disposed
        ) {
          sleepingStage.rejectZeroWorkCertification();
        }
        if (
          this.debugBehaviorEnabled
          && certificationGeneration === this.sleepingCertificationGeneration
        ) {
          console.warn('Sleeping zero-work safety certification failed:', error);
        }
        return null;
      });
    let timeoutHandle: ReturnType<typeof setTimeout>;
    const timeout = new Promise<null>((resolve) => {
      timeoutHandle = setTimeout(() => {
        this.rejectSleepingSafetyReadbackTimeout(
          certificationGeneration,
          sleepingStage,
          new Error(`Sleeping safety certification exceeded ${this.safetyReadbackTimeoutMs}ms.`),
        );
        resolve(null);
      }, this.safetyReadbackTimeoutMs);
    });
    const promise = Promise.race([certificationWork, timeout])
      .finally(() => {
        clearTimeout(timeoutHandle);
        if (this.sleepingCertificationPromise === promise) {
          this.sleepingCertificationPromise = null;
        }
      });
    this.sleepingCertificationPromise = promise;
    return promise;
  }

  readSleepingIslandsTelemetryAsync(
    renderer: any,
  ): Promise<SleepingIslandsTelemetry | null> {
    if (this.sleepingCertificationPromise) {
      // An explicit caller asks for a fresh certificate of the current state,
      // not the result of an older periodic probe. Invalidate only that async
      // generation; preserve a pending regression timeout injection.
      this.sleepingCertificationGeneration++;
      this.sleepingCertificationPromise = null;
      this.sleepingIslands?.rejectZeroWorkCertification();
    }
    // Explicit inspection is a queue-drained maintenance cycle outside
    // PhysicsEngine.step(), not work owned by the preceding animation frame.
    // Keep it out of the per-frame 2..5 submission ledger.
    return this.startSleepingZeroWorkCertification(renderer, undefined, false);
  }

  isCcdEnabled(): boolean {
    return this.contactGeneration?.isCcdEnabled?.() ?? (this.config.enableCcd ?? true);
  }

  isCcdDiagnosticsEnabled(): boolean {
    return this.contactGeneration?.isCcdDiagnosticsEnabled?.()
      ?? ((this.config.enableCcd ?? true) && this.config.enableCcdDiagnostics === true);
  }

  readCcdDiagnosticsAsync(renderer: any): Promise<CcdDiagnostics | null> {
    if (!this.contactGeneration) return Promise.resolve(null);
    return this.contactGeneration.readCcdDiagnosticsAsync(renderer);
  }

  getSubmissionSnapshot(frameId = this.frameId): PhysicsSubmissionSnapshot {
    return this.submissionLedger.snapshot(frameId);
  }

  getSubstepBroadphaseRefreshTelemetry(): SubstepBroadphaseRefreshTelemetry {
    return Object.freeze({
      attempts: this.substepBroadphaseRefreshAttempts,
      successes: this.substepBroadphaseRefreshSuccesses,
      failures: this.substepBroadphaseRefreshFailures,
      attemptsThisFrame: this.substepBroadphaseRefreshAttemptsThisFrame,
      failuresThisFrame: this.substepBroadphaseRefreshFailuresThisFrame,
      lastFrameId: this.substepBroadphaseRefreshLastFrameId,
      lastFixedStepId: this.substepBroadphaseRefreshLastFixedStepId,
      lastSubstepIndex: this.substepBroadphaseRefreshLastSubstepIndex,
      lastFailureReason: this.substepBroadphaseRefreshLastFailureReason,
      stickyFailure: this.substepBroadphaseRefreshStickyFailure,
    });
  }

  /** CPU-only debug-cache telemetry; reading it never performs a body scan. */
  getSupportFloorDebugCacheTelemetry(): SupportFloorDebugCacheTelemetry {
    return Object.freeze({
      scanCount: this.supportFloorDebugScanCount,
      cacheHits: this.supportFloorDebugCacheHits,
      invalidations: this.supportFloorDebugInvalidations,
      valid: this.supportFloorDebugCacheValid,
      body: this.supportFloorDebugCacheValid ? this.supportFloorDebugBody : -1,
      topY: this.supportFloorDebugCacheValid && Number.isFinite(this.supportFloorDebugTopY)
        ? this.supportFloorDebugTopY
        : null,
    });
  }

  /**
   * Regression-only probe that executes the exact lookup used by each debug
   * substep without initializing or submitting GPU work.
   */
  probeSupportFloorDebugCacheForTesting(lookups = 1): SupportFloorDebugCacheTelemetry {
    const boundedLookups = Math.max(0, Math.min(1_000_000, Math.floor(lookups)));
    for (let lookup = 0; lookup < boundedLookups; lookup++) {
      this.resolveSupportFloorDebugCache();
    }
    return this.getSupportFloorDebugCacheTelemetry();
  }

  getAllocationTelemetry(): PhysicsAllocationTelemetry {
    const allocation = this.pairContactsGpuAllocation;
    const logicalVec4Count = this.maxPairContacts * CONTACT_RECORD_VEC4S;
    const plannedPairContacts = this.capacityPlan.gpuBuffers.find(
      (buffer) => buffer.key === 'pairContacts',
    );
    if (plannedPairContacts === undefined) {
      throw new Error('Capacity preflight is missing the pairContacts GPU allocation.');
    }
    const actualGpuBytes = allocation?.buffer.size ?? 0;
    const plannedPersistentMap = this.capacityPlan.gpuBuffers.find(
      (buffer) => buffer.key === 'persistentManifoldMap',
    );
    if (plannedPersistentMap === undefined) {
      throw new Error('Capacity preflight is missing the persistentManifoldMap allocation.');
    }
    const persistentMapAllocation = this.persistentManifoldMapGpuAllocation;
    const persistentMapGpuBytes = persistentMapAllocation?.buffer.size
      ?? (this.initialized ? plannedPersistentMap.bytes : 0);
    const adjacencyBytes = this.contactOnlyAdjacencyAlias
      ? this.capacityPlan.derived.bodyConstraintRefsWordCount * Uint32Array.BYTES_PER_ELEMENT
      : 0;
    const constraintRecordStagingCpuBytes = this.jointRecordsData.byteLength
      + this.springRecordsData.byteLength;
    const capacityMatchesCurrentRecords = this.capacityPlan.derived.storageMaxJoints
      === this.storageMaxJoints
      && this.capacityPlan.derived.storageMaxSprings === this.storageMaxSprings;
    return {
      capacityPlan: {
        mode: this.capacityPlan.allocation.mode,
        cpuBytes: this.capacityPlan.totalCpuBytes,
        gpuBytes: this.capacityPlan.totalGpuBytes,
        totalBytes: this.capacityPlan.totalCentralBytes,
        largestGpuBuffer: {
          key: this.capacityPlan.largestGpuBuffer.key,
          label: this.capacityPlan.largestGpuBuffer.label,
          bytes: this.capacityPlan.largestGpuBuffer.bytes,
        },
        limitsChecked: this.capacityPlan.limitStatus.checked,
        limitsOk: this.capacityPlan.limitStatus.ok,
      },
      actualInstalled: {
        // The planner counts the one-vec4 GPU-attribute placeholders. Add the
        // separate live-only scene/reset staging arrays retained by the engine.
        cpuBytes: this.capacityPlan.totalCpuBytes + constraintRecordStagingCpuBytes,
        gpuBytes: this.capacityPlan.totalGpuBytes,
        totalBytes: this.capacityPlan.totalCentralBytes + constraintRecordStagingCpuBytes,
        constraintRecordStagingCpuBytes,
        matchesCurrentRecordCapacities: capacityMatchesCurrentRecords,
      },
      pairContacts: {
        gpuOnly: true,
        initialized: allocation !== undefined,
        destroyed: allocation?.destroyed ?? false,
        cpuBytes: allocation?.attribute.array.byteLength ?? this.pairContactsData.byteLength,
        gpuBytes: actualGpuBytes,
        expectedGpuBytes: plannedPairContacts.bytes,
        plannerGpuBytes: plannedPairContacts.bytes,
        gpuBytesMatchPlanner: allocation === undefined
          ? null
          : actualGpuBytes === plannedPairContacts.bytes,
        logicalVec4Count,
      },
      persistentContacts: {
        mode: this.contactPoolMode,
        descriptorCapacity: this.maxPersistentManifolds,
        logicalSlotRows: this.logicalMaxPairContacts,
        physicalContactRows: this.maxPairContacts,
        mapWords: this.capacityPlan.derived.persistentManifoldMapWordCount,
        mapGpuOnly: this.contactPoolMode === 'compact',
        initialized: this.contactPoolMode === 'compact'
          ? persistentMapAllocation !== undefined
          : this.initialized,
        destroyed: persistentMapAllocation?.destroyed ?? false,
        cpuBytes: persistentMapAllocation?.attribute.array.byteLength
          ?? this.persistentManifoldMapData.byteLength,
        gpuBytes: persistentMapGpuBytes,
        expectedGpuBytes: plannedPersistentMap.bytes,
        gpuBytesMatchPlanner: !this.initialized
          ? null
          : persistentMapGpuBytes === plannedPersistentMap.bytes,
        expectedGenerationMode: this.contactPoolMode === 'compact'
          ? COMPACT_CONTACT_POOL_GENERATION_MODE
          : 0,
      },
      constraintRecords: {
        joints: {
          logicalMax: this.maxJoints,
          live: this.jointCount,
          hardVelocityLive: this.hardJointVelocityConstraintCount,
          allocated: this.storageMaxJoints,
          generation: this.jointRecordGeneration,
          growthCount: this.jointRecordGrowthCount,
          retiredPending: this.jointRecordRetiredPending,
          retiredDestroyed: this.jointRecordRetiredDestroyed,
          gpuOnly: true,
          initialized: this.jointRecordsGpuAllocation !== undefined,
          cpuBytes: this.jointRecordsData.byteLength,
          gpuAttributeCpuBytes:
            this.jointRecordsGpuAllocation?.attribute.array.byteLength ?? 0,
          gpuBytes: this.jointRecordsGpuAllocation?.gpuByteLength ?? 0,
        },
        springs: {
          logicalMax: this.maxSprings,
          live: this.springCount,
          hardVelocityLive: this.hardSpringVelocityConstraintCount,
          allocated: this.storageMaxSprings,
          generation: this.springRecordGeneration,
          growthCount: this.springRecordGrowthCount,
          retiredPending: this.springRecordRetiredPending,
          retiredDestroyed: this.springRecordRetiredDestroyed,
          gpuOnly: true,
          initialized: this.springRecordsGpuAllocation !== undefined,
          cpuBytes: this.springRecordsData.byteLength,
          gpuAttributeCpuBytes:
            this.springRecordsGpuAllocation?.attribute.array.byteLength ?? 0,
          gpuBytes: this.springRecordsGpuAllocation?.gpuByteLength ?? 0,
        },
      },
      contactOnlyAdjacency: {
        aliasActive: this.contactOnlyAdjacencyAlias,
        cpuArrayAliased: this.bodyConstraintRefsData === this.pairBodyContactIndicesData,
        gpuAttributeAliased: this.initialized
          ? this.bodyConstraintRefsAttr === this.pairBodyContactIndicesAttr
          : null,
        savedCpuBytes: adjacencyBytes,
        savedGpuBytes: adjacencyBytes,
        savedBytes: adjacencyBytes * 2,
      },
      optionalStages: {
        sleepingTopologyCache: this.sleepingIslands?.getAllocationTelemetry() ?? null,
        restitution: this.restitutionStage?.getAllocationTelemetry() ?? null,
        velocityProjection: this.velocityConstraintProjection?.getAllocationTelemetry() ?? null,
      },
    };
  }

  getBroadphaseNativeSubmissionDiagnostics(): {
    ready: boolean;
    fallbackSubmits: number;
  } {
    return this.broadPhase?.getNativeSubmissionDiagnostics?.() ?? {
      ready: false,
      fallbackSubmits: 0,
    };
  }

  private clearPairContactsGpu(
    label: string,
    includeConstraintRecords = false,
    includeBroadphaseScratch = false,
  ): { pairActivityCleared: boolean; pairVisitedCleared: boolean } {
    const allocation = this.pairContactsGpuAllocation;
    if (!allocation || allocation.destroyed) {
      return { pairActivityCleared: false, pairVisitedCleared: false };
    }
    const encoder = this.device.createCommandEncoder({ label });
    encoder.clearBuffer(allocation.buffer, 0, allocation.gpuByteLength);
    const renderer = this.rendererRef;
    const rendererAttributes = renderer?._attributes;
    const rendererBackend = renderer?.backend;
    const rendererScratchClears: StorageBufferAttribute[] = [];
    let pairActivityCleared = false;
    let pairVisitedCleared = false;
    if (includeBroadphaseScratch) {
      // pairActivity is written by BroadPhaseStage through a native encoder,
      // then consumed by Three's ContactGeneration compute graph. A pending
      // BufferAttribute version must not cause Three to upload the CPU zero
      // array after the native candidate pass and erase that same-step list.
      // Clear the renderer-owned buffers in this topology submission and, once
      // submitted, acknowledge the exact version without scheduling a later
      // CPU upload. pairVisited is native broadphase scratch and follows the
      // same ownership rule.
      this.pairActivityAttr.needsUpdate = true;
      this.pairVisitedBitsAttr.needsUpdate = true;
      const pairActivityBuffer = rendererBackend?.get?.(this.pairActivityAttr)?.buffer as
        | GPUBuffer
        | undefined;
      const pairVisitedBuffer = rendererBackend?.get?.(this.pairVisitedBitsAttr)?.buffer as
        | GPUBuffer
        | undefined;
      const canAcknowledgeRendererVersion = typeof rendererAttributes?.get === 'function';
      if (pairActivityBuffer) {
        encoder.clearBuffer(pairActivityBuffer, 0, this.pairActivityData.byteLength);
        if (canAcknowledgeRendererVersion) {
          rendererScratchClears.push(this.pairActivityAttr);
          pairActivityCleared = true;
        }
      }
      if (pairVisitedBuffer) {
        encoder.clearBuffer(pairVisitedBuffer, 0, this.pairVisitedBitsData.byteLength);
        if (canAcknowledgeRendererVersion) {
          rendererScratchClears.push(this.pairVisitedBitsAttr);
          pairVisitedCleared = true;
        }
      }
    }
    const persistentMapAllocation = this.persistentManifoldMapGpuAllocation;
    if (persistentMapAllocation && !persistentMapAllocation.destroyed) {
      // ContactGeneration.reset() rebuilds slot sentinels and the compact ABI
      // on the next dispatch. Clearing here makes the reset observable
      // immediately and prevents stale allocator headers between those calls.
      encoder.clearBuffer(
        persistentMapAllocation.buffer,
        0,
        persistentMapAllocation.gpuByteLength,
      );
    }
    if (includeConstraintRecords) {
      const jointAllocation = this.jointRecordsGpuAllocation;
      const springAllocation = this.springRecordsGpuAllocation;
      if (jointAllocation && !jointAllocation.destroyed) {
        encoder.clearBuffer(jointAllocation.buffer, 0, jointAllocation.gpuByteLength);
      }
      if (springAllocation && !springAllocation.destroyed) {
        encoder.clearBuffer(springAllocation.buffer, 0, springAllocation.gpuByteLength);
      }
    }
    this.submissionLedger.withActualSubmitScope(
      this.device.queue,
      {
        frameId: this.frameId,
        submissionClass: 'topology',
        label,
      },
      () => this.device.queue.submit([encoder.finish()]),
    );
    for (const attribute of rendererScratchClears) {
      const attributeState = rendererAttributes?.get?.(attribute);
      if (attributeState) {
        attributeState.version = attribute.version;
      }
      attribute.clearUpdateRanges?.();
    }
    this.submissionLedger.record('topology', label, {
      frameId: this.frameId,
    });
    return { pairActivityCleared, pairVisitedCleared };
  }

  private resetSubstepBroadphaseRefreshTelemetry(): void {
    this.substepBroadphaseRefreshAttempts = 0;
    this.substepBroadphaseRefreshSuccesses = 0;
    this.substepBroadphaseRefreshFailures = 0;
    this.substepBroadphaseRefreshAttemptsThisFrame = 0;
    this.substepBroadphaseRefreshFailuresThisFrame = 0;
    this.substepBroadphaseRefreshLastFrameId = -1;
    this.substepBroadphaseRefreshLastFixedStepId = -1;
    this.substepBroadphaseRefreshLastSubstepIndex = -1;
    this.substepBroadphaseRefreshLastFailureReason = null;
    this.substepBroadphaseRefreshStickyFailure = false;
    this.postContactSafetySampleOwnerFrameId = -1;
  }

  clearScene(): void {
    this.invalidateSleepingCertification();
    this.broadPhase?.reset?.();
    this.contactGeneration?.reset?.();
    this.constraintSafety?.reset?.();

    this.bodyCount = 0;
    this.dynamicBodyCount = 0;
    this.jointCount = 0;
    this.springCount = 0;
    this.hardVelocityConstraintCount = 0;
    this.hardJointVelocityConstraintCount = 0;
    this.hardSpringVelocityConstraintCount = 0;
    // The projection stage owns no raw allocation and is capacity-bound to
    // storage attributes that survive clearScene(). Retain its ComputeNode
    // identities so Reset cannot reintroduce first-step shader compilation.
    this.conservativeIslandCount = 0;
    this.sleepTopologyGeneration = (this.sleepTopologyGeneration + 1) >>> 0;

    this.jointRecordsData.fill(0.0);
    this.springRecordsData.fill(0.0);
    this.pairActivityData.fill(0);
    this.pairVisitedBitsData.fill(0);
    this.pairBodyContactCountsData.fill(0);
    this.pairBodyContactIndicesData.fill(0);
    this.bodyConstraintCountsData.fill(0);
    if (!this.contactOnlyAdjacencyAlias) this.bodyConstraintRefsData.fill(0);
    this.conservativeBodyFlagsData.fill(0);
    this.ignoredPairHashData.fill(0);
    if (this.contactPoolMode === 'legacy') this.persistentManifoldMapData.fill(0);
    this.ignoredPairCount = 0;
    this.pairColorBodyClaimsData.fill(0);

    this.accumulator = 0.0;
    this.frameId = 0;
    this.submissionLedger.reset();
    this.clearPairContactsGpu('physics-clear-scene-gpu-only-storage', true, true);
    this.hasAdvancedSimulation = false;
    this.invalidateBodyColoring();
    this.resetBodyColoringScheduleTelemetry();
    this.pairDispatchTruncationWarned = false;
    this.waitingForInitialCandidatePairs = true;
    this.resetSubstepBroadphaseRefreshTelemetry();
    this.supportDebugReadbackInFlight = false;
    this.lastSupportDebugLogFrame = -1;
    this.prevSupportBodyCount = -1;
    this.prevCandidatePairsForChurn = null;
    this.prevFloorCandidatePairsForChurn = null;
    this.invalidateSupportFloorDebugCache();
    this.sleepingReadbackInFlight = false;
    this.lastSleepingReadbackFrame = -1;

    this.stats.bodyCount = 0;
    this.stats.frameCount = 0;
    this.stats.simulationStepCount = 0;
    this.stats.totalMs = 0;
    this.stats.integrationMs = 0;
    this.stats.broadPhaseMs = 0;
    this.stats.solverMs = 0;
    this.stats.velocityUpdateMs = 0;
    this.stats.broadPhaseReady = false;
    this.stats.candidatePairsEnabled = false;
    this.stats.pairDispatchTruncated = false;
    this.stats.avbdIterationsLast = 0;
    this.stats.avbdIterationsAverage = 0;
    this.stats.avbdAdaptiveEarlyExits = 0;
    this.stats.sleepingBodies = 0;
    this.stats.sleepingIslands = 0;
    this.stats.sleepingFailOpen = false;
    this.stats.sleepingZeroWorkSteps = 0;

    if (!this.initialized) return;

    this.avbdState.resetAdaptiveIterationStats();
    this.sleepingIslands?.requestReset();

    this.pairBodyContactCountsAttr.needsUpdate = true;
    this.pairBodyContactIndicesAttr.needsUpdate = true;
    this.bodyConstraintCountsAttr.needsUpdate = true;
    if (!this.contactOnlyAdjacencyAlias) this.bodyConstraintRefsAttr.needsUpdate = true;
    this.conservativeBodyFlagsAttr.needsUpdate = true;
    this.ignoredPairHashAttr.needsUpdate = true;
    if (this.contactPoolMode === 'legacy') {
      this.persistentManifoldMapAttr.needsUpdate = true;
    }
    this.pairColorBodyClaimsAttr.needsUpdate = true;
  }

  resetSimulationToInitialPose(): void {
    if (this.bodyCount <= 0) return;
    this.invalidateSleepingCertification();
    this.invalidateSupportFloorDebugCache();
    // Invalidate pending sparse diagnostic readbacks before uploading a fresh
    // manifold map.  Otherwise an old scene generation can re-latch overflow
    // after this reset has already cleared the GPU/CPU contact state.
    this.contactGeneration?.reset?.();
    // Native broadphase fatal samples share the constraint-safety gate. Rotate
    // its staging-ring generation on every pose reset so a pending map from the
    // old scene cannot poison the newly cleared contact generation.
    this.broadPhase?.reset?.();
    this.constraintSafety?.reset?.();
    this.sleepTopologyGeneration = (this.sleepTopologyGeneration + 1) >>> 0;
    this.sleepingIslands?.requestReset();

    for (let i = 0; i < this.bodyCount; i++) {
      const bodyBase = i * 4;
      const poseBase = i * 8;
      const inertialBase = i * INERTIAL_POSE_FLOATS_PER_BODY;

      this.positionsData[bodyBase + 0] = this.initialPoseData[poseBase + 0];
      this.positionsData[bodyBase + 1] = this.initialPoseData[poseBase + 1];
      this.positionsData[bodyBase + 2] = this.initialPoseData[poseBase + 2];
      this.positionsData[bodyBase + 3] = this.initialPoseData[poseBase + 3];

      this.inertialPoseData[inertialBase + 0] = this.initialPoseData[poseBase + 0];
      this.inertialPoseData[inertialBase + 1] = this.initialPoseData[poseBase + 1];
      this.inertialPoseData[inertialBase + 2] = this.initialPoseData[poseBase + 2];
      this.inertialPoseData[inertialBase + 3] = this.initialPoseData[poseBase + 3];

      this.quaternionsData[bodyBase + 0] = this.initialPoseData[poseBase + 4];
      this.quaternionsData[bodyBase + 1] = this.initialPoseData[poseBase + 5];
      this.quaternionsData[bodyBase + 2] = this.initialPoseData[poseBase + 6];
      this.quaternionsData[bodyBase + 3] = this.initialPoseData[poseBase + 7];

      this.inertialPoseData[inertialBase + 4] = this.initialPoseData[poseBase + 4];
      this.inertialPoseData[inertialBase + 5] = this.initialPoseData[poseBase + 5];
      this.inertialPoseData[inertialBase + 6] = this.initialPoseData[poseBase + 6];
      this.inertialPoseData[inertialBase + 7] = this.initialPoseData[poseBase + 7];
      this.inertialPoseData[inertialBase + 8] = this.initialPoseData[poseBase + 0];
      this.inertialPoseData[inertialBase + 9] = this.initialPoseData[poseBase + 1];
      this.inertialPoseData[inertialBase + 10] = this.initialPoseData[poseBase + 2];
      this.inertialPoseData[inertialBase + 11] = this.initialPoseData[poseBase + 3];
      this.inertialPoseData[inertialBase + 12] = this.initialPoseData[poseBase + 4];
      this.inertialPoseData[inertialBase + 13] = this.initialPoseData[poseBase + 5];
      this.inertialPoseData[inertialBase + 14] = this.initialPoseData[poseBase + 6];
      this.inertialPoseData[inertialBase + 15] = this.initialPoseData[poseBase + 7];
      this.inertialPoseData[inertialBase + 16] = 0.0;
      this.inertialPoseData[inertialBase + 17] = 0.0;
      this.inertialPoseData[inertialBase + 18] = 0.0;
      this.inertialPoseData[inertialBase + 19] = 0.0;

      this.velocitiesData[bodyBase + 0] = this.initialLinearVelData[bodyBase + 0];
      this.velocitiesData[bodyBase + 1] = this.initialLinearVelData[bodyBase + 1];
      this.velocitiesData[bodyBase + 2] = this.initialLinearVelData[bodyBase + 2];
      this.velocitiesData[bodyBase + 3] = this.initialLinearVelData[bodyBase + 3];

      this.prevLinearVelData[bodyBase + 0] = this.initialLinearVelData[bodyBase + 0];
      this.prevLinearVelData[bodyBase + 1] = this.initialLinearVelData[bodyBase + 1];
      this.prevLinearVelData[bodyBase + 2] = this.initialLinearVelData[bodyBase + 2];
      this.prevLinearVelData[bodyBase + 3] = this.initialLinearVelData[bodyBase + 3];

      this.angularVelData[bodyBase + 0] = this.initialAngularVelData[bodyBase + 0];
      this.angularVelData[bodyBase + 1] = this.initialAngularVelData[bodyBase + 1];
      this.angularVelData[bodyBase + 2] = this.initialAngularVelData[bodyBase + 2];
      this.angularVelData[bodyBase + 3] = this.initialAngularVelData[bodyBase + 3];
    }

    // The full contact cache is GPU-only and is cleared below with a topology
    // submission after the submission ledger is reset.
    for (let jointIndex = 0; jointIndex < this.jointCount; jointIndex++) {
      const c0LinBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_C0_LIN_OFFSET);
      const c0AngBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_C0_ANG_OFFSET);
      const lambdaLinBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_LAMBDA_LIN_OFFSET);
      const lambdaAngBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_LAMBDA_ANG_OFFSET);
      const penaltyLinBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_PENALTY_LIN_OFFSET);
      const penaltyAngBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_PENALTY_ANG_OFFSET);
      this.jointRecordsData.fill(0.0, c0LinBase, c0LinBase + 4);
      this.jointRecordsData.fill(0.0, c0AngBase, c0AngBase + 4);
      this.jointRecordsData.fill(0.0, lambdaLinBase, lambdaLinBase + 4);
      this.jointRecordsData.fill(0.0, lambdaAngBase, lambdaAngBase + 4);
      this.jointRecordsData.fill(0.0, penaltyLinBase, penaltyLinBase + 4);
      this.jointRecordsData.fill(0.0, penaltyAngBase, penaltyAngBase + 4);
    }
    for (let springIndex = 0; springIndex < this.springCount; springIndex++) {
      const anchorABase = springRecordVec4FloatIndex(springIndex, SPRING_RECORD_ANCHOR_A_OFFSET);
      const anchorBBase = springRecordVec4FloatIndex(springIndex, SPRING_RECORD_ANCHOR_B_OFFSET);
      const stateBase = springRecordVec4FloatIndex(springIndex, SPRING_RECORD_STATE_OFFSET);
      const restLength = this.springRecordsData[anchorABase + 3] ?? 0.0;
      const materialStiffness = Math.max(this.springRecordsData[anchorBBase + 3] ?? 0.0, 0.0);
      this.springRecordsData[stateBase + 0] = materialStiffness > 0.0
        ? Math.min(materialStiffness, this.avbdPenaltyFloor)
        : 0.0;
      this.springRecordsData[stateBase + 1] = 0.0;
      this.springRecordsData[stateBase + 2] = restLength;
      this.springRecordsData[stateBase + 3] = 0.0;
    }
    this.pairCandidateIndicesData.fill(0);
    this.pairActiveCandidateSlotsData.fill(0);
    this.pairActiveContactsData.fill(0);
    this.pairVisitedBitsData.fill(0);
    this.pairBodyContactCountsData.fill(0);
    this.pairBodyContactIndicesData.fill(0);
    this.bodyConstraintCountsData.fill(0);
    const resetSeedColorCount = Math.max(
      1,
      Math.min(32, this.activePairSolveColorCount),
    );
    for (let body = 0; body < this.bodyCount; body++) {
      this.bodyConstraintCountsData[body] = (body % resetSeedColorCount) << 16;
    }
    if (!this.contactOnlyAdjacencyAlias) this.bodyConstraintRefsData.fill(0);
    this.pairColorBodyClaimsData.fill(0);
    if (this.contactPoolMode === 'legacy') this.persistentManifoldMapData.fill(0);
    this.accumulator = 0.0;
    this.frameId = 0;
    this.submissionLedger.reset();
    this.clearPairContactsGpu(
      'physics-reset-pair-contacts',
      false,
      this.initialized,
    );
    if (this.jointCount > 0 && this.jointRecordsGpuAllocation) {
      writeFloat32Range(
        this.device.queue,
        this.jointRecordsGpuAllocation.buffer,
        0,
        this.jointRecordsData.subarray(0, this.jointCount * JOINT_RECORD_FLOATS),
      );
    }
    if (this.springCount > 0 && this.springRecordsGpuAllocation) {
      writeFloat32Range(
        this.device.queue,
        this.springRecordsGpuAllocation.buffer,
        0,
        this.springRecordsData.subarray(0, this.springCount * SPRING_RECORD_FLOATS),
      );
    }
    this.hasAdvancedSimulation = false;
    this.invalidateBodyColoring();
    this.resetBodyColoringScheduleTelemetry();
    this.pairDispatchTruncationWarned = false;
    this.waitingForInitialCandidatePairs = this.requiresCandidatePairPath();
    this.resetSubstepBroadphaseRefreshTelemetry();
    this.supportDebugReadbackInFlight = false;
    this.lastSupportDebugLogFrame = -1;
    this.prevSupportBodyCount = -1;
    this.prevCandidatePairsForChurn = null;
    this.prevFloorCandidatePairsForChurn = null;

    this.stats.totalMs = 0;
    this.stats.frameCount = 0;
    this.stats.simulationStepCount = 0;
    this.stats.integrationMs = 0;
    this.stats.broadPhaseMs = 0;
    this.stats.solverMs = 0;
    this.stats.velocityUpdateMs = 0;
    this.stats.broadPhaseReady = false;
    this.stats.candidatePairsEnabled = false;
    this.stats.pairDispatchTruncated = false;
    this.stats.sleepingBodies = 0;
    this.stats.sleepingIslands = 0;
    this.stats.sleepingFailOpen = false;
    this.stats.sleepingZeroWorkSteps = 0;
    this.sleepingReadbackInFlight = false;
    this.lastSleepingReadbackFrame = -1;

    if (!this.initialized) return;

    const bodyVec4Words = this.bodyCount * 4;
    this.publishRendererStorageRange(
      this.positionsAttr,
      this.positionsData,
      0,
      bodyVec4Words,
    );
    this.publishRendererStorageRange(
      this.initialPoseAttr,
      this.initialPoseData,
      0,
      this.bodyCount * 8,
    );
    this.publishRendererStorageRange(
      this.inertialPoseAttr,
      this.inertialPoseData,
      0,
      this.bodyCount * INERTIAL_POSE_FLOATS_PER_BODY,
    );
    this.publishRendererStorageRange(
      this.velocitiesAttr,
      this.velocitiesData,
      0,
      bodyVec4Words,
    );
    this.publishRendererStorageRange(
      this.prevLinearVelAttr,
      this.prevLinearVelData,
      0,
      bodyVec4Words,
    );
    this.publishRendererStorageRange(
      this.quaternionsAttr,
      this.quaternionsData,
      0,
      bodyVec4Words,
    );
    this.publishRendererStorageRange(
      this.angularVelAttr,
      this.angularVelData,
      0,
      bodyVec4Words,
    );

    this.pairBodyContactCountsAttr.needsUpdate = true;
    this.pairBodyContactIndicesAttr.needsUpdate = true;
    this.bodyConstraintCountsAttr.needsUpdate = true;
    if (!this.contactOnlyAdjacencyAlias) this.bodyConstraintRefsAttr.needsUpdate = true;
    if (this.contactPoolMode === 'legacy') {
      this.persistentManifoldMapAttr.needsUpdate = true;
    }
    this.pairColorBodyClaimsAttr.needsUpdate = true;
  }

  private synchronizeGravityMutation(): void {
    const current: [number, number, number] = [
      Math.fround(this.config.gravity[0]),
      Math.fround(this.config.gravity[1]),
      Math.fround(this.config.gravity[2]),
    ];
    const changed = current.some(
      (value, axis) => !Object.is(value, this.observedGravityF32[axis]),
    );
    if (!changed) return;
    this.observedGravityF32 = current;
    this.sleepingIslands?.requestWakeAll();
  }

  setGravity(gravity: readonly [number, number, number]): void {
    this.config.gravity[0] = gravity[0];
    this.config.gravity[1] = gravity[1];
    this.config.gravity[2] = gravity[2];
    this.synchronizeGravityMutation();
  }

  setDeltaTime(deltaTime: number): void {
    const clamped = Math.max(1 / 500, Math.min(1 / 10, deltaTime));
    this.config.deltaTime = clamped;
    this.accumulator = Math.min(this.accumulator, clamped);
  }

  setSubsteps(substeps: number): void {
    const clamped = Math.max(1, Math.min(8, Math.floor(substeps)));
    this.config.substeps = clamped;
  }

  getSubsteps(): number {
    return this.config.substeps;
  }

  setCollisionMargin(collisionMargin: number): void {
    this.collisionMargin = Math.max(0.0, collisionMargin);
    this.contactGeneration?.setCollisionMargin?.(this.collisionMargin);
    this.avbdState?.setCollisionMargin?.(this.collisionMargin);
    this.sleepingIslands?.requestWakeAll();
  }

  getCollisionMargin(): number {
    return this.collisionMargin;
  }

  setContactRestitution(
    restitution: number,
    iterations = this.restitutionIterations,
    velocityThreshold = this.restitutionVelocityThreshold,
  ): void {
    this.contactRestitution = Math.max(0.0, Math.min(1.0, restitution));
    this.restitutionIterations = Math.max(0, Math.min(128, Math.floor(iterations)));
    this.restitutionVelocityThreshold = Math.max(0.0, velocityThreshold);
    this.sleepingIslands?.requestWakeAll();
    // Scratch creation needs a materialized renderer backend. The optional
    // stage is therefore created lazily by the next step after this toggle.
  }

  getContactRestitution(): number {
    return this.contactRestitution;
  }

  getRestitutionIterations(): number {
    return this.restitutionIterations;
  }

  getRestitutionVelocityThreshold(): number {
    return this.restitutionVelocityThreshold;
  }

  getPairManifoldSlots(): number {
    return this.pairManifoldSlots;
  }

  setAvbdPairSweeps(sweeps: number): void {
    this.avbdPairSweeps = Math.max(1, Math.min(4, Math.floor(sweeps)));
  }

  getAvbdPairSweeps(): number {
    return this.avbdPairSweeps;
  }

  getAvbdNativeCommandCollectorPolicy(): AvbdNativeCommandCollectorPolicy {
    return getAvbdNativeCommandCollectorPolicy(
      this.avbdBodySolveMode,
      this.avbdPairSweeps,
      {
        restitutionActive: this.contactRestitution > 0.0 && this.restitutionIterations > 0,
        jointCount: this.jointCount,
        springCount: this.springCount,
      },
    );
  }

  setPairSolveColorCount(colorCount: number): void {
    const clamped = Math.max(1, Math.min(this.maxPairSolveColorCount, Math.floor(colorCount)));
    if (clamped !== this.activePairSolveColorCount) {
      this.invalidateBodyColoring();
    }
    this.activePairSolveColorCount = clamped;
  }

  setSolverIterations(iterations: number): void {
    const clamped = Math.max(1, Math.min(64, Math.floor(iterations)));
    this.solverIterations = clamped;
  }

  getSolverIterations(): number {
    return this.solverIterations;
  }

  getAvbdAdaptiveIterationsConfigured(): boolean {
    return this.avbdAdaptiveIterationsConfigured;
  }

  /**
   * Switches the nonlinear-round policy without rebuilding solver buffers.
   * The next solve republishes this value to the GPU-resident adaptive gate;
   * disabling it therefore guarantees the configured iteration count runs.
   */
  setAvbdAdaptiveIterationsConfigured(enabled: boolean): void {
    this.avbdAdaptiveIterationsConfigured = Boolean(enabled);
  }

  /**
   * Concrete solve path selected by the most recent AvbdState invocation.
   * This is deliberately separate from the configured policy: an adaptive
   * policy may validly fall back to the fixed reference/generic kernels.
   */
  getAvbdAdaptiveSelectedLast(): boolean {
    return this.avbdState?.getSolvePathTelemetry?.().adaptiveSelectedLast ?? false;
  }

  getAvbdAdaptiveMinIterations(): number {
    return this.avbdAdaptiveMinIterations;
  }

  /** @deprecated Use the explicit linear/angular beta setters. */
  setAvbdDualUpdateBeta(beta: number): void {
    const clamped = Math.max(0.0, beta);
    this.avbdDualUpdateBeta = clamped;
    // Preserve the legacy one-knob behavior for existing callers. Profiles
    // use setAvbdDualUpdateBetas() so betaAng is never an implicit clamp.
    this.avbdAngularDualUpdateBeta = Math.min(
      clamped,
      AVBD_REFERENCE_ANGULAR_DUAL_UPDATE_BETA,
    );
    this.avbdState?.setDualUpdateBetas?.(
      this.avbdDualUpdateBeta,
      this.avbdAngularDualUpdateBeta,
    );
  }

  setAvbdLinearDualUpdateBeta(beta: number): void {
    this.avbdDualUpdateBeta = Math.max(0.0, beta);
    this.avbdState?.setDualUpdateBetas?.(
      this.avbdDualUpdateBeta,
      this.avbdAngularDualUpdateBeta,
    );
  }

  getAvbdDualUpdateBeta(): number {
    return this.avbdDualUpdateBeta;
  }

  getAvbdLinearDualUpdateBeta(): number {
    return this.avbdDualUpdateBeta;
  }

  setAvbdDualUpdateBetas(linearBeta: number, angularBeta: number): void {
    this.avbdDualUpdateBeta = Math.max(0.0, linearBeta);
    this.avbdAngularDualUpdateBeta = Math.max(0.0, angularBeta);
    this.avbdState?.setDualUpdateBetas?.(
      this.avbdDualUpdateBeta,
      this.avbdAngularDualUpdateBeta,
    );
  }

  setAvbdAngularDualUpdateBeta(beta: number): void {
    this.avbdAngularDualUpdateBeta = Math.max(0.0, beta);
    this.avbdState?.setDualUpdateBetas?.(
      this.avbdDualUpdateBeta,
      this.avbdAngularDualUpdateBeta,
    );
  }

  getAvbdAngularDualUpdateBeta(): number {
    return this.avbdAngularDualUpdateBeta;
  }

  setAvbdPreventPenetratingNormalDropout(enabled: boolean): void {
    this.avbdPreventPenetratingNormalDropout = Boolean(enabled);
    this.avbdState?.setPreventPenetratingNormalDropout?.(this.avbdPreventPenetratingNormalDropout);
  }

  getAvbdPreventPenetratingNormalDropout(): boolean {
    return this.avbdPreventPenetratingNormalDropout;
  }

  setAvbdBodySolveMode(mode: AvbdBodySolveMode): void {
    if (mode === 'colored' && this.avbdBodySolveMode !== 'colored') {
      this.invalidateBodyColoring();
    }
    this.avbdBodySolveMode = mode;
  }

  getAvbdBodySolveMode(): AvbdBodySolveMode {
    return this.avbdBodySolveMode;
  }

  setAvbdPenaltyDecayGamma(gamma: number): void {
    const clamped = Math.max(0.0, Math.min(1.0, gamma));
    this.avbdPenaltyDecayGamma = clamped;
    this.avbdState?.setPenaltyDecayGamma?.(clamped);
  }

  getAvbdPenaltyDecayGamma(): number {
    return this.avbdPenaltyDecayGamma;
  }

  setAvbdPenaltyFloor(kStart: number): void {
    const clamped = Math.max(1e-6, kStart);
    this.avbdPenaltyFloor = clamped;
    this.avbdState?.setPenaltyFloor?.(clamped);
  }

  getAvbdPenaltyFloor(): number {
    return this.avbdPenaltyFloor;
  }

  setAvbdRegularizationAlpha(alpha: number): void {
    this.avbdRegularizationAlpha = Math.max(0.0, Math.min(1.0, alpha));
  }

  getAvbdRegularizationAlpha(): number {
    return this.avbdRegularizationAlpha;
  }

  setAvbdFriction(friction: number): void {
    const clamped = Math.max(0.0, Math.min(2.0, friction));
    this.avbdFrictionStatic = clamped;
    const frictionWord = Math.round((clamped / 2.0) * 0x3fff) & 0x3fff;
    for (let body = 0; body < this.bodyCount; body++) {
      const base = body * 4;
      const currentMeta = this.shapesWordData[base] ?? 0;
      this.shapesWordData[base] = ((currentMeta & ~0x3fff) | frictionWord) >>> 0;
    }
    if (this.initialized) {
      this.shapesAttr.needsUpdate = true;
    }
    this.contactGeneration?.setFriction?.(1.0);
    this.avbdState?.setFriction?.(1.0, 1.0);
    this.sleepingIslands?.requestWakeAll();
  }

  getAvbdFriction(): number {
    return this.avbdFrictionStatic;
  }

  getRenderBuffers(): { positions: StorageBufferAttribute; quaternions: StorageBufferAttribute } | null {
    if (!this.initialized) return null;

    return {
      positions: this.positionsAttr,
      quaternions: this.quaternionsAttr,
    };
  }

  getSleepingSolverContactLayout(): {
    canonicalActiveContactsOffset: number;
    solverActiveContactsOffset: number;
    scratchCapacity: number;
    maxActivePairContacts: number;
    compactionEnabled: boolean;
  } {
    return {
      canonicalActiveContactsOffset: this.pairActiveContactsOffset,
      solverActiveContactsOffset: this.pairSolverActiveContactsOffset,
      scratchCapacity: this.maxCandidatePairs,
      maxActivePairContacts: this.maxActivePairContacts,
      compactionEnabled:
        this.pairSolverActiveContactsOffset !== this.pairActiveContactsOffset,
    };
  }

  /** CPU-only diagnostics; reading this never maps buffers or submits GPU work. */
  getSleepingComputeGroupDiagnostics(): SleepingComputeGroupDiagnostics | null {
    return this.sleepingIslands?.getComputeGroupDiagnostics() ?? null;
  }

  /** CPU-only certificate/usage counters for the exact all-sleeping no-op path. */
  getSleepingZeroWorkDiagnostics(): SleepingZeroWorkDiagnostics | null {
    return this.sleepingIslands?.getZeroWorkDiagnostics() ?? null;
  }

  /**
   * Invalidates an all-sleeping certificate and wakes every dynamic island on
   * the next ordered physics step. Besides runtime controls, this provides an
   * explicit way for performance certification to exercise settled AVBD work
   * instead of accidentally timing only the zero-submit idle shortcut.
   */
  requestWakeAllBodies(): void {
    this.sleepingIslands?.requestWakeAll();
  }

  getContactRenderBuffers(): ContactRenderBuffers | null {
    if (!this.initialized) return null;

    return {
      positions: this.positionsAttr,
      quaternions: this.quaternionsAttr,
      pairContacts: this.pairContactsAttr,
      pairActivity: this.pairActivityAttr,
      maxPairContacts: this.maxPairContacts,
      maxActivePairContacts: this.maxActivePairContacts,
      pairActivityWordCount: this.pairActivityWordCount,
      pairActiveContactsOffset: this.pairActiveContactsOffset,
    };
  }

  getSpringRenderBuffers(): SpringRenderBuffers | null {
    if (!this.initialized) return null;

    return {
      positions: this.positionsAttr,
      quaternions: this.quaternionsAttr,
      springRecords: this.springRecordsAttr,
      maxSprings: this.storageMaxSprings,
      generation: this.springRecordGeneration,
    };
  }

  getMaxActiveContactDebugPoints(): number {
    // The overlay is a diagnostic view, not solver storage. Size it for the
    // bodies that actually exist when the scene is built instead of reserving
    // maxBodies * maxContactsPerBody in three separate instance matrices.
    return Math.min(
      this.maxActivePairContacts,
      Math.max(1, this.bodyCount * this.maxContactsPerBodySolver),
    );
  }

  private ensureRestitutionStage(renderer: any): RestitutionStage {
    if (!this.restitutionStage) {
      this.restitutionStage = new RestitutionStage(
        this.pairContactsAttr,
        this.pairActivityAttr,
        this.pairBodyContactCountsAttr,
        this.pairBodyContactIndicesAttr,
        this.positionsAttr,
        this.quaternionsAttr,
        this.velocitiesAttr,
        this.angularVelAttr,
        this.inverseInertiaAttr,
        this.conservativeBodyFlagsAttr,
        this.config.maxBodies,
        this.maxPairContacts,
        this.maxContactsPerBodySolver,
        this.pairActiveContactsOffset,
        this.pairActivityWordCount,
        { renderer, device: this.device },
      );
    }
    return this.restitutionStage;
  }

  private ensureVelocityConstraintProjection(
    restitutionStage: RestitutionStage,
  ): VelocityConstraintProjectionStage {
    if (!this.velocityConstraintProjection) {
      this.velocityConstraintProjection = this.createVelocityConstraintProjection(
        this.jointRecordsAttr,
        this.springRecordsAttr,
        this.storageMaxJoints,
        this.storageMaxSprings,
        restitutionStage,
      );
    }
    return this.velocityConstraintProjection;
  }

  private maybePollSleepingTelemetry(renderer: any, maintenanceCycle = false): boolean {
    if (!this.sleepingIslands || this.sleepingReadbackInFlight) return false;
    if (this.frameId - this.lastSleepingReadbackFrame < 120) return false;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return false;
    // Start telemetry only on a frame with no earlier optional diagnostic.
    // Its completion may enqueue a second safety-gate proof; reserving both
    // slots keeps total physics-owned submits at or below five.
    if (
      !maintenanceCycle
      && this.submissionLedger.snapshot(this.frameId).totalOwnedSubmitIntents > 2
    ) {
      this.sleepingTelemetryMaintenancePending = true;
      return false;
    }
    this.sleepingTelemetryMaintenancePending = false;
    this.lastSleepingReadbackFrame = this.frameId;
    this.sleepingReadbackInFlight = true;
    const telemetryTopologyGeneration = this.sleepTopologyGeneration;
    this.submissionLedger.record('diagnostic', 'sleeping-island-telemetry-readback', {
      frameId: this.frameId,
    });
    const telemetryPromise = this.submissionLedger.withActualSubmitScope(
      this.device.queue,
      {
        frameId: this.frameId,
        submissionClass: 'diagnostic',
        label: 'sleeping-island-telemetry-readback',
      },
      () => this.sleepingIslands!.readTelemetryAsync(renderer),
    );
    void telemetryPromise
      .then((telemetry) => {
        if (!telemetry || this.disposed) return;
        this.stats.sleepingBodies = telemetry.sleepingBodies;
        this.stats.sleepingIslands = telemetry.sleepingIslands;
        this.stats.sleepingFailOpen = telemetry.failOpen;
        if (
          this.sleepingIslands?.telemetryMayCertifyZeroWork(telemetry)
          && this.sleepingCertificationPromise === null
          && telemetryTopologyGeneration === this.sleepTopologyGeneration
        ) {
          // Reuse this queue-ordered telemetry and map only the two-word shared
          // fatal gate. The Promise callback publishes CPU state only; a later
          // observed idle tick services the actual queue work so it cannot
          // escape whole-frame submit accounting or the in-flight fence.
          this.pendingSleepingCertificationTelemetry = telemetry;
        }
      })
      .catch((error) => {
        if (this.debugBehaviorEnabled) {
          console.warn('Sleeping-island telemetry readback failed:', error);
        }
      })
      .finally(() => {
        this.sleepingReadbackInFlight = false;
      });
    return true;
  }

  setDebugBehaviorEnabled(enabled: boolean): void {
    this.debugBehaviorEnabled = enabled;
    this.broadPhase?.setDebugEnabled?.(enabled);
    this.contactGeneration?.setDebugEnabled?.(enabled);
    this.avbdState?.setDebugEnabled?.(enabled);
    this.supportDebugReadbackInFlight = false;
    this.lastSupportDebugLogFrame = -1;
    this.prevSupportBodyCount = -1;
    this.prevCandidatePairsForChurn = null;
    this.prevFloorCandidatePairsForChurn = null;
    if (!enabled) {
      this.contactGeneration?.setFloorDebugBody?.(-1);
    }
  }

  setDebugEnabled(enabled: boolean): void {
    this.setDebugBehaviorEnabled(enabled);
  }

  setDebugLogEveryFrame(enabled: boolean): void {
    const interval = enabled ? 1 : 30;
    this.debugLogEveryNFrames = interval;
    this.supportDebugEveryNFrames = interval;
    this.lastSupportDebugLogFrame = -1;
    this.broadPhase?.setDebugLogInterval?.(interval);
    this.contactGeneration?.setDebugLogInterval?.(interval);
    this.avbdState?.setDebugLogInterval?.(interval);
  }

  applyPlayerControl(
    renderer: any,
    config: {
      bodyIndex: number;
      targetVelocity: [number, number, number];
      moveGain?: number;
      jumpSpeed?: number;
      jumpRequested?: boolean;
      groundedHint?: boolean;
    },
  ): void {
    if (!this.initialized || !this.playerControl) return;
    if (config.bodyIndex < 0 || config.bodyIndex >= this.bodyCount) return;
    this.sleepingIslands?.requestLocalMutation();
    // The control kernel changes only this body's velocity. The next sleeping
    // pass observes that velocity as a local wake seed and wakes its connected
    // island without touching disconnected stacks.

    this.playerControl.dispatchControl(
      renderer,
      this.bodyCount,
      config.bodyIndex,
      config.targetVelocity,
      config.moveGain ?? 0.4,
      config.jumpSpeed ?? 6.0,
      config.jumpRequested ?? false,
      config.groundedHint ?? false,
    );
  }

  async readPlayerStateAsync(renderer: any, bodyIndex: number): Promise<PlayerProbeState | null> {
    if (!this.initialized || !this.playerControl) return null;
    if (bodyIndex < 0 || bodyIndex >= this.bodyCount) return null;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return null;

    this.playerControl.dispatchProbe(renderer, this.bodyCount, bodyIndex);

    const rawBuffer = await renderer.getArrayBufferAsync(this.playerControl.getProbeAttribute());
    const values = new Float32Array(rawBuffer);
    if (values.length < 8) return null;

    return {
      position: [values[0], values[1], values[2]],
      velocity: [values[4], values[5], values[6]],
      grounded: values[3] > 0.5,
    };
  }

  async readRigidBodyStatesAsync(renderer: any): Promise<RigidBodyReadbackState[] | null> {
    if (!this.initialized || !this.hasAdvancedSimulation) return null;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return null;

    let positionsRaw: ArrayBuffer;
    let quaternionsRaw: ArrayBuffer;
    let velocitiesRaw: ArrayBuffer;
    let angularVelocitiesRaw: ArrayBuffer;
    try {
      positionsRaw = await renderer.getArrayBufferAsync(this.positionsAttr);
    } catch (error) {
      throw new Error(`positions readback failed: ${error instanceof Error ? error.message : String(error)}`);
    }
    try {
      quaternionsRaw = await renderer.getArrayBufferAsync(this.quaternionsAttr);
    } catch (error) {
      throw new Error(`quaternions readback failed: ${error instanceof Error ? error.message : String(error)}`);
    }
    try {
      velocitiesRaw = await renderer.getArrayBufferAsync(this.velocitiesAttr);
    } catch (error) {
      throw new Error(`velocities readback failed: ${error instanceof Error ? error.message : String(error)}`);
    }
    try {
      angularVelocitiesRaw = await renderer.getArrayBufferAsync(this.angularVelAttr);
    } catch (error) {
      throw new Error(`angular velocities readback failed: ${error instanceof Error ? error.message : String(error)}`);
    }

    const positions = new Float32Array(positionsRaw);
    const quaternions = new Float32Array(quaternionsRaw);
    const velocities = new Float32Array(velocitiesRaw);
    const angularVelocities = new Float32Array(angularVelocitiesRaw);
    const states: RigidBodyReadbackState[] = [];

    for (let body = 0; body < this.bodyCount; body++) {
      const bodyBase = body * 4;
      const poseBase = body * 8;
      states.push({
        body,
        position: [
          positions[bodyBase] ?? 0.0,
          positions[bodyBase + 1] ?? 0.0,
          positions[bodyBase + 2] ?? 0.0,
        ],
        initialPosition: [
          this.initialPoseData[poseBase] ?? 0.0,
          this.initialPoseData[poseBase + 1] ?? 0.0,
          this.initialPoseData[poseBase + 2] ?? 0.0,
        ],
        quaternion: [
          quaternions[bodyBase] ?? 0.0,
          quaternions[bodyBase + 1] ?? 0.0,
          quaternions[bodyBase + 2] ?? 0.0,
          quaternions[bodyBase + 3] ?? 1.0,
        ],
        velocity: [
          velocities[bodyBase] ?? 0.0,
          velocities[bodyBase + 1] ?? 0.0,
          velocities[bodyBase + 2] ?? 0.0,
        ],
        angularVelocity: [
          angularVelocities[bodyBase] ?? 0.0,
          angularVelocities[bodyBase + 1] ?? 0.0,
          angularVelocities[bodyBase + 2] ?? 0.0,
        ],
        inverseMass: positions[bodyBase + 3] ?? 0.0,
      });
    }

    return states;
  }

  async readJointStatesAsync(renderer: any, jointIndices?: readonly number[]): Promise<JointReadbackState[] | null> {
    if (!this.initialized) return null;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return null;

    let jointRecordsRaw: ArrayBuffer;
    try {
      jointRecordsRaw = await renderer.getArrayBufferAsync(this.jointRecordsAttr);
    } catch (error) {
      throw new Error(`joint records readback failed: ${error instanceof Error ? error.message : String(error)}`);
    }

    const jointRecords = new Float32Array(jointRecordsRaw);
    const jointWords = new Uint32Array(jointRecordsRaw);
    const requested = jointIndices ?? Array.from({ length: this.jointCount }, (_, index) => index);
    const states: JointReadbackState[] = [];

    for (const jointIndex of requested) {
      if (jointIndex < 0 || jointIndex >= this.jointCount) continue;

      const metaBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_META_OFFSET);
      if ((jointWords[metaBase + 3] ?? 0) === 0) continue;

      const bodyAWord = jointWords[metaBase] ?? WORLD_BODY_INDEX;
      const bodyBWord = jointWords[metaBase + 1] ?? 0;
      const jointTypeWord = jointWords[metaBase + 2] ?? 0;

      const anchorABase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_ANCHOR_A_OFFSET);
      const anchorBBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_ANCHOR_B_OFFSET);
      const restBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_REST_RELATIVE_ROTATION_OFFSET);
      const stiffnessBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_STIFFNESS_OFFSET);
      const c0LinBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_C0_LIN_OFFSET);
      const c0AngBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_C0_ANG_OFFSET);
      const lambdaLinBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_LAMBDA_LIN_OFFSET);
      const lambdaAngBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_LAMBDA_ANG_OFFSET);
      const penaltyLinBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_PENALTY_LIN_OFFSET);
      const penaltyAngBase = jointRecordVec4FloatIndex(jointIndex, JOINT_RECORD_PENALTY_ANG_OFFSET);

      states.push({
        joint: jointIndex,
        bodyA: bodyAWord === WORLD_BODY_INDEX ? null : bodyAWord,
        bodyB: bodyBWord,
        type: jointTypeWord === 1 ? 'fixed' : 'spherical',
        anchorA: [
          jointRecords[anchorABase] ?? 0.0,
          jointRecords[anchorABase + 1] ?? 0.0,
          jointRecords[anchorABase + 2] ?? 0.0,
        ],
        anchorB: [
          jointRecords[anchorBBase] ?? 0.0,
          jointRecords[anchorBBase + 1] ?? 0.0,
          jointRecords[anchorBBase + 2] ?? 0.0,
        ],
        torqueArm: jointRecords[anchorABase + 3] ?? 0.0,
        restRelative: [
          jointRecords[restBase] ?? 0.0,
          jointRecords[restBase + 1] ?? 0.0,
          jointRecords[restBase + 2] ?? 0.0,
          jointRecords[restBase + 3] ?? 1.0,
        ],
        stiffnessLin: jointRecords[stiffnessBase] ?? 0.0,
        stiffnessAng: jointRecords[stiffnessBase + 1] ?? 0.0,
        c0Lin: [
          jointRecords[c0LinBase] ?? 0.0,
          jointRecords[c0LinBase + 1] ?? 0.0,
          jointRecords[c0LinBase + 2] ?? 0.0,
        ],
        c0Ang: [
          jointRecords[c0AngBase] ?? 0.0,
          jointRecords[c0AngBase + 1] ?? 0.0,
          jointRecords[c0AngBase + 2] ?? 0.0,
        ],
        lambdaLin: [
          jointRecords[lambdaLinBase] ?? 0.0,
          jointRecords[lambdaLinBase + 1] ?? 0.0,
          jointRecords[lambdaLinBase + 2] ?? 0.0,
        ],
        lambdaAng: [
          jointRecords[lambdaAngBase] ?? 0.0,
          jointRecords[lambdaAngBase + 1] ?? 0.0,
          jointRecords[lambdaAngBase + 2] ?? 0.0,
        ],
        penaltyLin: [
          jointRecords[penaltyLinBase] ?? 0.0,
          jointRecords[penaltyLinBase + 1] ?? 0.0,
          jointRecords[penaltyLinBase + 2] ?? 0.0,
        ],
        penaltyAng: [
          jointRecords[penaltyAngBase] ?? 0.0,
          jointRecords[penaltyAngBase + 1] ?? 0.0,
          jointRecords[penaltyAngBase + 2] ?? 0.0,
        ],
      });
    }

    return states;
  }

  async readRestitutionDebugStatesAsync(renderer: any): Promise<Array<{
    contact: number;
    target: number;
    lambda: number;
    deltaLambda: number;
    storedEpoch: number;
  }> | null> {
    if (!this.initialized || !this.restitutionStage) return null;
    return this.restitutionStage.readActiveDebugStates(renderer);
  }

  async readSpringStatesAsync(renderer: any, springIndices?: readonly number[]): Promise<SpringReadbackState[] | null> {
    if (!this.initialized) return null;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return null;

    let springRecordsRaw: ArrayBuffer;
    try {
      springRecordsRaw = await renderer.getArrayBufferAsync(this.springRecordsAttr);
    } catch (error) {
      throw new Error(`spring records readback failed: ${error instanceof Error ? error.message : String(error)}`);
    }

    const springRecords = new Float32Array(springRecordsRaw);
    const springWords = new Uint32Array(springRecordsRaw);
    const requested = springIndices ?? Array.from({ length: this.springCount }, (_, index) => index);
    const states: SpringReadbackState[] = [];

    for (const springIndex of requested) {
      if (springIndex < 0 || springIndex >= this.springCount) continue;

      const metaBase = springRecordVec4FloatIndex(springIndex, SPRING_RECORD_META_OFFSET);
      if ((springWords[metaBase + 2] ?? 0) === 0) continue;

      const bodyAWord = springWords[metaBase] ?? WORLD_BODY_INDEX;
      const bodyBWord = springWords[metaBase + 1] ?? 0;
      const anchorABase = springRecordVec4FloatIndex(springIndex, SPRING_RECORD_ANCHOR_A_OFFSET);
      const anchorBBase = springRecordVec4FloatIndex(springIndex, SPRING_RECORD_ANCHOR_B_OFFSET);
      const stateBase = springRecordVec4FloatIndex(springIndex, SPRING_RECORD_STATE_OFFSET);

      states.push({
        spring: springIndex,
        bodyA: bodyAWord === WORLD_BODY_INDEX ? null : bodyAWord,
        bodyB: bodyBWord,
        restLength: springRecords[anchorABase + 3] ?? 0.0,
        materialStiffness: (springWords[metaBase + 3] ?? 0) !== 0
          ? Number.POSITIVE_INFINITY
          : springRecords[anchorBBase + 3] ?? 0.0,
        currentPenalty: springRecords[stateBase] ?? 0.0,
        constraint: springRecords[stateBase + 1] ?? 0.0,
        currentLength: springRecords[stateBase + 2] ?? 0.0,
        penaltyIncrement: (springWords[metaBase + 3] ?? 0) === 0
          ? springRecords[stateBase + 3] ?? 0.0
          : 0.0,
        lambda: (springWords[metaBase + 3] ?? 0) !== 0
          ? springRecords[stateBase + 3] ?? 0.0
          : 0.0,
      });
    }

    return states;
  }

  async readActiveContactPointsAsync(renderer: any): Promise<DebugContactPoint[] | null> {
    if (!this.initialized) return null;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return null;

    const [
      activityRaw,
      pairContactsRaw,
      quaternionsRaw,
      positionsRaw,
    ] = await Promise.all([
      renderer.getArrayBufferAsync(this.pairActivityAttr),
      renderer.getArrayBufferAsync(this.pairContactsAttr),
      renderer.getArrayBufferAsync(this.quaternionsAttr),
      renderer.getArrayBufferAsync(this.positionsAttr),
    ]);

    const activity = new Uint32Array(activityRaw);
    const activeContacts = activity.subarray(
      this.pairActiveContactsOffset,
      this.pairActiveContactsOffset + this.maxActivePairContacts + CANDIDATE_LIST_HEADER_WORDS,
    );
    const { meta, arms } = decodePairContactViews(pairContactsRaw, this.maxPairContacts);
    const quaternions = new Float32Array(quaternionsRaw);
    const positions = new Float32Array(positionsRaw);

    const normalizeQuat = (x: number, y: number, z: number, w: number): [number, number, number, number] => {
      const len = Math.hypot(x, y, z, w);
      if (len <= 1e-12) return [0.0, 0.0, 0.0, 1.0];
      const inv = 1.0 / len;
      return [x * inv, y * inv, z * inv, w * inv];
    };
    const rotateVecByQuat = (
      qx: number, qy: number, qz: number, qw: number,
      vx: number, vy: number, vz: number,
    ): [number, number, number] => {
      const tx = 2.0 * (qy * vz - qz * vy);
      const ty = 2.0 * (qz * vx - qx * vz);
      const tz = 2.0 * (qx * vy - qy * vx);
      return [
        vx + qw * tx + (qy * tz - qz * ty),
        vy + qw * ty + (qz * tx - qx * tz),
        vz + qw * tz + (qx * ty - qy * tx),
      ];
    };

    const activeListCount = Math.min(activeContacts[0] ?? 0, this.maxActivePairContacts);
    const points: DebugContactPoint[] = [];
    for (let k = 0; k < activeListCount; k++) {
      const p = activeContacts[k + 1] ?? this.maxPairContacts;
      if (p >= this.maxPairContacts) continue;

      const metaBase = p * 4;
      if ((meta[metaBase + 2] ?? 0.0) < 0.5) continue;

      const i = Math.round(meta[metaBase] ?? -1);
      const j = Math.round(meta[metaBase + 1] ?? -1);
      if (i < 0 || j < 0) continue;

      const iBase = i * 4;
      const jBase = j * 4;
      const iPosX = positions[iBase] ?? 0.0;
      const iPosY = positions[iBase + 1] ?? 0.0;
      const iPosZ = positions[iBase + 2] ?? 0.0;
      const jPosX = positions[jBase] ?? 0.0;
      const jPosY = positions[jBase + 1] ?? 0.0;
      const jPosZ = positions[jBase + 2] ?? 0.0;

      const iQuatBase = i * 4;
      const jQuatBase = j * 4;
      const [iQx, iQy, iQz, iQw] = normalizeQuat(
        quaternions[iQuatBase] ?? 0.0,
        quaternions[iQuatBase + 1] ?? 0.0,
        quaternions[iQuatBase + 2] ?? 0.0,
        quaternions[iQuatBase + 3] ?? 1.0,
      );
      const [jQx, jQy, jQz, jQw] = normalizeQuat(
        quaternions[jQuatBase] ?? 0.0,
        quaternions[jQuatBase + 1] ?? 0.0,
        quaternions[jQuatBase + 2] ?? 0.0,
        quaternions[jQuatBase + 3] ?? 1.0,
      );

      const armBase = p * 8;
      const [raX, raY, raZ] = rotateVecByQuat(
        iQx,
        iQy,
        iQz,
        iQw,
        arms[armBase] ?? 0.0,
        arms[armBase + 1] ?? 0.0,
        arms[armBase + 2] ?? 0.0,
      );
      const [rbX, rbY, rbZ] = rotateVecByQuat(
        jQx,
        jQy,
        jQz,
        jQw,
        arms[armBase + 4] ?? 0.0,
        arms[armBase + 5] ?? 0.0,
        arms[armBase + 6] ?? 0.0,
      );

      const pointAX = iPosX + raX;
      const pointAY = iPosY + raY;
      const pointAZ = iPosZ + raZ;
      const pointBX = jPosX + rbX;
      const pointBY = jPosY + rbY;
      const pointBZ = jPosZ + rbZ;

      points.push({
        x: 0.5 * (pointAX + pointBX),
        y: 0.5 * (pointAY + pointBY),
        z: 0.5 * (pointAZ + pointBZ),
      });
    }

    return points;
  }

  async readContactSolverScratchDiagnosticsAsync(renderer: any): Promise<{
    activeContacts: number;
    restoredLocalArmRecords: number;
    scratchActiveRecords: number;
    nonZeroConstraintScratch: number;
    invalidArmFlags: number;
    nonFiniteArmOrShadowComponents: number;
    logicalSlotMismatches: number;
    prepareKeySlotMismatches: number;
    logicalSlotMask: number;
    speculativeContacts: number;
    speculativeNonZeroLogicalSlots: number;
    reservedBit30Records: number;
    physicalActiveRecords: number;
    physicalSpeculativeRecords: number;
  } | null> {
    if (!this.initialized) return null;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return null;
    const [activityRaw, contactsRaw] = await Promise.all([
      renderer.getArrayBufferAsync(this.pairActivityAttr),
      renderer.getArrayBufferAsync(this.pairContactsAttr),
    ]);
    const activity = new Uint32Array(activityRaw);
    const contacts = new Float32Array(contactsRaw);
    const contactWords = new Uint32Array(contactsRaw);
    const solverContactView = decodeSolverContactListHeader(
      activity[this.pairSolverActiveContactsOffset] ?? 0,
      this.pairActiveContactsOffset,
      this.pairSolverActiveContactsOffset,
      this.maxActivePairContacts,
    );
    const activeCount = solverContactView.count;
    let restoredLocalArmRecords = 0;
    let scratchActiveRecords = 0;
    let nonZeroConstraintScratch = 0;
    let invalidArmFlags = 0;
    let nonFiniteArmOrShadowComponents = 0;
    let logicalSlotMismatches = 0;
    let prepareKeySlotMismatches = 0;
    let logicalSlotMask = 0;
    let speculativeContacts = 0;
    let speculativeNonZeroLogicalSlots = 0;
    let reservedBit30Records = 0;
    let physicalActiveRecords = 0;
    let physicalSpeculativeRecords = 0;
    const contactKeySlotBitCount = Math.ceil(Math.log2(this.pairManifoldSlots));
    const contactKeySlotMask = contactKeySlotBitCount > 0
      ? (1 << contactKeySlotBitCount) - 1
      : 0;
    for (let activeIndex = 0; activeIndex < activeCount; activeIndex++) {
      const p = activity[solverContactView.entriesOffset + activeIndex]
        ?? this.maxPairContacts;
      if (p >= this.maxPairContacts) continue;
      const metaBase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_META_OFFSET);
      const encodedMeta = contactWords[metaBase + 3] ?? 0;
      const logicalSlot = decodeContactLogicalSlot(encodedMeta);
      logicalSlotMask |= 1 << logicalSlot;
      if (logicalSlot !== p % this.pairManifoldSlots) logicalSlotMismatches++;
      const cacheBase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_CACHE_OFFSET);
      const cacheKey = (contactWords[cacheBase] ?? 0) & 0x7fffffff;
      const preparedKeySlot = cacheKey & contactKeySlotMask;
      if (cacheKey !== 0 && preparedKeySlot !== logicalSlot) prepareKeySlotMismatches++;
      if ((encodedMeta & CONTACT_INFO_SPECULATIVE_BIT) !== 0) {
        speculativeContacts++;
        if (logicalSlot !== 0) speculativeNonZeroLogicalSlots++;
      }
      if ((encodedMeta & CONTACT_INFO_RESERVED_BIT_30) !== 0) reservedBit30Records++;
      const scratchActive = (encodedMeta & 0x07000000) !== 0;
      if (scratchActive) scratchActiveRecords++;
      const c0Base = contactRecordVec4FloatIndex(p, CONTACT_RECORD_CONSTRAINT_C0_OFFSET);
      const constraintScratch = contacts[c0Base + 3] ?? Number.NaN;
      if (!Number.isFinite(constraintScratch) || Math.abs(constraintScratch) > 1e-12) {
        nonZeroConstraintScratch++;
      }
      const armABase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_ARM_A_OFFSET);
      const armBBase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_ARM_B_OFFSET);
      const shadowBase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_SHADOW_OFFSET);
      const armFlag = contacts[armBBase + 3] ?? Number.NaN;
      const validArmFlag = Number.isFinite(armFlag)
        && Number.isInteger(armFlag)
        && armFlag >= 0
        && armFlag <= 3;
      if (!validArmFlag) invalidArmFlags++;
      let finiteComponents = true;
      for (const component of [
        armABase,
        armABase + 1,
        armABase + 2,
        armBBase,
        armBBase + 1,
        armBBase + 2,
        shadowBase,
        shadowBase + 1,
        shadowBase + 2,
        shadowBase + 3,
      ]) {
        if (!Number.isFinite(contacts[component])) {
          finiteComponents = false;
          break;
        }
      }
      if (!finiteComponents) nonFiniteArmOrShadowComponents++;
      if (
        !scratchActive
        && Math.abs(constraintScratch) <= 1e-12
        && validArmFlag
        && finiteComponents
      ) {
        restoredLocalArmRecords++;
      }
    }
    // Explicit regression-only physical-row census. The solver loop above is
    // intentionally driven by the compact active stream; this second pass
    // proves that one-step CCD rows are also deactivated in backing storage.
    for (let p = 0; p < this.maxPairContacts; p++) {
      const metaBase = contactRecordVec4FloatIndex(p, CONTACT_RECORD_META_OFFSET);
      if ((contacts[metaBase + 2] ?? 0.0) < 0.5) continue;
      physicalActiveRecords++;
      const encodedMeta = contactWords[metaBase + 3] ?? 0;
      if ((encodedMeta & CONTACT_INFO_SPECULATIVE_BIT) !== 0) {
        physicalSpeculativeRecords++;
      }
    }
    return {
      activeContacts: activeCount,
      restoredLocalArmRecords,
      scratchActiveRecords,
      nonZeroConstraintScratch,
      invalidArmFlags,
      nonFiniteArmOrShadowComponents,
      logicalSlotMismatches,
      prepareKeySlotMismatches,
      logicalSlotMask: logicalSlotMask >>> 0,
      speculativeContacts,
      speculativeNonZeroLogicalSlots,
      reservedBit30Records,
      physicalActiveRecords,
      physicalSpeculativeRecords,
    };
  }

  /** Regression/debug view of the immutable canonical active-contact stream. */
  async readCanonicalActiveContactBodyPairsAsync(
    renderer: any,
  ): Promise<Array<[number, number]> | null> {
    if (!this.initialized) return null;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return null;
    const [activityRaw, contactsRaw] = await Promise.all([
      renderer.getArrayBufferAsync(this.pairActivityAttr),
      renderer.getArrayBufferAsync(this.pairContactsAttr),
    ]);
    const activity = new Uint32Array(activityRaw);
    const { meta } = decodePairContactViews(contactsRaw, this.maxPairContacts);
    const activeCount = Math.min(
      activity[this.pairActiveContactsOffset] ?? 0,
      this.maxActivePairContacts,
    );
    const pairs: Array<[number, number]> = [];
    for (let index = 0; index < activeCount; index++) {
      const contact = activity[this.pairActiveContactsOffset + 1 + index]
        ?? this.maxPairContacts;
      if (contact >= this.maxPairContacts) continue;
      const base = contact * 4;
      pairs.push([
        Math.round(meta[base] ?? -1),
        Math.round(meta[base + 1] ?? -1),
      ]);
    }
    return pairs;
  }

  /**
   * Regression-only readback used to certify f32 equivalence between the
   * contact-only shader specialization and a hard-capacity generic world with
   * zero live hard constraints. Never called by the production loop.
   */
  async readCanonicalActiveAvbdContactStatesAsync(
    renderer: any,
  ): Promise<DebugAvbdContactState[] | null> {
    if (!this.initialized) return null;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return null;
    const [activityRaw, contactsRaw] = await Promise.all([
      renderer.getArrayBufferAsync(this.pairActivityAttr),
      renderer.getArrayBufferAsync(this.pairContactsAttr),
    ]);
    const activity = new Uint32Array(activityRaw);
    const contacts = new Float32Array(contactsRaw);
    const contactWords = new Uint32Array(contactsRaw);
    const activeCount = Math.min(
      activity[this.pairActiveContactsOffset] ?? 0,
      this.maxActivePairContacts,
    );
    const states: DebugAvbdContactState[] = [];
    for (let index = 0; index < activeCount; index++) {
      const contact = activity[this.pairActiveContactsOffset + 1 + index]
        ?? this.maxPairContacts;
      if (contact >= this.maxPairContacts) continue;
      const metaBase = contactRecordVec4FloatIndex(contact, CONTACT_RECORD_META_OFFSET);
      const dualBase = contactRecordVec4FloatIndex(contact, CONTACT_RECORD_DUAL_OFFSET);
      const penaltyBase = contactRecordVec4FloatIndex(contact, CONTACT_RECORD_PENALTY_OFFSET);
      states.push({
        bodyA: Math.round(contacts[metaBase] ?? -1),
        bodyB: Math.round(contacts[metaBase + 1] ?? -1),
        logicalSlot: decodeContactLogicalSlot(contactWords[metaBase + 3] ?? 0),
        dual: [
          contacts[dualBase] ?? Number.NaN,
          contacts[dualBase + 1] ?? Number.NaN,
          contacts[dualBase + 2] ?? Number.NaN,
          contacts[dualBase + 3] ?? Number.NaN,
        ],
        penalty: [
          contacts[penaltyBase] ?? Number.NaN,
          contacts[penaltyBase + 1] ?? Number.NaN,
          contacts[penaltyBase + 2] ?? Number.NaN,
          contacts[penaltyBase + 3] ?? Number.NaN,
        ],
      });
    }
    return states;
  }

  /** Regression/debug view of the exact post-sleep contact stream consumed by
   * AVBD. The canonical render/contact list remains available separately. */
  async readSolverActiveContactBodyPairsAsync(
    renderer: any,
  ): Promise<Array<[number, number]> | null> {
    if (!this.initialized) return null;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return null;
    const [activityRaw, contactsRaw] = await Promise.all([
      renderer.getArrayBufferAsync(this.pairActivityAttr),
      renderer.getArrayBufferAsync(this.pairContactsAttr),
    ]);
    const activity = new Uint32Array(activityRaw);
    const { meta } = decodePairContactViews(contactsRaw, this.maxPairContacts);
    const solverContactView = decodeSolverContactListHeader(
      activity[this.pairSolverActiveContactsOffset] ?? 0,
      this.pairActiveContactsOffset,
      this.pairSolverActiveContactsOffset,
      this.maxActivePairContacts,
    );
    const activeCount = solverContactView.count;
    const pairs: Array<[number, number]> = [];
    for (let index = 0; index < activeCount; index++) {
      const contact = activity[solverContactView.entriesOffset + index]
        ?? this.maxPairContacts;
      if (contact >= this.maxPairContacts) continue;
      const base = contact * 4;
      pairs.push([
        Math.round(meta[base] ?? -1),
        Math.round(meta[base + 1] ?? -1),
      ]);
    }
    return pairs;
  }

  private invalidateSupportFloorDebugCache(): void {
    // Any future API that changes a static body's mass or geometric shape must
    // call this too; collision-filter/friction metadata cannot change the
    // debug floor selection and deliberately stays on the hit path.
    this.supportFloorDebugInvalidations++;
    this.supportFloorDebugCacheValid = false;
    this.supportFloorDebugBody = -1;
    this.supportFloorDebugTopY = Number.NEGATIVE_INFINITY;
  }

  private resolveSupportFloorDebugCache(): { body: number; topY: number } {
    if (this.supportFloorDebugCacheValid) {
      this.supportFloorDebugCacheHits++;
      return {
        body: this.supportFloorDebugBody,
        topY: this.supportFloorDebugTopY,
      };
    }

    const floor = this.findSupportFloorBody(this.positionsData, this.shapesData);
    this.supportFloorDebugScanCount++;
    this.supportFloorDebugBody = floor.body;
    this.supportFloorDebugTopY = floor.topY;
    this.supportFloorDebugCacheValid = true;
    return floor;
  }

  private findSupportFloorBody(
    positions: Float32Array,
    shapes: Float32Array,
  ): { body: number; topY: number } {
    let floorBody = -1;
    let floorFootprint = -1.0;
    let floorCenterY = Number.POSITIVE_INFINITY;
    let floorTopY = Number.NEGATIVE_INFINITY;

    for (let body = 0; body < this.bodyCount; body++) {
      const base = body * 4;
      const inverseMass = positions[base + 3] ?? 0.0;
      if (inverseMass !== 0.0) continue;

      const centerY = positions[base + 1] ?? 0.0;
      const halfX = Math.max(0.0, shapes[base + 1] ?? 0.0);
      const halfY = Math.max(0.0, shapes[base + 2] ?? 0.0);
      const halfZ = Math.max(0.0, shapes[base + 3] ?? 0.0);
      const footprint = halfX * halfZ;
      const largerFootprint = footprint > floorFootprint * 1.05;
      const tieButLower = Math.abs(footprint - floorFootprint) <= 1e-6 && centerY < floorCenterY;
      if (floorBody < 0 || largerFootprint || tieButLower) {
        floorBody = body;
        floorFootprint = footprint;
        floorCenterY = centerY;
        floorTopY = centerY + halfY;
      }
    }

    return { body: floorBody, topY: floorTopY };
  }

  private logSupportDiagnostics(renderer: any): void {
    if (!this.debugBehaviorEnabled) return;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return;
    if (this.supportDebugReadbackInFlight) return;
    if (
      this.lastSupportDebugLogFrame >= 0
      && this.frameId - this.lastSupportDebugLogFrame < this.supportDebugEveryNFrames
    ) {
      return;
    }

    const sampledFrame = this.frameId;
    this.supportDebugReadbackInFlight = true;
    this.lastSupportDebugLogFrame = sampledFrame;

    Promise.all([
      renderer.getArrayBufferAsync(this.pairActivityAttr),
      renderer.getArrayBufferAsync(this.pairContactsAttr),
      renderer.getArrayBufferAsync(this.positionsAttr),
      renderer.getArrayBufferAsync(this.shapesAttr),
    ]).then(([activityRaw, pairContactsRaw, positionsRaw, shapesRaw]: ArrayBuffer[]) => {
      const activity = new Uint32Array(activityRaw);
      const { meta } = decodePairContactViews(pairContactsRaw, this.maxPairContacts);
      const positions = new Float32Array(positionsRaw);
      const shapes = new Float32Array(shapesRaw);

      if (this.prevSupportBodyCount !== this.bodyCount) {
        this.prevSupportBodyCount = this.bodyCount;
        this.prevCandidatePairsForChurn = null;
        this.prevFloorCandidatePairsForChurn = null;
      }

      const { body: floorBody, topY: floorTopY } = this.findSupportFloorBody(positions, shapes);

      if (floorBody < 0) {
        this.prevCandidatePairsForChurn = null;
        this.prevFloorCandidatePairsForChurn = null;
        return;
      }

      const nearFloorDynamicBodies = new Set<number>();
      const nearFloorBand = 0.12;
      const lowerBand = -0.75;
      for (let body = 0; body < this.bodyCount; body++) {
        if (body === floorBody) continue;
        const base = body * 4;
        const inverseMass = positions[base + 3] ?? 0.0;
        if (inverseMass <= 0.0) continue;
        const centerY = positions[base + 1] ?? 0.0;
        const halfY = Math.max(0.0, shapes[base + 2] ?? 0.0);
        const bottomGap = (centerY - halfY) - floorTopY;
        if (bottomGap <= nearFloorBand && bottomGap >= lowerBand) {
          nearFloorDynamicBodies.add(body);
        }
      }

      const candidateSpan = Math.min(
        activity[this.pairCandidateIndicesOffset] ?? 0,
        this.maxCandidatePairs,
      );
      const candidatePairs = new Set<number>();
      const floorCandidatePairs = new Set<number>();
      const floorCandidateBodies = new Set<number>();
      for (let slot = 0; slot < candidateSpan; slot++) {
        const packed = activity[this.pairCandidateIndicesOffset + 1 + slot] ?? 0xFFFFFFFF;
        const i = packed & 0xFFFF;
        const j = packed >>> 16;
        if (i >= j || j >= this.bodyCount) continue;
        candidatePairs.add(packed);
        if (i === floorBody || j === floorBody) {
          floorCandidatePairs.add(packed);
          floorCandidateBodies.add(i === floorBody ? j : i);
        }
      }

      const activeContactCount = Math.min(
        activity[this.pairActiveContactsOffset] ?? 0,
        this.maxActivePairContacts,
      );
      const floorActiveBodies = new Set<number>();
      for (let idx = 0; idx < activeContactCount; idx++) {
        const contact = activity[this.pairActiveContactsOffset + 1 + idx] ?? this.maxPairContacts;
        if (contact >= this.maxPairContacts) continue;
        const metaBase = contact * 4;
        const i = Math.round(meta[metaBase] ?? -1);
        const j = Math.round(meta[metaBase + 1] ?? -1);
        if (i < 0 || j < 0 || i >= j || j >= this.bodyCount) continue;
        if (i === floorBody || j === floorBody) {
          floorActiveBodies.add(i === floorBody ? j : i);
        }
      }

      let candidateSupported = 0;
      let activeSupported = 0;
      for (const body of nearFloorDynamicBodies) {
        if (floorCandidateBodies.has(body)) candidateSupported++;
        if (floorActiveBodies.has(body)) activeSupported++;
      }

      const nearFloorCount = nearFloorDynamicBodies.size;
      const candidateRecall = nearFloorCount > 0 ? candidateSupported / nearFloorCount : 1.0;
      const activeRecall = nearFloorCount > 0 ? activeSupported / nearFloorCount : 1.0;
      const broadphaseLost = Math.max(0, nearFloorCount - candidateSupported);
      const contactRejected = Math.max(0, candidateSupported - activeSupported);

      const previousCandidate = this.prevCandidatePairsForChurn;
      const previousFloorCandidate = this.prevFloorCandidatePairsForChurn;
      let jaccard = 1.0;
      let added = 0;
      let removed = 0;
      if (previousCandidate) {
        let intersection = 0;
        for (const pair of candidatePairs) {
          if (previousCandidate.has(pair)) intersection++;
          else added++;
        }
        for (const pair of previousCandidate) {
          if (!candidatePairs.has(pair)) removed++;
        }
        const union = candidatePairs.size + previousCandidate.size - intersection;
        jaccard = union > 0 ? intersection / union : 1.0;
      }

      let floorJaccard = 1.0;
      if (previousFloorCandidate) {
        let floorIntersection = 0;
        for (const pair of floorCandidatePairs) {
          if (previousFloorCandidate.has(pair)) floorIntersection++;
        }
        const floorUnion = floorCandidatePairs.size + previousFloorCandidate.size - floorIntersection;
        floorJaccard = floorUnion > 0 ? floorIntersection / floorUnion : 1.0;
      }

      this.prevCandidatePairsForChurn = candidatePairs;
      this.prevFloorCandidatePairsForChurn = floorCandidatePairs;

      console.info(
        `[Support Debug] frame=${sampledFrame} floor=${floorBody} nearFloor=${nearFloorCount} ` +
        `candSupport=${candidateSupported}(${(100.0 * candidateRecall).toFixed(1)}%) ` +
        `activeSupport=${activeSupported}(${(100.0 * activeRecall).toFixed(1)}%) ` +
        `broadphaseLost=${broadphaseLost} contactRejected=${contactRejected} ` +
        `pairs=${candidatePairs.size} floorPairs=${floorCandidatePairs.size} ` +
        `jaccard=${jaccard.toFixed(3)} floorJaccard=${floorJaccard.toFixed(3)} ` +
        `added=${added} removed=${removed}`,
      );
    }).catch((error: unknown) => {
      console.warn('Support debug readback failed:', error);
    }).finally(() => {
      this.supportDebugReadbackInFlight = false;
    });
  }

}
